import {
  enrichPowerWithCollection,
  getCollectionDetails,
  getCollectionList,
  parseCollection,
  type PowerCollection,
} from './collections-data';
import type { ArticleContent } from './articleContent';
import { EVERYPOWER_DESCRIPTION, EVERYPOWER_PAWNS } from './everypower-data';
import { ALLPOWERS_DESCRIPTION } from './allpowers-data';
import { resolvePowerPrices } from './fairtrade-data';
import type { AceAvatarFrame } from './ace-avatar-frames';
import type { PowerPstyleCmsConfig } from './power-pstyle-themes';
import { isPubliclyVisible, type CmsPublicationStatus } from './cms-publication';
import { powerShowsPstyleSection } from './power-pstyle-themes';
import { fixMojibake } from './fix-mojibake';
import type { RelatedExploreConfig } from './related-explore';
import { resolveRelatedByItems } from './related-explore';
import type { CalloutColorId } from './callout-colors';
import type { PowerDocSection } from './power-doc-section';
import { migratePowerLoreFields, powerLoreHasRenderableContent, type PowerLore } from './power-lore';
import { XAT_API } from '@/services/api-paths';

export { fixMojibake } from './fix-mojibake';
export type { RelatedExploreConfig } from './related-explore';
export type { PowerDocSection } from './power-doc-section';
export type { PowerLore } from './power-lore';

export type { PowerCollection } from './collections-data';

// Types
export type PowerPrice = {
  fairtrade_min: number;
  fairtrade_max: number;
  store: string;
};

export type PowerPawnInfo = {
  hat: string;
  name: string;
};

export type PowerInfo = {
  id: number;
  name: string;
  slug: string;
  description: { long: string; short: string };
  prices: PowerPrice;
  status: false | string;
  released: string;
  categories?: string[];
  group?: string;
  category: string;
  type?: string;
  limited: boolean;
  smilies: string[];
  /** Códigos de sticker (ex.: pumpkin) — chat `(slug.codigo)`. */
  stickers?: string[];
  pawns: PowerPawnInfo[];
  image?: string;
  popularity?: number;
  collection?: PowerCollection;
  /** Tutorial opcional editável no painel (blocos como nos guias). */
  tutorial?: ArticleContent;
  /** Tema Pstyle personalizado no painel (degradê, imagem nos botões, status). */
  pstyle?: PowerPstyleCmsConfig;
  /** Exibe a secção «Avatar frames» na página pública (desligado por padrão). */
  avatarFramesEnabled?: boolean;
  avatarFrames?: AceAvatarFrame[];
  /**
   * Secções de documentação custom (smiley + subtítulo + título + descrição).
   * Desligadas por padrão; podem ser colocadas em vários pontos da página.
   */
  docSectionsEnabled?: boolean;
  docSections?: PowerDocSection[];
  /**
   * Lore / história editorial (opcional). Experiência narrativa separada da
   * documentação técnica (`docSections`).
   */
  lore?: PowerLore;
  /** Texto personalizado da caixa «Como funciona» (vazio = texto automático). */
  calloutHowItWorks?: string;
  calloutHowItWorksTitle?: string;
  calloutHowItWorksColor?: CalloutColorId;
  /** Texto personalizado da caixa «Power Limitado» (vazio = texto automático). */
  calloutLimited?: string;
  calloutLimitedTitle?: string;
  calloutLimitedColor?: CalloutColorId;
  /** Secção «Explore mais» (título + até 4 powers manuais). */
  relatedExplore?: RelatedExploreConfig;
  /** Cabeçalho da secção pública «Sobre este power» (vazio = defaults). */
  overviewSmiley?: string;
  overviewSubtitle?: string;
  overviewTitle?: string;
  /** Workflow de revisão — omitido = publicado (legado). */
  publicationStatus?: CmsPublicationStatus;
};

/** Categorias de filtro alinhadas à API xatBlog (type + tags). */
export type PowerCategory =
  | 'todos'
  | 'smiley'
  | 'function'
  | 'group'
  | 'hug'
  | 'jinx'
  | 'epic'
  | 'games'
  | 'stickers'
  | 'collection'
  | 'limited'
  | 'unlimited';

export type PowerApiFilterCategory = Exclude<PowerCategory, 'todos' | 'limited' | 'unlimited'>;

export type ApiCategoryDef = {
  id: PowerApiFilterCategory;
  label: string;
  description: string;
};

export type SortOption =
  | 'id-desc'
  | 'id-asc'
  | 'recent'
  | 'oldest'
  | 'popular'
  | 'price-asc'
  | 'price-desc'
  | 'store-asc'
  | 'store-desc'
  | 'alpha'
  | 'alpha-desc'
  | 'limited-first'
  | 'unlimited-first';

export type SortOptionGroup = {
  label: string;
  options: { id: SortOption; label: string }[];
};

export const POWERS_PAGE_SIZE = 24;

export const API_CATEGORY_DEFS: ApiCategoryDef[] = [
  {
    id: 'smiley',
    label: 'Smilies',
    description: 'Coleções de smilies animados e expressivos para o chat',
  },
  {
    id: 'function',
    label: 'Function',
    description: 'Powers funcionais que alteram recursos e comportamento do chat',
  },
  {
    id: 'group',
    label: 'Group Powers',
    description: 'Powers de grupo que personalizam e expandem salas de chat',
  },
  {
    id: 'hug',
    label: 'Hug',
    description: 'Powers com abraços animados temáticos para enviar no chat',
  },
  {
    id: 'stickers',
    label: 'Stickers',
    description: 'Stickers artísticos e temáticos para mensagens',
  },
  {
    id: 'games',
    label: 'Games',
    description: 'Jogos interativos para jogar diretamente no chat',
  },
  {
    id: 'epic',
    label: 'Epic',
    description: 'Powers épicos raros com efeitos exclusivos de prestígio',
  },
  {
    id: 'collection',
    label: 'Collection',
    description: 'Powers de coleção com itens e combinações especiais',
  },
  {
    id: 'jinx',
    label: 'Jinx',
    description: 'Powers jinx que aplicam efeitos temporários nos usuários',
  },
];

export const POWER_CATEGORIES: { id: PowerCategory; label: string }[] = [
  { id: 'todos', label: 'Todos' },
  ...API_CATEGORY_DEFS.map((c) => ({ id: c.id, label: c.label })),
  { id: 'limited', label: 'Limitados' },
  { id: 'unlimited', label: 'Ilimitados' },
];

export function getFilterCategories(): { id: PowerCategory; label: string }[] {
  return POWER_CATEGORIES.filter((c) => c.id !== 'todos');
}

export function getExploreCategories(): (ApiCategoryDef & { count: number })[] {
  const catalog = withoutAcePowers(MOCK_POWERS);
  return API_CATEGORY_DEFS.map((def) => ({
    ...def,
    count: catalog.filter((p) => p.category === def.id).length,
  }))
    .filter((c) => c.count > 0)
    .sort((a, b) => b.count - a.count);
}

export const SORT_OPTION_GROUPS: SortOptionGroup[] = [
  {
    label: 'ID',
    options: [
      { id: 'id-desc', label: 'Mais recentes' },
      { id: 'id-asc', label: 'Mais antigos' },
    ],
  },
  {
    label: 'Lançamento',
    options: [
      { id: 'recent', label: 'Data mais recente' },
      { id: 'oldest', label: 'Data mais antiga' },
    ],
  },
  {
    label: 'Nome',
    options: [
      { id: 'alpha', label: 'A – Z' },
      { id: 'alpha-desc', label: 'Z – A' },
    ],
  },
  {
    label: 'Fairtrade',
    options: [
      { id: 'price-asc', label: 'Menor preço' },
      { id: 'price-desc', label: 'Maior preço' },
    ],
  },
  {
    label: 'Loja',
    options: [
      { id: 'store-asc', label: 'Menor preço' },
      { id: 'store-desc', label: 'Maior preço' },
    ],
  },
  {
    label: 'Outros',
    options: [
      { id: 'popular', label: 'Mais populares' },
      { id: 'limited-first', label: 'Limitados primeiro' },
      { id: 'unlimited-first', label: 'Ilimitados primeiro' },
    ],
  },
];

export const SORT_OPTIONS: { id: SortOption; label: string }[] = SORT_OPTION_GROUPS.flatMap(
  (group) => group.options
);

export type CategoryColor = { bg: string; text: string; border: string; glow: string };

export const CATEGORY_COLORS: Record<string, CategoryColor> = {
  smiley: {
    bg: 'bg-violet-500/15',
    text: 'text-violet-300',
    border: 'border-violet-500/30',
    glow: 'rgba(139,92,246,0.18)',
  },
  function: {
    bg: 'bg-sky-500/15',
    text: 'text-sky-300',
    border: 'border-sky-500/30',
    glow: 'rgba(56,189,248,0.18)',
  },
  group: {
    bg: 'bg-emerald-500/15',
    text: 'text-emerald-300',
    border: 'border-emerald-500/30',
    glow: 'rgba(16,185,129,0.18)',
  },
  hug: {
    bg: 'bg-pink-500/15',
    text: 'text-pink-300',
    border: 'border-pink-500/30',
    glow: 'rgba(236,72,153,0.18)',
  },
  jinx: {
    bg: 'bg-orange-500/15',
    text: 'text-orange-300',
    border: 'border-orange-500/30',
    glow: 'rgba(249,115,22,0.18)',
  },
  epic: {
    bg: 'bg-amber-500/15',
    text: 'text-amber-300',
    border: 'border-amber-500/30',
    glow: 'rgba(245,158,11,0.18)',
  },
  games: {
    bg: 'bg-rose-500/15',
    text: 'text-rose-300',
    border: 'border-rose-500/30',
    glow: 'rgba(244,63,94,0.18)',
  },
  stickers: {
    bg: 'bg-cyan-500/15',
    text: 'text-cyan-300',
    border: 'border-cyan-500/30',
    glow: 'rgba(6,182,212,0.18)',
  },
  collection: {
    bg: 'bg-indigo-500/15',
    text: 'text-indigo-300',
    border: 'border-indigo-500/30',
    glow: 'rgba(99,102,241,0.18)',
  },
};

export const DEFAULT_CATEGORY_COLOR: CategoryColor = {
  bg: 'bg-blue-500/15',
  text: 'text-blue-300',
  border: 'border-blue-500/30',
  glow: 'rgba(59,130,246,0.18)',
};

export function getCategoryColor(category: string): CategoryColor {
  return CATEGORY_COLORS[category.toLowerCase()] ?? DEFAULT_CATEGORY_COLOR;
}

export function getPowerCategoryLabel(category: string): string {
  const found = API_CATEGORY_DEFS.find((c) => c.id === category.toLowerCase());
  if (found) return found.label;
  return category.charAt(0).toUpperCase() + category.slice(1);
}

export function formatStorePrice(price: string | number): string {
  if (!price || price === '0' || price === 0) return 'Gratuito';
  const raw = String(price).trim();
  if (/^unknown$/i.test(raw)) return 'Não comercializável';
  if (/days?$/i.test(raw)) return raw;
  const amount = raw.replace(/\s*xats$/i, '').trim();
  return `${amount} xats`;
}

function parseStorePriceForSort(price: string | number): number {
  if (!price || price === '0' || price === 0) return 0;
  const raw = String(price).trim();
  if (/^unknown$/i.test(raw)) return Number.MAX_SAFE_INTEGER;
  const digits = raw.replace(/\s*xats$/i, '').replace(/[^\d]/g, '');
  const parsed = Number.parseInt(digits, 10);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

export function formatFairtradePrice(min: number, max: number): string {
  if (!min && !max) return '—';
  if (min === max) return `${min} xats`;
  return `${min} – ${max} xats`;
}

const EN_SHORT_DESCRIPTION_MAP: Record<string, string> = {
  'purple pawn': 'Peão roxo épico',
  'gold pawn': 'Peão dourado épico',
  'neon glow display name': 'Nome com brilho neon',
  'color your name': 'Colore o seu nome',
  'boot a user': 'Transfere um utilizador para outra sala',
  'add a hat to your pawn': 'Adiciona um chapéu ao peão',
  'personal avatar': 'Avatar pessoal personalizado',
  'ban animations': 'Animações de ban',
  'freeze smiley': 'Congela smilies na animação',
  'face mix': 'Combina rostos de smilies',
  'all powers': 'Todos os powers',
  'every power': 'Todos os powers',
  'appear higher on userlist': 'Sobe na lista de visitantes',
  'style your chat': 'Personaliza o visual da sala',
  'hearts card game': 'Jogo de cartas Hearts',
  'matchrace game': 'Jogo MatchRace',
};

function isLikelyPortuguese(text: string): boolean {
  const clean = fixMojibake(text);
  return /[áàâãéêíóôõúçÁÀÂÃÉÊÍÓÔÕÚÇ]/.test(clean) ||
    /\b(do|da|dos|das|para|com|seu|sua|utilizador|peão|nome|sala|chat|power|smilies|coleção)\b/i.test(clean);
}

function titleCaseName(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

function localizeShortDescription(power: PowerInfo): string {
  const raw = fixMojibake(power.description.short?.trim() ?? '');
  if (!raw) return `Power ${power.name}`;
  if (isLikelyPortuguese(raw)) return raw;

  const mapped = EN_SHORT_DESCRIPTION_MAP[raw.toLowerCase()];
  if (mapped) return mapped;

  const name = titleCaseName(power.name);
  const lower = raw.toLowerCase();

  if (lower.endsWith(' stickers')) return `Stickers de ${name}`;
  if (lower.endsWith(' smilies') || lower.endsWith(' smiley')) return `Smilies de ${name}`;
  if (lower.includes('game')) return `Jogo ${name}`;
  if (lower.includes('pawn')) return `Peão ${name}`;
  if (lower.includes('hug')) return `Hug ${name}`;
  if (lower.includes('jinx')) return `Jinx ${name}`;
  if (lower.includes('sticker')) return `Stickers de ${name}`;

  switch (power.category) {
    case 'stickers':
      return `Stickers de ${name}`;
    case 'games':
      return `Jogo ${name}`;
    case 'group':
      return `Group Power ${name}`;
    case 'function':
      return raw.charAt(0).toUpperCase() + raw.slice(1);
    case 'hug':
      return `Hug ${name}`;
    case 'jinx':
      return `Jinx ${name}`;
    case 'epic':
      return `Epic ${name}`;
    case 'collection':
      return `Collection ${name}`;
    default:
      return `Smilies de ${name}`;
  }
}

function localizeLongDescription(power: PowerInfo, shortPt: string): string {
  const raw = fixMojibake(power.description.long?.trim() ?? '');
  if (!raw) return shortPt;
  if (isLikelyPortuguese(raw)) return raw;

  const name = titleCaseName(power.name);

  switch (power.category) {
    case 'stickers':
      return `Stickers exclusivos do power ${name} para personalizar as suas mensagens no xat.`;
    case 'games':
      return `Power de jogo ${name} que adiciona entretenimento interativo diretamente no chat.`;
    case 'group':
      return `Power de grupo ${name} que expande e personaliza a experiência da sua sala no xat.`;
    case 'function':
      return `${shortPt}. Power funcional do xat que altera recursos e comportamentos do chat.`;
    case 'hug':
      return `Power hug ${name} com abraços animados temáticos para enviar no chat do xat.`;
    case 'jinx':
      return `Power jinx ${name} que aplica efeitos temporários nos usuários do chat.`;
    case 'epic':
      return `Power épico ${name} — raro e exclusivo, com efeitos de prestígio no xat.`;
    case 'collection':
      return `Power de coleção ${name} com itens e combinações especiais para colecionadores.`;
    default:
      return `Coleção de smilies do power ${name} para usar no chat do xat. Envie cada smiley com o código entre parênteses.`;
  }
}

const POWER_DESCRIPTION_OVERRIDES: Record<string, { short: string; long: string }> = {
  allpowers: ALLPOWERS_DESCRIPTION,
  everypower: EVERYPOWER_DESCRIPTION,
};

export function getPowerDescription(power: PowerInfo): { short: string; long: string } {
  const override = POWER_DESCRIPTION_OVERRIDES[power.slug];
  if (override) return override;

  const short = localizeShortDescription(power);
  const long = localizeLongDescription(power, short);
  return { short, long };
}

export function formatReleaseDate(dateStr: string): string {
  if (!dateStr?.trim()) return '—';

  const trimmed = dateStr.trim();
  const slashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (slashMatch) {
    const day = slashMatch[1].padStart(2, '0');
    const month = slashMatch[2].padStart(2, '0');
    const year = slashMatch[3];
    return `${day}/${month}/${year}`;
  }

  // YYYY-MM-DD (ou ISO com hora): usar o calendário da string, sem shift de fuso
  // (new Date('2026-07-24') é UTC midnight → dia anterior em PT-BR).
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (isoDate) {
    const year = Number(isoDate[1]);
    if (year < 2000) return '—';
    return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;
  }

  try {
    const date = new Date(trimmed);
    if (Number.isNaN(date.getTime()) || date.getFullYear() < 2000) return '—';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  } catch {
    return trimmed;
  }
}

export function isAcePower(power: Pick<PowerInfo, 'type'>): boolean {
  return power.type?.toLowerCase() === 'ace';
}

/** Powers exibidos no catálogo público — exclui Aces (têm secção própria em /aces). */
export function withoutAcePowers(powers: PowerInfo[]): PowerInfo[] {
  return powers.filter((p) => !isAcePower(p));
}

export function getPowersStats(powers: PowerInfo[]) {
  const total = powers.length;
  const limited = powers.filter((p) => p.limited).length;
  const funcional = powers.filter((p) => p.category === 'function').length;
  const sorted = [...powers].sort((a, b) => b.id - a.id);
  const latest = sorted[0];
  return { total, limited, funcional, latest };
}

export function filterPowersByQuery(powers: PowerInfo[], query: string): PowerInfo[] {
  const q = query.trim().toLowerCase();
  if (!q) return powers;

  const scored = powers
    .map((p) => {
      const name = p.name.toLowerCase();
      const slug = p.slug.toLowerCase();
      const desc = getPowerDescription(p);
      const short = desc.short.toLowerCase();
      const long = desc.long.toLowerCase();
      const category = p.category.toLowerCase();
      const categoryLabel = getPowerCategoryLabel(p.category).toLowerCase();

      let score = 0;
      if (name === q || slug === q) score = 1000;
      else if (name.startsWith(q) || slug.startsWith(q)) score = 800;
      else if (name.includes(q) || slug.includes(q)) score = 600;
      else if (short.startsWith(q)) score = 200;
      else if (short.includes(q)) score = 120;
      else if (long.includes(q)) score = 40;
      else if (category.includes(q) || categoryLabel.includes(q)) score = 20;
      else return null;

      // Desempate: correspondência mais curta (ex.: «topman» > «everypower» que só cita topman)
      const nameIdx = name.indexOf(q);
      const slugIdx = slug.indexOf(q);
      const proximity =
        nameIdx >= 0 ? 50 - Math.min(nameIdx, 50) : slugIdx >= 0 ? 40 - Math.min(slugIdx, 40) : 0;

      return { power: p, score: score + proximity, nameLen: name.length };
    })
    .filter((row): row is { power: PowerInfo; score: number; nameLen: number } => row != null);

  scored.sort((a, b) => b.score - a.score || a.nameLen - b.nameLen || a.power.name.localeCompare(b.power.name));
  return scored.map((row) => row.power);
}

export function searchPowers(powers: PowerInfo[], query: string, limit = 8): PowerInfo[] {
  return filterPowersByQuery(powers, query).slice(0, limit);
}

export function filterAndSortPowers(
  powers: PowerInfo[],
  search: string,
  category: PowerCategory,
  sort: SortOption
): PowerInfo[] {
  let result = [...powers];

  if (search.trim().length > 0) {
    result = filterPowersByQuery(result, search);
  }

  if (category !== 'todos') {
    if (category === 'limited') {
      result = result.filter((p) => p.limited);
    } else if (category === 'unlimited') {
      result = result.filter((p) => !p.limited);
    } else {
      result = result.filter((p) => p.category === category);
    }
  }

  switch (sort) {
    case 'id-desc':
      result.sort((a, b) => b.id - a.id);
      break;
    case 'id-asc':
      result.sort((a, b) => a.id - b.id);
      break;
    case 'recent':
      result.sort((a, b) => new Date(b.released).getTime() - new Date(a.released).getTime());
      break;
    case 'oldest':
      result.sort((a, b) => new Date(a.released).getTime() - new Date(b.released).getTime());
      break;
    case 'popular':
      result.sort((a, b) => (b.popularity ?? 0) - (a.popularity ?? 0));
      break;
    case 'price-asc':
      result.sort((a, b) => (a.prices.fairtrade_min ?? 0) - (b.prices.fairtrade_min ?? 0));
      break;
    case 'price-desc':
      result.sort((a, b) => (b.prices.fairtrade_min ?? 0) - (a.prices.fairtrade_min ?? 0));
      break;
    case 'store-asc':
      result.sort(
        (a, b) => parseStorePriceForSort(a.prices.store) - parseStorePriceForSort(b.prices.store)
      );
      break;
    case 'store-desc':
      result.sort(
        (a, b) => parseStorePriceForSort(b.prices.store) - parseStorePriceForSort(a.prices.store)
      );
      break;
    case 'alpha':
      result.sort((a, b) => a.name.localeCompare(b.name, 'pt'));
      break;
    case 'alpha-desc':
      result.sort((a, b) => b.name.localeCompare(a.name, 'pt'));
      break;
    case 'limited-first':
      result.sort((a, b) => {
        if (a.limited !== b.limited) return a.limited ? -1 : 1;
        return b.id - a.id;
      });
      break;
    case 'unlimited-first':
      result.sort((a, b) => {
        if (a.limited !== b.limited) return a.limited ? 1 : -1;
        return b.id - a.id;
      });
      break;
  }

  return result;
}

export async function fetchPowerInfo(slug: string): Promise<PowerInfo | null> {
  const key = slug.trim().toLowerCase();
  if (!key) return null;

  const endpoints = [
    XAT_API.powerinfo(key),
    `https://api.xatblog.net/powerinfo/${encodeURIComponent(key)}`,
  ];

  for (const endpoint of endpoints) {
    try {
      const res = await fetch(endpoint, { cache: 'no-store' });
      if (!res.ok) continue;
      const data = await res.json();
      const raw =
        data?.code === 200 && data.power
          ? data.power
          : data?.code === 200 && Array.isArray(data.powers) && data.powers.length > 0
            ? data.powers[0]
            : null;
      if (raw) return normalizePowerApiData(raw, key);
    } catch {
      continue;
    }
  }

  const mock = getPowerBySlug(key);
  return mock ? mergePowerWithMockExtras(withLocalizedDescription(mock)) : null;
}

function withLocalizedDescription(power: PowerInfo): PowerInfo {
  return enrichPowerWithCollection({ ...power, description: getPowerDescription(power) });
}

/** Garante arrays válidos para galeria (evita falhas quando CMS/API vêm incompletos). */
export function normalizePowerGalleries(power: PowerInfo): PowerInfo {
  return {
    ...power,
    smilies: Array.isArray(power.smilies) ? power.smilies : [],
    stickers: Array.isArray(power.stickers) ? power.stickers : [],
    pawns: Array.isArray(power.pawns) ? power.pawns : [],
  };
}

function mergeUniqueSmilies(...lists: string[][]): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  for (const list of lists) {
    for (const item of list) {
      const key = item.trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      result.push(item.trim());
    }
  }
  return result;
}

function mergeUniquePawns(...lists: PowerPawnInfo[][]): PowerPawnInfo[] {
  const result: PowerPawnInfo[] = [];
  const seen = new Set<string>();
  for (const list of lists) {
    for (const pawn of list) {
      const key = `${pawn.hat.toLowerCase()}::${pawn.name.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(pawn);
    }
  }
  return result;
}

function pickRicherSmilies(a: string[], b: string[]): string[] {
  const merged = mergeUniqueSmilies(a, b);
  if (merged.length > 0) return merged;
  return b.length > a.length ? b : a;
}

function everypowerPawnsList(): PowerPawnInfo[] {
  return EVERYPOWER_PAWNS.map((p) => ({ hat: p.hat, name: p.name }));
}

function pickRicherPawns(a: PowerPawnInfo[], b: PowerPawnInfo[]): PowerPawnInfo[] {
  const merged = mergeUniquePawns(a, b);
  if (merged.length > 0) return merged;
  return b.length > a.length ? b : a;
}

function pickPowerPawns(slug: string, a: PowerPawnInfo[], b: PowerPawnInfo[]): PowerPawnInfo[] {
  if (slug === 'everypower') return everypowerPawnsList();
  return pickRicherPawns(a, b);
}

/**
 * Combina mock, CMS e API sem perder smilies/peões quando uma fonte chega vazia
 * ou uma resposta antiga da API sobrescreve dados já carregados.
 */
export function resolvePowerDetailFromSources(sources: PowerInfo[]): PowerInfo | null {
  if (sources.length === 0) return null;

  let best = mergePowerWithMockExtras(normalizePowerGalleries(sources[0]));

  for (let i = 1; i < sources.length; i++) {
    const raw = normalizePowerGalleries(sources[i]);
    const next = mergePowerWithMockExtras(raw);
    // Registos editados no painel (CMS) são autoritativos — inclusive em smilies/peões,
    // para que remoções feitas no painel não voltem via mock/API.
    const nextIsCms = isCmsAuthoredPower(raw);
    best = mergePowerWithMockExtras({
      ...best,
      ...next,
      smilies: nextIsCms && next.smilies.length > 0
        ? next.smilies
        : pickRicherSmilies(best.smilies, next.smilies),
      stickers: nextIsCms && (next.stickers?.length ?? 0) > 0
        ? next.stickers
        : pickRicherSmilies(best.stickers ?? [], next.stickers ?? []),
      pawns: nextIsCms && next.pawns.length > 0
        ? pickPowerPawns(best.slug, next.pawns, next.pawns)
        : pickPowerPawns(best.slug, best.pawns, next.pawns),
      prices: resolvePowerPrices(next.prices, best.prices),
    });
  }

  return mergePowerWithMockExtras(best);
}

export type ResolvePowerForPublicDetailOptions = {
  cmsHydrated?: boolean;
  getCmsPower?: (slug: string) => PowerInfo | undefined;
  /** Registo CMS já obtido (ex.: GET /powers/:slug) — tem prioridade editorial. */
  cmsPower?: PowerInfo | null;
  /** Só inclui registo CMS se estiver publicado (páginas /powers). */
  requirePublicCms?: boolean;
  /** Exclui powers tipo ace do catálogo (páginas /powers). */
  excludeAce?: boolean;
};

/** Carrega power para detalhe público — mock, API xat e CMS (CMS ganha nos campos editoriais). */
export async function resolvePowerForPublicDetail(
  slug: string,
  options: ResolvePowerForPublicDetailOptions = {}
): Promise<PowerInfo | null> {
  const key = slug.trim().toLowerCase();
  if (!key) return null;

  const sources: PowerInfo[] = [];
  const seed = getPowerBySlug(key);
  if (seed && (!options.excludeAce || !isAcePower(seed))) {
    sources.push(seed);
  }

  const api = await fetchPowerInfo(key);
  if (api && (!options.excludeAce || !isAcePower(api))) {
    sources.push(api);
  }

  const pushCms = (cms: PowerInfo | undefined | null) => {
    if (!cms) return;
    if (options.excludeAce && isAcePower(cms)) return;
    if (options.requirePublicCms && !isPubliclyVisible(cms.publicationStatus)) return;
    sources.push(cms);
  };

  if (options.cmsHydrated && options.getCmsPower) {
    pushCms(options.getCmsPower(key));
  }
  pushCms(options.cmsPower);

  const resolved = resolvePowerDetailFromSources(sources);
  if (!resolved) return null;
  return mergePowerWithMockExtras(normalizePowerGalleries(resolved));
}

function pawnCodeToDisplayHat(code: string): string {
  const c = code.trim();
  if (!c) return '';
  if (c.startsWith('(')) return c;
  return `(${c})`;
}

/** Normaliza peões da API xatBlog (`pawns` + fallback em `collection.pawns`). */
export function normalizeApiPawns(raw: Record<string, unknown>): PowerPawnInfo[] {
  const result: PowerPawnInfo[] = [];
  const seen = new Set<string>();

  const addPawn = (hat: string, name: string) => {
    const h = hat.trim();
    const n = name.trim();
    if (!h && !n) return;
    const key = `${h.toLowerCase()}::${n.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);
    result.push({
      hat: h || pawnCodeToDisplayHat(n),
      name: n || h.replace(/^\(|\)$/g, '').trim(),
    });
  };

  const rawPawns = raw.pawns;
  if (Array.isArray(rawPawns)) {
    for (const item of rawPawns) {
      if (typeof item === 'string') {
        const code = item.trim();
        if (code) addPawn(pawnCodeToDisplayHat(code), code);
      } else if (item && typeof item === 'object') {
        const p = item as Record<string, unknown>;
        const hat = typeof p.hat === 'string' ? p.hat : '';
        const name = typeof p.name === 'string' ? p.name : '';
        if (hat || name) addPawn(hat, name);
      }
    }
  }

  if (result.length === 0) {
    const collection = raw.collection;
    if (collection && typeof collection === 'object') {
      const collPawns = (collection as Record<string, unknown>).pawns;
      if (Array.isArray(collPawns)) {
        for (const item of collPawns) {
          if (typeof item === 'string' && item.trim()) {
            const code = item.trim();
            addPawn(pawnCodeToDisplayHat(code), code);
          }
        }
      }
    }
  }

  return result;
}

function parseApiReleasedDate(raw: string | undefined): string {
  if (!raw) return '';
  const value = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const dmy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  if (dmy) {
    const [, day, month, year] = dmy;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return value.slice(0, 10);
}

/** Registos guardados no painel (API CMS) trazem publicationStatus; mock e xatBlog não. */
export function isCmsAuthoredPower(power: Pick<PowerInfo, 'publicationStatus'>): boolean {
  return power.publicationStatus !== undefined;
}

function hasOwnDescription(power: PowerInfo): boolean {
  return Boolean(power.description?.short?.trim() || power.description?.long?.trim());
}

function sanitizePowerTextFields(power: PowerInfo): PowerInfo {
  const short = fixMojibake(power.description?.short ?? '');
  const long = fixMojibake(power.description?.long ?? '');
  const next: PowerInfo = {
    ...power,
    description: { short, long },
  };
  if (power.calloutHowItWorks != null) {
    next.calloutHowItWorks = fixMojibake(power.calloutHowItWorks);
  }
  if (power.calloutLimited != null) {
    next.calloutLimited = fixMojibake(power.calloutLimited);
  }
  if (power.overviewSubtitle != null) {
    next.overviewSubtitle = fixMojibake(power.overviewSubtitle);
  }
  if (power.overviewTitle != null) {
    next.overviewTitle = fixMojibake(power.overviewTitle);
  }
  return next;
}

function enrichPowerFromMock(power: PowerInfo): PowerInfo {
  const cmsAuthored = isCmsAuthoredPower(power);
  // Descrição editada no painel tem prioridade sobre o texto estático do mock.
  const keepCmsDescription = cmsAuthored && hasOwnDescription(power);

  const mock = MOCK_POWERS.find((p) => p.slug === power.slug);
  if (!mock) {
    let normalized = normalizePowerGalleries(sanitizePowerTextFields(power));
    if ((normalized.stickers?.length ?? 0) > 0 && normalized.category === 'smiley') {
      normalized = { ...normalized, category: 'stickers' };
    }
    normalized = migratePowerLoreFields(normalized);
    // Não passar texto do painel pela localização automática (podia trocá-lo por texto genérico).
    return keepCmsDescription
      ? enrichPowerWithCollection(normalized)
      : withLocalizedDescription(normalized);
  }

  const powerSmilies = power.smilies ?? [];
  const powerStickers = power.stickers ?? [];
  const powerPawns = power.pawns ?? [];
  const mockSmilies = mock.smilies ?? [];
  const mockStickers = mock.stickers ?? [];
  const mockPawns = mock.pawns ?? [];

  // CMS é autoritativo nas galerias (permite remover itens no painel); mock é só fallback.
  const smilies = cmsAuthored
    ? powerSmilies.length > 0
      ? powerSmilies
      : mockSmilies
    : pickRicherSmilies(powerSmilies, mockSmilies);
  const stickers = cmsAuthored
    ? powerStickers.length > 0
      ? powerStickers
      : mockStickers
    : pickRicherSmilies(powerStickers, mockStickers);
  const pawns =
    power.slug === 'everypower'
      ? everypowerPawnsList()
      : cmsAuthored
        ? powerPawns.length > 0
          ? powerPawns
          : mockPawns
        : pickRicherPawns(powerPawns, mockPawns);
  const hasApiCategories = Array.isArray(power.categories) && power.categories.length > 0;

  const resolvedCategory = (() => {
    if (cmsAuthored && power.category?.trim()) return power.category;
    // Lista de stickers da API ganha sobre mock com category "smiley" errada.
    if (stickers.length > 0) return 'stickers';
    if (power.category && power.category !== 'smiley') return power.category;
    return mock.category || power.category;
  })();

  const merged: PowerInfo = sanitizePowerTextFields({
    ...power,
    name: power.name || mock.name,
    category: resolvedCategory,
    type: power.type ?? mock.type,
    limited: cmsAuthored
      ? Boolean(power.limited)
      : hasApiCategories
        ? isLimitedFromPower(power)
        : Boolean(power.limited ?? mock.limited),
    popularity: power.popularity ?? mock.popularity,
    released: power.released || mock.released,
    prices: resolvePowerPrices(power.prices, mock.prices),
    smilies,
    stickers,
    pawns,
    description: keepCmsDescription ? power.description : mock.description,
    tutorial: power.tutorial ?? mock.tutorial,
    pstyle: power.pstyle ?? mock.pstyle,
    avatarFramesEnabled: power.avatarFramesEnabled ?? mock.avatarFramesEnabled,
    avatarFrames: power.avatarFrames ?? mock.avatarFrames,
    docSectionsEnabled: power.docSectionsEnabled ?? mock.docSectionsEnabled,
    docSections: power.docSections ?? mock.docSections,
    lore: (() => {
      const cmsLore = power.lore;
      const seedLore = mock.lore;
      const cmsOk = powerLoreHasRenderableContent(cmsLore);
      const seedOk = powerLoreHasRenderableContent(seedLore);
      // Topman: seed canónico (capítulos + curiosidade) até o CMS ter curiosidades próprias.
      if (power.slug.toLowerCase() === 'topman' && seedOk && seedLore) {
        if (cmsOk && cmsLore && (cmsLore.curiosities?.length ?? 0) > 0) {
          return {
            ...cmsLore,
            theme: cmsLore.theme || seedLore.theme || 'normal',
            subtitle: cmsLore.subtitle?.trim() || seedLore.subtitle || 'Nos arquivos do xat',
            curiosities: cmsLore.curiosities.map((c, i) => ({
              ...c,
              smiley: c.smiley.trim() || seedLore.curiosities[i]?.smiley || c.smiley,
            })),
          };
        }
        return seedLore;
      }
      if (cmsOk) return cmsLore;
      return seedLore ?? cmsLore;
    })(),
  });

  return keepCmsDescription
    ? enrichPowerWithCollection(migratePowerLoreFields(merged))
    : withLocalizedDescription(migratePowerLoreFields(merged));
}

function isLimitedFromPower(power: PowerInfo): boolean {
  if (power.categories?.includes('is_limited')) return true;
  if (power.status === 'limited') return true;
  return power.limited;
}

const MEANINGFUL_CATEGORY_TAGS = new Set([
  'is_epic',
  'is_game',
  'is_hug',
  'is_jinx',
  'is_collection',
  'is_stickers',
  'is_group',
]);

const FUNCTION_API_TYPES = new Set([
  'function',
  'fx',
  'back',
  'blasts',
  'ace',
  'colors',
  'other',
]);

/** A API xatBlog só deve substituir a categoria do catálogo quando houver sinal claro (type/tags). */
export function hasConfidentApiCategory(
  power: Pick<PowerInfo, 'type' | 'categories'>
): boolean {
  const type = power.type?.toLowerCase().trim() ?? '';
  const tags = (power.categories ?? []).filter(
    (tag) => tag !== 'is_limited' && tag !== 'is_unlimited'
  );

  if (tags.some((tag) => MEANINGFUL_CATEGORY_TAGS.has(tag))) return true;
  if (!type) return false;

  return (
    FUNCTION_API_TYPES.has(type) ||
    type === 'stickers' ||
    type === 'games' ||
    type === 'gamebans' ||
    type === 'hugs' ||
    type === 'jinx' ||
    type === 'epic' ||
    type === 'group' ||
    type === 'collection'
  );
}

function mergeCatalogCategory(catalog: PowerInfo, api: PowerInfo): string {
  const catalogCategory = catalog.category?.trim() || '';
  const apiCategory = api.category?.trim() || '';

  if ((api.stickers?.length ?? 0) > 0 || (catalog.stickers?.length ?? 0) > 0) {
    return 'stickers';
  }

  // Categorias do seed/CMS (function, stickers, …) têm prioridade sobre reclassificação da xatBlog.
  if (catalogCategory && catalogCategory !== 'smiley') {
    return catalogCategory;
  }

  if (hasConfidentApiCategory(api) && apiCategory) {
    return apiCategory;
  }

  return catalogCategory || apiCategory || 'smiley';
}

/** Aplica campos básicos da API xatBlog ao registo do catálogo (mock/CMS). */
export function mergePowerWithApiBasics(catalog: PowerInfo, api: PowerInfo): PowerInfo {
  // Registos editados no painel mantêm nome, limitado e galerias do CMS.
  const cmsAuthored = isCmsAuthoredPower(catalog);
  const catalogSmilies = catalog.smilies ?? [];
  const catalogPawns = catalog.pawns ?? [];

  return mergePowerWithMockExtras({
    ...catalog,
    id: api.id || catalog.id,
    name: cmsAuthored && catalog.name ? catalog.name : api.name || catalog.name,
    limited: cmsAuthored ? catalog.limited : api.limited,
    status: cmsAuthored ? catalog.status : api.status,
    released: api.released || catalog.released,
    category: mergeCatalogCategory(catalog, api),
    type: api.type ?? catalog.type,
    categories: api.categories?.length ? api.categories : catalog.categories,
    prices: resolvePowerPrices(api.prices, catalog.prices),
    smilies:
      cmsAuthored && catalogSmilies.length > 0
        ? catalogSmilies
        : pickRicherSmilies(catalogSmilies, api.smilies ?? []),
    stickers:
      cmsAuthored && (catalog.stickers?.length ?? 0) > 0
        ? catalog.stickers
        : pickRicherSmilies(catalog.stickers ?? [], api.stickers ?? []),
    pawns:
      cmsAuthored && catalogPawns.length > 0
        ? pickPowerPawns(catalog.slug, catalogPawns, catalogPawns)
        : pickPowerPawns(catalog.slug, catalogPawns, api.pawns ?? []),
    popularity: catalog.popularity ?? api.popularity,
  });
}

export function powerInfoFromPricesCatalogEntry(entry: Record<string, unknown>): PowerInfo | null {
  const name = typeof entry.name === 'string' ? entry.name.trim() : '';
  if (!name) return null;

  const categories = Array.isArray(entry.categories)
    ? entry.categories.map(String).filter(Boolean)
    : [];
  const storeRaw = typeof entry.store_price === 'string' ? entry.store_price : '0';
  const store = storeRaw.replace(/\s*xats?\s*$/i, '').trim() || '0';
  const short =
    typeof entry.description === 'string'
      ? entry.description
      : typeof (entry.description as { short?: string } | undefined)?.short === 'string'
        ? (entry.description as { short: string }).short
        : '';
  const long =
    typeof entry.description_full === 'string'
      ? entry.description_full
      : typeof (entry.description as { long?: string } | undefined)?.long === 'string'
        ? (entry.description as { long: string }).long
        : short;

  return normalizePowerApiData(
    {
      id: typeof entry.id === 'number' ? entry.id : Number(entry.id) || 0,
      name,
      description: { short, long },
      prices: {
        fairtrade_min: Number(entry.fairtrade_min_xats) || 0,
        fairtrade_max: Number(entry.fairtrade_max_xats) || 0,
        store,
      },
      status: categories.includes('is_limited') ? 'limited' : false,
      released: typeof entry.released === 'string' ? entry.released : undefined,
      categories,
      type: typeof entry.type === 'string' ? entry.type : undefined,
      smilies: [],
      stickers: [],
      pawns: [],
    },
    name.toLowerCase()
  );
}

/** Catálogo agregado (1 request) — proxy Laravel ou api.xatblog.net/prices. */
export async function fetchPowersPricesCatalog(
  signal?: AbortSignal
): Promise<PowerInfo[] | null> {
  const endpoints = [XAT_API.prices(), 'https://api.xatblog.net/prices'];

  for (const endpoint of endpoints) {
    try {
      const res = await fetch(endpoint, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal,
      });
      if (!res.ok) continue;

      const data = (await res.json()) as {
        code?: number;
        prices?: unknown;
      };
      if (data.code !== 200 || !Array.isArray(data.prices)) continue;

      const powers: PowerInfo[] = [];
      for (const row of data.prices) {
        if (!row || typeof row !== 'object') continue;
        const power = powerInfoFromPricesCatalogEntry(row as Record<string, unknown>);
        if (power) powers.push(power);
      }
      return powers.length > 0 ? powers : null;
    } catch {
      if (signal?.aborted) return null;
      continue;
    }
  }

  return null;
}

/** Garante smilies/peões do mock quando a API ou CMS vierem incompletos. */
export function mergePowerWithMockExtras(power: PowerInfo): PowerInfo {
  return enrichPowerFromMock(power);
}

function normalizePowerApiData(raw: Record<string, unknown>, slug: string): PowerInfo {
  const prices = (raw.prices as PowerPrice) ?? { fairtrade_min: 0, fairtrade_max: 0, store: '0' };
  const description = (raw.description as { long: string; short: string }) ?? {
    long: '',
    short: '',
  };
  const key = slug.toLowerCase();
  const stickers = Array.isArray(raw.stickers)
    ? raw.stickers.map(String).map((s) => s.trim()).filter(Boolean)
    : [];
  const base: PowerInfo = {
    id: (raw.id as number) ?? 0,
    name: (raw.name as string) ?? slug,
    slug: key,
    description,
    prices,
    status: (raw.status as false | string) ?? false,
    released: parseApiReleasedDate(typeof raw.released === 'string' ? raw.released : undefined),
    categories: (raw.categories as string[]) ?? [],
    group: raw.group as string | undefined,
    type: typeof raw.type === 'string' ? raw.type : undefined,
    category: key === 'everypower' ? 'epic' : resolveCategory(raw),
    limited: isLimited(raw),
    smilies: Array.isArray(raw.smilies) ? raw.smilies.map(String) : [],
    stickers,
    pawns: normalizeApiPawns(raw),
    popularity: (raw.popularity as number) ?? 0,
    collection: parseCollection(raw.collection) ?? getCollectionDetails(slug),
  };
  return enrichPowerFromMock(base);
}

/** Resolve categoria de filtro a partir dos dados da API (type + tags). */
export function resolveApiFilterCategory(
  apiType: string,
  apiCategories: string[] = []
): PowerApiFilterCategory {
  const type = apiType?.toLowerCase().trim() ?? '';
  const tags = apiCategories ?? [];

  if (tags.includes('is_epic') || type === 'epic') return 'epic';
  if (tags.includes('is_game') || type === 'games' || type === 'gamebans') return 'games';
  if (tags.includes('is_hug') || type === 'hugs') return 'hug';
  if (tags.includes('is_jinx') || type === 'jinx') return 'jinx';
  if (tags.includes('is_collection')) return 'collection';
  if (tags.includes('is_stickers') || type === 'stickers') return 'stickers';
  if (tags.includes('is_group')) return 'group';
  if (
    type === 'function' ||
    type === 'fx' ||
    type === 'back' ||
    type === 'blasts' ||
    type === 'ace' ||
    type === 'colors' ||
    type === 'other'
  ) {
    return 'function';
  }
  return 'smiley';
}

function resolveCategory(raw: Record<string, unknown>): string {
  const stickers = Array.isArray(raw.stickers) ? raw.stickers : [];
  if (stickers.length > 0) return 'stickers';

  const apiType = typeof raw.type === 'string' ? raw.type : '';
  const apiCategories = (raw.categories as string[]) ?? [];
  if (apiType || apiCategories.length > 0) {
    return resolveApiFilterCategory(apiType, apiCategories);
  }
  return 'smiley';
}

function isLimited(raw: Record<string, unknown>): boolean {
  const cats = raw.categories as string[] | undefined;
  if (cats?.includes('is_limited')) return true;
  if (raw.limited === true) return true;
  if (raw.status === 'limited') return true;
  return false;
}

// --- Mock Data ----------------------------------------------------------------

export const MOCK_POWERS: PowerInfo[] = [
  // ── Funcionais ──────────────────────────────────────────────────────────────
  {
    id: 35,
    name: 'Purple',
    slug: 'purple',
    type: 'epic',
    description: {
      short: 'Peão roxo épico e posição privilegiada na lista de visitantes.',
      long: 'Purple é o primeiro power épico do xat. Com ele activo, o seu peão fica roxo independentemente do cargo na sala — visitante, membro, moderador ou dono. Sobe automaticamente na lista de visitantes acima dos demais do mesmo cargo. Inclui smilies temáticos e o hug exclusivo /hug purp. O peão roxo mantém-se mesmo em modo stealth.',
    },
    prices: { fairtrade_min: 23500, fairtrade_max: 24000, store: '25000' },
    status: false,
    released: '2023-10-26',
    category: 'epic',
    categories: ['is_epic', 'is_hug'],
    limited: false,
    smilies: ['purple', 'pucheer', 'pudizzy', 'pueyes', 'pueyes2', 'pufit', 'pushim', 'pusleep', 'puspray', 'putears', 'pucry'],
    pawns: [],
    popularity: 99,
  },
  {
    id: 153,
    name: 'Gold',
    slug: 'gold',
    type: 'epic',
    description: {
      short: 'Peão dourado épico e limitado — o mais exclusivo do xat.',
      long: 'Gold é um power épico e limitado que transforma o seu peão em dourado, sinalizando a posição de topo na hierarquia do xat. Extremamente raro e valorizado no fairtrade, é o símbolo máximo de prestígio na plataforma. Inclui smilies temáticos exclusivos.',
    },
    prices: { fairtrade_min: 62000, fairtrade_max: 63000, store: '50000' },
    status: 'limited',
    released: '2015-01-01',
    category: 'epic',
    limited: true,
    smilies: ['goldstar', 'bar', 'bars', 'goldb'],
    pawns: [],
    popularity: 95,
  },
  {
    id: 1,
    name: 'Topman',
    slug: 'topman',
    type: 'function',
    description: {
      short: 'Sobe na lista de visitantes acima dos outros usuários do mesmo cargo.',
      long: 'Topman faz com que o seu nome apareça mais acima na lista de visitantes em relação aos outros usuários com o mesmo cargo. Ideal para quem quer mais visibilidade na sala sem precisar de um power épico.',
    },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '200' },
    status: false,
    released: '2008-01-01',
    category: 'function',
    limited: false,
    smilies: ['topman'],
    pawns: [],
    popularity: 80,
    lore: {
      enabled: true,
      theme: 'normal',
      subtitle: 'Nos arquivos do xat',
      title: 'A história por trás do Topman',
      smiley: 'topman',
      intro: '',
      chapters: [
        {
          id: 'topman-lore-ch-1',
          label: 'HISTÓRIA',
          title: 'A primeira onda',
          body:
            '<p>O power [[Topman]] foi lançado junto com outros três powers, [[Nofollow]], [[Zoom]] e o [[Mod8]].</p><p>Nessa época, a idéia dos fundadores do xat era testar novas funcionalidades que pudessem melhorar a experiênmcia de usuários e moderadores.</p>',
        },
        {
          id: 'topman-lore-ch-2',
          label: 'CONTEXTO',
          title: 'Capítulo em construção',
          body: '<p>Conteúdo a definir.</p>',
        },
      ],
      curiosities: [
        {
          id: 'topman-lore-cu-1',
          title: 'Como era antes',
          body:
            '<p>Em 2011, antes do [[Purple]] surgir no universo do xat, o peão do centro que fica no smiley do power [[Topman]] era o peão preto.</p>',
          smiley: '/icons/oldtopman.svg',
        },
      ],
    },
  },
  {
    id: 21,
    name: 'Nameglow',
    slug: 'nameglow',
    type: 'function',
    description: {
      short: 'Efeito de brilho neon no nome de utilizador. Cor padrão: verde.',
      long: 'Nameglow adiciona um brilho neon ao seu nome no chat. A cor padrão é verde, mas pode ser personalizada. É também necessário para activar o power Namecolor e criar gradientes de cor no nome.',
    },
    prices: { fairtrade_min: 340, fairtrade_max: 360, store: '400' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 88,
  },
  {
    id: 103,
    name: 'Namecolor',
    slug: 'namecolor',
    type: 'epic',
    description: {
      short: 'Colore o seu nome com qualquer cor. Requer o power Nameglow.',
      long: 'Namecolor permite colorir o seu nome de utilizador no chat com total liberdade de cor. Para funcionar, é necessário ter o power Nameglow activo. Power épico muito procurado por quem quer personalização avançada do nome.',
    },
    prices: { fairtrade_min: 9400, fairtrade_max: 9700, store: '10000' },
    status: false,
    released: '2023-10-26',
    category: 'epic',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },
  {
    id: 25,
    name: 'Boot',
    slug: 'boot',
    type: 'function',
    description: {
      short: 'Transfere um utilizador para outra sala de chat.',
      long: 'Boot permite que moderadores e donos de salas transfiram utilizadores para outra sala de chat. Power limitado, essencial para moderação avançada e controlo de salas.',
    },
    prices: { fairtrade_min: 2800, fairtrade_max: 3300, store: '500' },
    status: 'limited',
    released: '2008-01-01',
    category: 'function',
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 12,
    name: 'Hat',
    slug: 'hat',
    type: 'function',
    description: {
      short: 'Adiciona um chapéu personalizado ao peão. Combinável e colorível.',
      long: 'Hat permite adicionar uma grande variedade de chapéus ao seu peão. Os chapéus podem ser combinados entre si e coloridos livremente, criando combinações únicas. Muitos outros powers desbloqueiam chapéus exclusivos quando o Hat está activo.',
    },
    prices: { fairtrade_min: 80, fairtrade_max: 90, store: '100' },
    status: false,
    released: '2008-01-01',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [
      { hat: '(hat#a)', name: 'p1pwn*00c000*hat1*w0i0ia' },
      { hat: '(hat#b)', name: 'p1pwn*00c000*hat1*w0i0ib' },
      { hat: '(hat#e)', name: 'p1pwn*00c000*hat1*w0i0ie' },
      { hat: '(hat#f)', name: 'p1pwn*00c000*hat1*w0i0if' },
      { hat: '(hat#g)', name: 'p1pwn*00c000*hat1*w3i0ig' },
      { hat: '(hat#p)', name: 'p1pwn*00c000*hat1*w0i0ip' },
      { hat: '(hat#r)', name: 'p1pwn*00c000*hat1*w0i0ir' },
    ],
    popularity: 82,
  },
  {
    id: 209,
    name: 'Xavi',
    slug: 'xavi',
    type: 'function',
    description: {
      short: 'Cria um avatar pessoal que reage ao que você escreve.',
      long: 'Xavi permite criar um avatar personalizado que reage dinamicamente ao texto que você escreve no chat. Escolha diferentes formatos de cabeça, penteados e acessórios para criar um avatar verdadeiramente único.',
    },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '500' },
    status: false,
    released: '2013-01-01',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 86,
    name: 'Blastban',
    slug: 'blastban',
    type: 'blasts',
    description: {
      short: 'Animações exclusivas quando alguém é banido na sala.',
      long: 'Blastban exibe animações especiais sempre que alguém recebe um ban na sala. Quanto mais powers Blastban estiverem atribuídos na sala, mais animações são desbloqueadas. Power de grupo que torna os bans muito mais dramáticos e divertidos.',
    },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '500' },
    status: false,
    released: '2012-01-01',
    category: 'group',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 640,
    name: 'Freeze',
    slug: 'freeze',
    type: 'function',
    description: {
      short: 'Congela qualquer smiley em qualquer posição da animação.',
      long: 'Freeze permite congelar um smiley animado em qualquer fotograma da sua animação. Útil para capturar expressões específicas e criar combinações visuais únicas com os seus smilies favoritos.',
    },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '299' },
    status: false,
    released: '2016-01-01',
    category: 'function',
    limited: false,
    smilies: ['stop1', 'stop2', 'stop3', 'stop4'],
    pawns: [],
    popularity: 65,
  },
  {
    id: 589,
    name: 'Face',
    slug: 'face',
    type: 'smiley',
    description: {
      short: 'Mistura e combina partes de smilies para criar rostos únicos.',
      long: 'Face permite combinar diferentes olhos, bocas e acessórios de smilies para criar milhares de rostos únicos. Com tantas combinações possíveis, cada utilizador pode criar expressões completamente originais que não existem em nenhum outro lugar.',
    },
    prices: { fairtrade_min: 450, fairtrade_max: 490, store: '499' },
    status: false,
    released: '2013-06-01',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 0,
    name: 'Allpowers',
    slug: 'allpowers',
    type: 'function',
    description: ALLPOWERS_DESCRIPTION,
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: '0' },
    status: false,
    released: '2008-01-01',
    category: 'function',
    limited: false,
    smilies: ['allpowers'],
    pawns: [{ hat: '(hat#e)', name: 'p1pwn*frnt1*w4096' }],
    popularity: 96,
  },
  {
    id: 95,
    name: 'Everypower',
    slug: 'everypower',
    type: 'epic',
    description: EVERYPOWER_DESCRIPTION,
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '',
    category: 'epic',
    limited: false,
    smilies: [],
    pawns: [...EVERYPOWER_PAWNS],
    popularity: 99,
  },
  // ── Pstyles ─────────────────────────────────────────────────────────────────
  {
    id: 720,
    name: 'Cstyle',
    slug: 'cstyle',
    type: 'function',
    description: {
      short: 'Personaliza o visual da sala com temas, fontes e muito mais.',
      long: 'Cstyle é um power de grupo que permite personalizar completamente o visual de uma sala de chat — temas, fontes, cores e outros elementos gráficos. Um dos powers mais recentes e versáteis para donos de salas que querem uma identidade visual única.',
    },
    prices: { fairtrade_min: 2500, fairtrade_max: 2800, store: '2900' },
    status: false,
    released: '2025-01-26',
    category: 'group',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 88,
  },
  // ── Smilies ──────────────────────────────────────────────────────────────────
  {
    id: 53,
    name: 'Anime',
    slug: 'anime',
    type: 'superanime',
    description: {
      short: 'Smilies de anime com expressões, penteados e acessórios variados.',
      long: 'Anime traz uma colecção diversificada de smilies inspirados na cultura japonesa — expressões marcantes, penteados icónicos, acessórios e muito mais. Uma das colecções clássicas e mais completas de smilies do xat.',
    },
    prices: { fairtrade_min: 200, fairtrade_max: 220, store: '300' },
    status: false,
    released: '2009-06-01',
    category: 'smiley',
    limited: false,
    smilies: ['grump', 'pouty', 'maniac', 'squint', 'doh', 'awe', 'grin', 'ahhh', 'fan', 'headband', 'hypno', 'dizzy', 'rage', 'comeon', 'quiver', 'tear', 'haira2', 'haira10', 'haira1', 'haira8', 'haira7', 'haira6', 'haira9', 'astro', 'haira5', 'haira4', 'haira3'],
    pawns: [],
    popularity: 86,
  },
  {
    id: 133,
    name: 'Space',
    slug: 'space',
    type: 'yellow',
    description: {
      short: 'Smilies espaciais com foguetões, planetas e aliens. Inclui hug.',
      long: 'Space transporta o xat para o cosmos com uma colecção de smilies galácticos — foguetões, satélites, telescópios, OVNIs, astronautas e muito mais. Inclui ainda um hug temático exclusivo. Perfeito para os amantes do universo.',
    },
    prices: { fairtrade_min: 180, fairtrade_max: 230, store: '300' },
    status: false,
    released: '2011-01-01',
    category: 'smiley',
    limited: false,
    smilies: ['space', 'eclipse', 'meteorite', 'nasa', 'rocket', 'radiotele', 'saucer', 'shuttle', 'telescope', 'satellite'],
    pawns: [],
    popularity: 77,
  },
  {
    id: 137,
    name: 'Dance',
    slug: 'dance',
    type: 'yellow',
    description: {
      short: 'Smilies animados com 11 estilos de dança diferentes.',
      long: 'Dance traz 11 smilies animados com movimentos de dança distintos e fluidos. Do breakdance ao shuffle, cada smilie captura um estilo diferente e traz energia e diversão às conversas no xat.',
    },
    prices: { fairtrade_min: 80, fairtrade_max: 100, store: '150' },
    status: false,
    released: '2010-01-01',
    category: 'smiley',
    limited: false,
    smilies: ['dance1', 'dance2', 'dance3', 'dance4', 'dance5', 'dance6', 'dance7', 'dance8', 'dance9', 'dance10', 'dance11'],
    pawns: [],
    popularity: 79,
  },
  {
    id: 49,
    name: 'Sport',
    slug: 'sport',
    type: 'yellow',
    description: {
      short: 'Smilies desportivos para apoiar a sua equipa favorita.',
      long: 'Sport apresenta uma colecção de smilies com temáticas desportivas — futebol, basquete, atletismo, troféus e muito mais. Ideal para os apaixonados por desporto que querem torcer e celebrar directamente no chat.',
    },
    prices: { fairtrade_min: 180, fairtrade_max: 200, store: '400' },
    status: false,
    released: '2009-06-01',
    category: 'smiley',
    limited: false,
    smilies: ['shirt', 'football', 'shirts', 'ball', 'bb', 'foam', 'weight', 'soccer', 'helmet', 'cupw', 'trophy'],
    pawns: [],
    popularity: 72,
  },
  {
    id: 117,
    name: 'Music',
    slug: 'music',
    type: 'yellow',
    description: {
      short: 'Smilies de instrumentos musicais e performance artística.',
      long: 'Music é para os amantes da música — smilies com instrumentos como guitarra, piano, trompete, harpa, violino, bateria e muito mais. Dê às suas conversas um toque artístico e musical único.',
    },
    prices: { fairtrade_min: 170, fairtrade_max: 190, store: '300' },
    status: false,
    released: '2011-01-01',
    category: 'smiley',
    limited: false,
    smilies: ['dj', 'flute', 'guitar', 'harp', 'piano', 'trumpet', 'disco', 'violin', 'drums', 'zippo'],
    pawns: [],
    popularity: 74,
  },
  {
    id: 299,
    name: 'Magic',
    slug: 'magic',
    type: 'other',
    description: {
      short: 'Smilies de magia com truques, coelhos e poções encantadas.',
      long: 'Magic traz uma colecção encantadora de smilies com temática de magia e ilusionismo — varinha mágica, coelho na cartola, cartas de baralho, poções e muito mais. Adicione um toque de mistério e maravilha às suas conversas.',
    },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2010-01-01',
    category: 'function',
    limited: false,
    smilies: ['magicwand', 'magictable', 'magicshow', 'magiclove', 'magician', 'magiccards', 'magicbunny', 'magicbottle', 'magicbook'],
    pawns: [],
    popularity: 73,
  },
  {
    id: 213,
    name: 'Zombie',
    slug: 'zombie',
    type: 'superscary',
    description: {
      short: 'Smilies de zombies e sobreviventes para os fãs de terror.',
      long: 'Zombie transforma o chat num cenário pós-apocalíptico com smilies de zombies e sobreviventes detalhados e expressivos. Para os fãs de terror que querem levar essa atmosfera para as suas conversas no xat.',
    },
    prices: { fairtrade_min: 180, fairtrade_max: 230, store: '300' },
    status: false,
    released: '2011-10-01',
    category: 'smiley',
    limited: false,
    smilies: ['zombie1', 'zombie2', 'zombie3', 'zombie4', 'survivor1', 'survivor2', 'survivor3', 'survivor4', 'bloodface', 'deadup', 'zombieback'],
    pawns: [],
    popularity: 70,
  },
  {
    id: 111,
    name: 'Fantasy',
    slug: 'fantasy',
    type: 'yellow',
    description: {
      short: 'Smilies de fantasia épica com dragões, elfos, magos e criaturas míticas.',
      long: 'Fantasy mergulha-o num mundo de fantasia épica com smilies de dragões, elfos, magos, orcs, crânios e criaturas míticas detalhadas. Uma das colecções mais ricas em lore e diversidade de personagens de todo o xat.',
    },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '250' },
    status: false,
    released: '2010-01-01',
    category: 'smiley',
    limited: false,
    smilies: ['dragon', 'wizard', 'elf2', 'ogre', 'orcm', 'orcf', 'medusa', 'axe', 'sword', 'flail', 'helmet2', 'scroll', 'slash', 'smskull', 'hole', 'darkeyes', 'invis'],
    pawns: [],
    popularity: 75,
  },
  {
    id: 113,
    name: 'Hero',
    slug: 'hero',
    type: 'yellow',
    description: {
      short: 'Smilies épicos de super-heróis com poderes únicos. Power limitado.',
      long: 'Hero apresenta uma colecção épica de smilies com super-heróis do xat — invisibilidade, electrocinese, telequinese, voo e muito mais. Power limitado e muito cobiçado pelos fãs de super-heróis.',
    },
    prices: { fairtrade_min: 250, fairtrade_max: 300, store: '250' },
    status: 'limited',
    released: '2012-01-01',
    category: 'smiley',
    limited: true,
    smilies: ['jekyl', 'invisi', 'iceman', 'genie', 'flying', 'fireball', 'epi', 'electro', 'daggers', 'maddr', 'telekin', 'amazon', 'upaway', 'ripshirt'],
    pawns: [],
    popularity: 78,
  },
  {
    id: 338,
    name: 'Gothic',
    slug: 'gothic',
    type: 'yellow',
    description: {
      short: 'Smilies góticos com estética dark, morcegos e caveiras. Power limitado.',
      long: 'Gothic apresenta uma colecção de smilies com estética gótica e sombria — morcegos, caveiras, metaleiros, asas e muito mais. Power limitado com designs dramáticos e memoráveis, perfeito para os fãs da cultura goth.',
    },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '240' },
    status: 'limited',
    released: '2013-10-01',
    category: 'smiley',
    limited: true,
    smilies: ['goth1', 'goth2', 'goth3', 'goth4', 'goth5', 'goth6', 'goth7', 'goth8', 'gothbat', 'gothskull', 'gothwings', 'gothmeta'],
    pawns: [],
    popularity: 69,
  },
  {
    id: 340,
    name: 'Pets',
    slug: 'pets',
    type: 'animal',
    description: {
      short: 'Smilies de animais de estimação — gato, cão, hamster e mais.',
      long: 'Pets traz uma adorável colecção de smilies de animais de estimação — gato, cão, peixe, hamster, papagaio, macaco, cobra, tartaruga e camaleão. Para os amantes de animais que querem partilhar esse amor no chat.',
    },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '220' },
    status: false,
    released: '2013-01-01',
    category: 'smiley',
    limited: false,
    smilies: ['pcat', 'pdog', 'pfish', 'phamster', 'pparrot', 'pmonkey', 'psnake', 'pturtle', 'pchameleon'],
    pawns: [],
    popularity: 76,
  },
  // ── Smilies Limitados ────────────────────────────────────────────────────────
  {
    id: 62,
    name: 'Valentine',
    slug: 'valentine',
    type: 'superlove',
    description: {
      short: 'Smilies de Dia dos Namorados — corações, rosas e cupidinhos.',
      long: 'Valentine celebra o Dia dos Namorados com uma colecção de smilies románticos — corações animados, rosas, anel de noivado, cupido e muito mais. Power limitado lançado para a época mais romântica do ano.',
    },
    prices: { fairtrade_min: 3200, fairtrade_max: 3600, store: '2900' },
    status: 'limited',
    released: '2009-02-14',
    category: 'smiley',
    limited: true,
    smilies: ['hug3', 'beat', 'bheart', 'cupid', 'card', 'hug2', 'ilu', 'rose', 'ring', 'rose2', 'lhand'],
    pawns: [],
    popularity: 82,
  },
  {
    id: 52,
    name: 'Halloween',
    slug: 'halloween',
    type: 'superhalloween',
    description: {
      short: 'Smilies de Halloween com monstros, abóboras e fantasmas.',
      long: 'Halloween traz o espírito da data mais assustadora do ano ao xat — abóboras, fantasmas, múmias, bruxas, caveiras e muito mais. Power limitado lançado especialmente para a temporada de Halloween.',
    },
    prices: { fairtrade_min: 1700, fairtrade_max: 2200, store: '667' },
    status: 'limited',
    released: '2009-10-31',
    category: 'smiley',
    limited: true,
    smilies: ['cdn', 'pkn', 'tort', 'frk', 'mmy', 'pknlaugh', 'wh', 'grim', 'ghost', 'bat', 'dig', 'die', 'tomb'],
    pawns: [],
    popularity: 80,
  },
  {
    id: 57,
    name: 'Christmas',
    slug: 'christmas',
    type: 'superxmas',
    description: {
      short: '26 smilies natalinos — Pai Natal, renas, presentes e neve.',
      long: 'Christmas celebra o Natal com 26 smilies festivos — Pai Natal, renas, trenó, elfo, árvore de Natal, presentes, meias, bolas decorativas e muito mais. Power limitado lançado especialmente para a época natalícia.',
    },
    prices: { fairtrade_min: 2200, fairtrade_max: 2500, store: '2290' },
    status: 'limited',
    released: '2009-12-01',
    category: 'smiley',
    limited: true,
    smilies: ['scarf', 'sack', 'sdeer', 'sfeet', 'sball', 'pole', 'cane', 'elf', 'ches', 'sledge', 'bell', 'beard', 'give', 'slist', 'shiver', 'bulb', 'spull', 'stock', 'tree', 'wreath', 'xb1', 'xb4', 'skiss', 'smound', 'sgift'],
    pawns: [],
    popularity: 84,
  },
  {
    id: 586,
    name: 'Springtime',
    slug: 'springtime',
    type: 'smiley',
    description: {
      short: 'Smilies de primavera com flores, abelhas e chuva. Power limitado.',
      long: 'Springtime celebra a chegada da primavera com smilies temáticos — sol, flores, abelhas, pássaros, plantas, joaninhas, chuva e até neve tardia. Power limitado lançado na época da primavera.',
    },
    prices: { fairtrade_min: 800, fairtrade_max: 1000, store: '229' },
    status: 'limited',
    released: '2019-04-01',
    category: 'smiley',
    limited: true,
    smilies: ['spflower', 'spbee', 'spbird', 'spsun', 'spplant', 'spladybug', 'sprainy', 'spsnow', 'sppaint'],
    pawns: [],
    popularity: 70,
  },
  // ── Stickers ─────────────────────────────────────────────────────────────────
  {
    id: 742,
    name: 'Stella',
    slug: 'stella',
    type: 'stickers',
    description: {
      short: 'Stickers delicados e smilies encantadores com charme suave.',
      long: 'Stella traz stickers delicados e smilies com charme suave e elegante. Inclui tema bónus Pstyle Stella (requer power Pstyle).',
    },
    prices: { fairtrade_min: 190, fairtrade_max: 210, store: '220' },
    status: false,
    released: '2026-03-29',
    category: 'stickers',
    limited: false,
    smilies: ['stellamoonb', 'stellacloudb', 'hattip2', 'stellacb5', 'stellamb4', 'stellacb4', 'stelladt', 'stellahair', 'stellacb', 'stellamb'],
    pawns: [
      { hat: '(hat#hd)', name: 'p1sstella' },
      { hat: '(hat#hV)', name: 'p1sstella2' },
      { hat: '(hat#hi)', name: 'p1sstella3' },
      { hat: '(hat#hf)', name: 'p1sstella4' },
    ],
    popularity: 80,
  },
  // ── Games ────────────────────────────────────────────────────────────────────
  {
    id: 224,
    name: 'Hearts',
    slug: 'hearts',
    type: 'games',
    description: {
      short: 'Jogo clássico de cartas Hearts para até 4 jogadores.',
      long: 'Hearts permite jogar o clássico jogo de cartas Hearts directamente no chat com até 4 jogadores. Power de grupo que adiciona entretenimento interactivo à sala, ideal para quem quer uma actividade diferente além da conversa.',
    },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '500' },
    status: false,
    released: '2017-01-01',
    category: 'games',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 192,
    name: 'Matchrace',
    slug: 'matchrace',
    type: 'games',
    description: {
      short: 'Corrida de velocidade no chat — quem chega primeiro vence!',
      long: 'Matchrace é um game power de grupo e limitado onde os jogadores competem para atingir o número de metros especificado antes dos outros. Introduz uma dinâmica de corrida por tempo real directamente no chat, com muita adrenalina.',
    },
    prices: { fairtrade_min: 400, fairtrade_max: 600, store: '691' },
    status: 'limited',
    released: '2014-01-01',
    category: 'games',
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 745,
    name: 'Beanie',
    slug: 'beanie',
    type: 'smiley',
    description: { short: 'Beanie smilies', long: 'Chill moods, desk moments, snacks... Well, it is BEANIE. It is something different.' },
    prices: { fairtrade_min: 200, fairtrade_max: 220, store: '15' },
    status: 'limited',
    released: '2026-06-01',
    category: 'smiley',
        categories: ['is_allpowers', 'is_new', 'is_latest', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 36,
  },
  {
    id: 746,
    name: 'Roshi',
    slug: 'roshi',
    type: 'smiley',
    description: {
      short: 'Smilies de praia com óculos, ondas e energia de tartaruga.',
      long: 'Ondas de praia, óculos cool e energia de tartaruga. Roshi está pronto para surfar no seu chat com smilies temáticos de verão e litoral.',
    },
    prices: { fairtrade_min: 1000, fairtrade_max: 1500, store: '340' },
    status: 'limited',
    released: '2026-06-16',
    category: 'smiley',
    categories: ['is_new', 'is_limited'],
    limited: true,
    smilies: ['rhshades', 'rhseaweed', 'rhsand', 'rhplastic', 'rhjelly', 'rhheartbubble', 'rhhattop', 'rhfish', 'rhcrossed', 'rhboardtop', 'rhboard', 'rhback', 'rhbaby'],
    pawns: [
      { hat: '(hat#hQ)', name: 'p1roh1' },
      { hat: '(hat#hm)', name: 'p1roh7' },
      { hat: '(hat#hJ)', name: 'p1roh6' },
      { hat: '(hat#hn)', name: 'p1roh5' },
      { hat: '(hat#hG)', name: 'p1roh4' },
      { hat: '(hat#hR)', name: 'p1roh3' },
      { hat: '(hat#ho)', name: 'p1roh2' },
    ],
    popularity: 82,
  },
  {
    id: 747,
    name: 'Dad',
    slug: 'dad',
    type: 'smiley',
    description: {
      short: 'Smilies com energia de pai para celebrar o Dia dos Pais.',
      long: 'O power da energia de pai, criado para o Dia Internacional dos Pais. Smilies divertidos com cerveja, urso, super-herói, futebol e muito mais.',
    },
    prices: { fairtrade_min: 250, fairtrade_max: 290, store: '300' },
    status: false,
    released: '2026-06-21',
    category: 'smiley',
    categories: ['is_new'],
    limited: false,
    smilies: ['ddbeer', 'ddbear', 'ddtop', 'ddsuper', 'ddrod', 'ddmug', 'ddhold', 'ddheart', 'ddhands', 'ddcard', 'ddsoccer', 'ddbike', 'dad'],
    pawns: [
      { hat: '(hat#hB)', name: 'p1dad9' },
      { hat: '(hat#hV)', name: 'p1dad8' },
      { hat: '(hat#hf)', name: 'p1dad7' },
      { hat: '(hat#hq)', name: 'p1dad6' },
      { hat: '(hat#hY)', name: 'p1dad5' },
      { hat: '(hat#hS)', name: 'p1dad4' },
      { hat: '(hat#hX)', name: 'p1dad3' },
      { hat: '(hat#hb)', name: 'p1dad2' },
      { hat: '(hat#hD)', name: 'p1dad1' },
    ],
    popularity: 84,
  },
  {
    id: 748,
    name: 'Astrox',
    slug: 'astrox',
    type: 'smiley',
    description: {
      short: 'Smilies cósmicos com energia de astronauta.',
      long: 'Do espaço profundo, Astrox traz moods cósmicos e energia de astronauta para o seu chat. Inclui back smiley e bónus PSTYLE.',
    },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: '365' },
    status: 'limited',
    released: '2026-06-26',
    category: 'smiley',
    categories: ['is_new', 'is_latest', 'is_limited'],
    limited: true,
    smilies: ['astback', 'astthink', 'astno', 'astcry', 'astlove', 'astheart', 'astsleep', 'astice', 'astshine'],
    pawns: [
      { hat: '(hat#hr)', name: 'p1astice' },
      { hat: '(hat#hP)', name: 'p1astheart' },
      { hat: '(hat#hE)', name: 'p1astlove' },
      { hat: '(hat#hy)', name: 'p1astshine' },
      { hat: '(hat#hU)', name: 'p1astmoon2' },
    ],
    popularity: 85,
  },
  {
    id: 749,
    name: 'Bowgal',
    slug: 'bowgal',
    type: 'smiley',
    description: {
      short: 'Smilies Bowgal com laços, humor e moods fofos.',
      long: 'Bowgal é a coleção mais recente de smilies com estética de laço e expressões divertidas — de sonolenta a apaixonada. Inclui dez smilies temáticos e cinco peões exclusivos para personalizar o seu pawn no chat.',
    },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: '300' },
    status: false,
    released: '2026-07-06',
    category: 'smiley',
    categories: ['is_new', 'is_latest'],
    limited: false,
    smilies: [
      'bwgannoy',
      'bwgbags',
      'bwgblanket',
      'bwgcoffee',
      'bwgcrybaby',
      'bwgcute',
      'bwgpout',
      'bwghearts',
      'bwgsleepy',
      'bwgwhat',
    ],
    pawns: [
      { hat: '(hat#he)', name: 'p1bwg1' },
      { hat: '(hat#ht)', name: 'p1bwg2' },
      { hat: '(hat#hM)', name: 'p1bwg3' },
      { hat: '(hat#hK)', name: 'p1bwg4' },
      { hat: '(hat#hg)', name: 'p1bwg5' },
    ],
    popularity: 88,
  },
  {
    id: 744,
    name: 'Libro',
    slug: 'libro',
    type: 'superhobby',
    description: { short: 'Libro smilies', long: 'It is time to read.' },
    prices: { fairtrade_min: 500, fairtrade_max: 700, store: '222' },
    status: 'limited',
    released: '2026-05-03',
    category: 'smiley',
        categories: ['is_new', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 36,
  },
  {
    id: 743,
    name: 'Leafy',
    slug: 'leafy',
    type: 'smilies',
    description: { short: 'Leafy smilies', long: 'Stay dry in the rain.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1200, store: '325' },
    status: 'limited',
    released: '2026-04-19',
    category: 'smiley',
        categories: ['is_new', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 36,
  },
  {
    id: 741,
    name: 'Patday',
    slug: 'patday',
    type: 'smiley',
    description: { short: 'Patday smilies', long: 'A touch of luck. A pot of gold. St. Patrick themed smilies.' },
    prices: { fairtrade_min: 900, fairtrade_max: 1200, store: '300' },
    status: 'limited',
    released: '2026-03-14',
    category: 'smiley',
        categories: ['is_new', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 36,
  },
  {
    id: 740,
    name: 'Pengfun',
    slug: 'pengfun',
    type: 'smiley',
    description: { short: 'Pengfun smilies', long: 'Tiny penguin. Big mood.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1300, store: '300' },
    status: 'limited',
    released: '2026-02-21',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 36,
  },
  {
    id: 739,
    name: 'Lovebeat',
    slug: 'lovebeat',
    type: 'superlove',
    description: { short: 'Lovebeat smilies', long: 'Headphones on, love on repeat. Includes 15 smilies. Bonus back.' },
    prices: { fairtrade_min: 1500, fairtrade_max: 2000, store: '350' },
    status: 'limited',
    released: '2026-02-09',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 36,
  },
  {
    id: 738,
    name: 'Vaf',
    slug: 'vaf',
    type: 'ace',
    description: { short: 'valentine frames', long: '6 valentine avatar frames;Special rounded frame' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: '60' },
    status: false,
    released: '2026-04-19',
    category: 'function',
    limited: false,
    smilies: ['vaf'],
    pawns: [
      { hat: '(hat#hq)', name: 'p1vaf1' },
      { hat: '(hat#ht)', name: 'p1vaf3' },
      { hat: '(hat#he)', name: 'p1vaf4' },
      { hat: '(hat#hf)', name: 'p1vaf2' },
      { hat: '(hat#hi)', name: 'p1vaf5' },
    ],
    popularity: 36,
  },
  {
    id: 737,
    name: 'Kitsune',
    slug: 'kitsune',
    type: 'smiley',
    description: {
      short: 'Kitsune smilies',
      long: 'Fox-themed smilies for the chat. Bonus Kitsune Pstyle theme included (requires Pstyle power).',
    },
    prices: { fairtrade_min: 2300, fairtrade_max: 2800, store: '333' },
    status: 'limited',
    released: '2026-01-17',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [
      'kitsune',
      'kitchat',
      'kitdance',
      'kitheaven',
      'kitwind',
      'kitstorm',
      'kitnight',
      'kitcool',
      'kitaqua',
      'kitfire',
      'kitice',
      'kitlove',
      'kitnature',
      'kithalo',
      'kitmask',
      'kitraio',
      'kitread',
      'kitninja',
      'kitpirate',
      'kitcowboy',
      'kitradio',
      'kitdemon',
    ],
    pawns: [
      { hat: '(hat#hw)', name: 'p1kitfire' },
      { hat: '(hat#hy)', name: 'p1kitice' },
      { hat: '(hat#hu)', name: 'p1kitnature' },
      { hat: '(hat#hs)', name: 'p1kitmask' },
      { hat: '(hat#hz)', name: 'p1kithalo' },
      { hat: '(hat#hx)', name: 'p1kitraio' },
    ],
    popularity: 36,
  },
  {
    id: 736,
    name: 'Xaf',
    slug: 'xaf',
    type: 'ace',
    description: { short: 'xmas frames', long: '2 christmas avatar frames' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: '20' },
    status: false,
    released: '2026-03-15',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 36,
  },
  {
    id: 734,
    name: 'Ratmas',
    slug: 'ratmas',
    type: 'superxmas',
    description: { short: 'Ratmas smilies', long: 'Its... RATMAS. Bonus back smiley included.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '25' },
    status: false,
    released: '2025-12-20',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 733,
    name: 'Kide',
    slug: 'kide',
    type: 'smiley',
    description: { short: 'Kide smilies', long: 'THIS CAT GOT 9 LIVES.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1500, store: '25' },
    status: 'limited',
    released: '2025-12-13',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 732,
    name: 'Kwinter',
    slug: 'kwinter',
    type: 'superxmas',
    description: { short: 'Kwinter smilies', long: 'Created for Winter Festival 2025 edition. Snow smilies to chill your chats. Bonus back included.' },
    prices: { fairtrade_min: 1500, fairtrade_max: 2000, store: '355' },
    status: 'limited',
    released: '2025-12-08',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 731,
    name: 'Lems',
    slug: 'lems',
    type: 'smiley',
    description: {
      short: 'Lems smilies and stickers',
      long: 'SWEET. SOUR. IMPOSSIBLY ADORABLE. Lemonade stickers and smilies. Bonus Lems Pstyle theme included (requires Pstyle power).',
    },
    prices: { fairtrade_min: 800, fairtrade_max: 1300, store: '365' },
    status: 'limited',
    released: '2025-11-22',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: ['lems', 'lemsback', 'lemsglasses', 'lemscheers', 'lemshair', 'lemspitcher'],
    pawns: [
      { hat: '(hat#hG)', name: 'p1lems1' },
      { hat: '(hat#hr)', name: 'p1lems2' },
      { hat: '(hat#hg)', name: 'p1lems3' },
      { hat: '(hat#hm)', name: 'p1lems4' },
      { hat: '(hat#hB)', name: 'p1lems5' },
      { hat: '(hat#hD)', name: 'p1lems6' },
      { hat: '(hat#hR)', name: 'p1lems7' },
    ],
    popularity: 37,
  },
  {
    id: 730,
    name: 'Morgana',
    slug: 'morgana',
    type: 'superhalloween',
    description: { short: 'Morgana stickers', long: 'Potions, charms, and the cutest stickers. Bonus pstyle included.' },
    prices: { fairtrade_min: 120, fairtrade_max: 140, store: '150' },
    status: false,
    released: '2025-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 729,
    name: 'Horrorgal',
    slug: 'horrorgal',
    type: 'superscary',
    description: { short: 'Horrorgal smilies', long: 'These smilies will find you. Bonus pstyle included.' },
    prices: { fairtrade_min: 1200, fairtrade_max: 1500, store: '366' },
    status: 'limited',
    released: '2025-10-19',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 728,
    name: 'Pawnhue',
    slug: 'pawnhue',
    type: 'ace',
    description: { short: 'Shift pawn hue', long: 'Shift your pawns hue for flair;Requires pawn powers' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: '300' },
    status: false,
    released: '2025-12-14',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 727,
    name: 'Cupcat',
    slug: 'cupcat',
    type: 'smiley',
    description: { short: 'Cupcat smilies', long: 'Half cupcake... half cat. One hundred percent cuteness guaranteed.' },
    prices: { fairtrade_min: 1600, fairtrade_max: 1900, store: '377' },
    status: 'limited',
    released: '2025-09-13',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 726,
    name: 'Rider',
    slug: 'rider',
    type: 'smiley',
    description: { short: 'Rider smilies', long: 'VROOOOM. Smilies who live on the road.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1200, store: '280' },
    status: 'limited',
    released: '2025-08-10',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 725,
    name: 'Emma',
    slug: 'emma',
    type: 'stickers',
    description: { short: 'Emma stickers', long: 'Photo lovers, meet Emma!' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '211' },
    status: 'limited',
    released: '2025-08-10',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 724,
    name: 'Queenb',
    slug: 'queenb',
    type: 'superfarm',
    description: { short: 'Queenb smilies', long: 'The queen has arrived. Royal smilies for your chat.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1200, store: '245' },
    status: 'limited',
    released: '2025-06-15',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 723,
    name: 'Lapin',
    slug: 'lapin',
    type: 'superbunny',
    description: { short: 'Lapin smilies', long: 'Shades, crown, and more. Meet the coolest bunny around.' },
    prices: { fairtrade_min: 250, fairtrade_max: 350, store: '279' },
    status: 'limited',
    released: '2025-04-20',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 722,
    name: 'Tele',
    slug: 'tele',
    type: 'smiley',
    description: { short: 'Tele smilies', long: 'Tune in and turn back time! Vintage TV power, with 16 retro-style smilies.' },
    prices: { fairtrade_min: 700, fairtrade_max: 1000, store: '225' },
    status: 'limited',
    released: '2025-04-05',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 721,
    name: 'Beaver',
    slug: 'beaver',
    type: 'superzoo',
    description: { short: 'Beaver smilies', long: 'It started with one stick, then two, and now your whole chat is a dam. Welcome Beaver, your chat may get redecorated with twigs.' },
    prices: { fairtrade_min: 2300, fairtrade_max: 2800, store: '300' },
    status: 'limited',
    released: '2025-03-23',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 37,
  },
  {
    id: 719,
    name: 'Selflove',
    slug: 'selflove',
    type: 'superlove',
    description: { short: 'Selflove smilies', long: 'Show yourself some love and confidence with these smilies!' },
    prices: { fairtrade_min: 400, fairtrade_max: 600, store: '325' },
    status: 'limited',
    released: '2025-02-13',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 718,
    name: 'Flipflop',
    slug: 'flipflop',
    type: 'smiley',
    description: { short: 'Flipflop smilies', long: 'Watch out for the flying flip-flops... These smilies have a personality of their own and an unusual love for clean feet.' },
    prices: { fairtrade_min: 100, fairtrade_max: 120, store: '150' },
    status: false,
    released: '2025-01-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 717,
    name: 'Elfo',
    slug: 'elfo',
    type: 'supersanta',
    description: { short: 'Elfo smilies', long: 'Meet Santas helper! Grab ELFO now and add magic to your chat.' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '299' },
    status: 'limited',
    released: '2024-12-23',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 716,
    name: 'Cerf',
    slug: 'cerf',
    type: 'supersanta',
    description: { short: 'Cerf smilies', long: 'Deck the chats with CERF! Cute and stylish deer smilies to sleigh your holidays.' },
    prices: { fairtrade_min: 700, fairtrade_max: 900, store: '270' },
    status: 'limited',
    released: '2024-12-14',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 715,
    name: 'Anigirl',
    slug: 'anigirl',
    type: 'superanime',
    description: { short: 'Anigirl smilies', long: 'Unleash your anime moods!' },
    prices: { fairtrade_min: 400, fairtrade_max: 600, store: '300' },
    status: 'limited',
    released: '2024-12-07',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 714,
    name: 'Fur',
    slug: 'fur',
    type: 'smiley',
    description: { short: 'Fur smilies', long: 'This power is here to make your chat cuter, funnier, and irresistibly fluffy! WARNING! Using FUR smilies may cause uncontrollable laughter and endless fun.' },
    prices: { fairtrade_min: 1200, fairtrade_max: 1500, store: '389' },
    status: 'limited',
    released: '2024-11-22',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 713,
    name: 'Handy',
    slug: 'handy',
    type: 'smiley',
    description: { short: 'Handy smilies', long: 'Let your hands do the talking! This power brings gestures like a peace sign, a thumbs-up, and more, all crafted in a charming, minimalistic cartoon style.' },
    prices: { fairtrade_min: 300, fairtrade_max: 500, store: '179' },
    status: 'limited',
    released: '2024-11-16',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 712,
    name: 'Susto',
    slug: 'susto',
    type: 'superscary',
    description: { short: 'Susto smilies', long: 'Prepare for the scare! Creepy, spooky smilies to give your chat a haunting touch.  ' },
    prices: { fairtrade_min: 200, fairtrade_max: 300, store: '150' },
    status: 'limited',
    released: '2024-11-02',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 711,
    name: 'Candybear',
    slug: 'candybear',
    type: 'superzoo',
    description: { short: 'Bearlicious', long: 'These sweet looking bears are ready for your smiley combos. No bears were harmed for the creation of this power. Bonus back added. MAY COINTAIN SUGAR.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1300, store: '379' },
    status: 'limited',
    released: '2024-10-19',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 710,
    name: 'Anna',
    slug: 'anna',
    type: 'smiley',
    description: { short: 'Anna smilies', long: 'These smilies have a sweet style... and attitude!' },
    prices: { fairtrade_min: 250, fairtrade_max: 350, store: '200' },
    status: 'limited',
    released: '2024-10-05',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 709,
    name: 'Magik',
    slug: 'magik',
    type: 'smiley',
    description: { short: 'Magik smilies', long: 'Add a sprinkle of magik to your chat with these majestik kaoani smilies.' },
    prices: { fairtrade_min: 200, fairtrade_max: 230, store: '20' },
    status: false,
    released: '2024-09-22',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 708,
    name: 'Puff',
    slug: 'puff',
    type: 'smiley',
    description: { short: 'Puff smilies', long: 'These expressive puffy smilies are waiting for you.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '200' },
    status: 'limited',
    released: '2024-09-11',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 707,
    name: 'Squid',
    slug: 'squid',
    type: 'smiley',
    description: { short: 'Squid smilies', long: 'Squid! Squids everywhere!' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '260' },
    status: 'limited',
    released: '2024-08-31',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 706,
    name: 'Starry',
    slug: 'starry',
    type: 'smiley',
    description: { short: 'Starry smilies', long: 'Turn your chat into a starry sky with this power. Backs included. ' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '250' },
    status: 'limited',
    released: '2024-08-24',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 705,
    name: 'Axo',
    slug: 'axo',
    type: 'smiley',
    description: { short: 'Axo smilies', long: 'Amphibious smilies to your chat. Back included. LIMITED!' },
    prices: { fairtrade_min: 350, fairtrade_max: 450, store: '299' },
    status: 'limited',
    released: '2024-08-10',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 38,
  },
  {
    id: 704,
    name: 'Cogu',
    slug: 'cogu',
    type: 'superfarm',
    description: { short: 'Mushroom smilies', long: 'Turn your chat into a fungi party with these mushroom-themed smilies. LIMITED! Bonus mushroom profile theme (requires pstyle power).' },
    prices: { fairtrade_min: 600, fairtrade_max: 700, store: '356' },
    status: 'limited',
    released: '2024-07-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [
      'cogu',
      'cgsurprise',
      'cgstarry',
      'cgsad',
      'cgrainbow',
      'cgkissy',
      'cgirked',
      'cgha',
      'cgevil',
      'cgcry',
      'cgback',
    ],
    pawns: [],
    popularity: 39,
  },
  {
    id: 702,
    name: 'Iconcolor',
    slug: 'iconcolor',
    type: 'ace',
    description: { short: 'Color pawn icon', long: 'Change your pawn icon color;Supports star, heart and BFF' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: '150' },
    status: false,
    released: '2024-08-31',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 701,
    name: 'Frosch',
    slug: 'frosch',
    type: 'superfarm',
    description: { short: 'Frosch smilies', long: 'It is a, Ribbit! frog-themed power. Ribbit!' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '375' },
    status: 'limited',
    released: '2024-07-15',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 700,
    name: 'Yellow',
    slug: 'yellow',
    type: 'ace',
    description: { short: 'Yellow pawn', long: 'Turn your pawn into yellow;Unlock Sunburst pstyle' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: '600' },
    status: false,
    released: '2026-02-14',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 699,
    name: 'Maus',
    slug: 'maus',
    type: 'smiley',
    description: { short: 'Say cheese', long: 'All these cute mouse smilies want one thing in common.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1200, store: '250' },
    status: 'limited',
    released: '2024-05-24',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 698,
    name: 'Flor',
    slug: 'flor',
    type: 'smiley',
    description: { short: 'Flor smilies', long: 'Flower-themed smilies freshly picked from xat garden. 5 backs included.' },
    prices: { fairtrade_min: 400, fairtrade_max: 600, store: '300' },
    status: 'limited',
    released: '2024-05-17',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 697,
    name: 'Starfx',
    slug: 'starfx',
    type: 'superfx',
    description: { short: 'Star FXs', long: 'Add star effects to smilies.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1100, store: '299' },
    status: 'limited',
    released: '2024-05-12',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 696,
    name: 'Likes',
    slug: 'likes',
    type: 'smiley',
    description: { short: 'Likes smilies', long: 'These social media themed smilies really need your likes. COMMENT! FOLLOW!' },
    prices: { fairtrade_min: 230, fairtrade_max: 270, store: '300' },
    status: false,
    released: '2024-05-07',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 695,
    name: 'Superhistory',
    slug: 'superhistory',
    type: 'supers',
    description: { short: 'History Collection', long: 'History themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2026-06-10',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 694,
    name: 'Terra',
    slug: 'terra',
    type: 'smiley',
    description: { short: 'Terra smilies', long: 'Go green or go home. Earth themed smilies.' },
    prices: { fairtrade_min: 400, fairtrade_max: 600, store: '250' },
    status: 'limited',
    released: '2024-04-19',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 693,
    name: 'Creamy',
    slug: 'creamy',
    type: 'smiley',
    description: { short: 'Creamy smilies', long: 'These ice cream cones are just chilling and waiting to sweeten up your chat. Back smilies included.' },
    prices: { fairtrade_min: 3000, fairtrade_max: 3500, store: '256' },
    status: 'limited',
    released: '2024-04-14',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 692,
    name: 'Tony',
    slug: 'tony',
    type: 'stickers',
    description: { short: 'Tony stickers', long: 'This is Tony, the cat. Black cat stickers.' },
    prices: { fairtrade_min: 280, fairtrade_max: 320, store: '211' },
    status: 'limited',
    released: '2024-02-25',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 691,
    name: 'Sadie',
    slug: 'sadie',
    type: 'stickers',
    description: { short: 'Sadie stickers', long: 'These stickers will haunt your chats. Bonus smilies added.' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '366' },
    status: 'limited',
    released: '2024-02-19',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 690,
    name: 'Android',
    slug: 'android',
    type: 'superhistory',
    description: { short: 'Android stickers', long: 'Error 404: Boredom protocol overridden! Join the Android revolution.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '350' },
    status: 'limited',
    released: '2024-01-27',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 39,
  },
  {
    id: 689,
    name: 'Silo',
    slug: 'silo',
    type: 'smiley',
    description: { short: 'Silo smilies', long: 'Silhouettes.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '300' },
    status: 'limited',
    released: '2024-01-20',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 688,
    name: 'Nyfun',
    slug: 'nyfun',
    type: 'smiley',
    description: { short: 'New year fun smilies', long: 'More fun smilies to celebrate the new year.' },
    prices: { fairtrade_min: 120, fairtrade_max: 140, store: '150' },
    status: false,
    released: '2024-01-02',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 687,
    name: 'Cheers',
    slug: 'cheers',
    type: 'stickers',
    description: { short: 'Cheers stickers', long: 'Celebrate new year with these stickers.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '200' },
    status: 'limited',
    released: '2023-12-29',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 686,
    name: 'Furball',
    slug: 'furball',
    type: 'smiley',
    description: { short: 'Furball smilies', long: 'Furball joy to match your holiday power combinations.' },
    prices: { fairtrade_min: 700, fairtrade_max: 800, store: '200' },
    status: 'limited',
    released: '2023-12-24',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 685,
    name: 'Snowies',
    slug: 'snowies',
    type: 'smiley',
    description: { short: 'Snowies smilies', long: 'Let it snow.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '225' },
    status: 'limited',
    released: '2023-12-23',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 684,
    name: 'Pawns',
    slug: 'pawns',
    type: 'games',
    description: { short: 'Pawns card game', long: 'Play the pawns card game with up to 4 players, featuring Special Mode and Duo Mode. Team up or emerge victorious against all challengers.' },
    prices: { fairtrade_min: 1300, fairtrade_max: 1400, store: '1500' },
    status: false,
    released: '2024-03-04',
    category: 'games',
        categories: ['is_game'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 683,
    name: 'Chilly',
    slug: 'chilly',
    type: 'stickers',
    description: { short: 'Freeze the moment', long: 'The winter\'s coolest stickers are here.' },
    prices: { fairtrade_min: 700, fairtrade_max: 800, store: '299' },
    status: 'limited',
    released: '2023-12-18',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 682,
    name: 'Gkturkey',
    slug: 'gkturkey',
    type: 'smiley',
    description: { short: 'Gkaoani turkeys', long: 'Assign these kaoani-style smilies to your room. It is turkey time.' },
    prices: { fairtrade_min: 600, fairtrade_max: 900, store: '250' },
    status: 'limited',
    released: '2023-11-19',
    category: 'group',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 681,
    name: 'Snowball',
    slug: 'snowball',
    type: 'smiley',
    description: { short: 'Break the ice', long: 'Doge those snowballs in style.' },
    prices: { fairtrade_min: 380, fairtrade_max: 500, store: '199' },
    status: 'limited',
    released: '2023-12-08',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 680,
    name: 'Patches',
    slug: 'patches',
    type: 'stickers',
    description: { short: 'Patches stickers', long: 'Got trouble? Grab the banana phone and peel away your problems!' },
    prices: { fairtrade_min: 800, fairtrade_max: 1000, store: '500' },
    status: 'limited',
    released: '2023-11-19',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 679,
    name: 'Ghosty',
    slug: 'ghosty',
    type: 'superhalloween',
    description: { short: 'Feel the presence', long: 'Have you ever seen a ghost?' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '242' },
    status: 'limited',
    released: '2023-11-06',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 678,
    name: 'Hallowneon',
    slug: 'hallowneon',
    type: 'superhalloween',
    description: { short: 'Halloween neon', long: 'Time for a spook-tacular power.' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '366' },
    status: 'limited',
    released: '2023-11-06',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 677,
    name: 'Autumnmood',
    slug: 'autumnmood',
    type: 'smiley',
    description: { short: 'Autumn mood', long: 'Refine your fall look with these smilies.' },
    prices: { fairtrade_min: 230, fairtrade_max: 250, store: '210' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 676,
    name: 'Krobo',
    slug: 'krobo',
    type: 'smiley',
    description: { short: 'Robo heads', long: 'Because robots can be cute too.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '244' },
    status: 'limited',
    released: '2023-11-19',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 675,
    name: 'Moyai',
    slug: 'moyai',
    type: 'superhistory',
    description: { short: 'Moyai', long: 'Summer time on Easter Island.' },
    prices: { fairtrade_min: 230, fairtrade_max: 270, store: '277' },
    status: 'limited',
    released: '2023-11-06',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 40,
  },
  {
    id: 674,
    name: 'Mood',
    slug: 'mood',
    type: 'smiley',
    description: { short: 'Feelings', long: 'Show how you\'re feeling with this new mood power!' },
    prices: { fairtrade_min: 600, fairtrade_max: 800, store: '345' },
    status: 'limited',
    released: '2023-11-06',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 673,
    name: 'Cathats',
    slug: 'cathats',
    type: 'smiley',
    description: { short: 'Yes, cat hats', long: 'Unleash the power of cattitude, embrace the meowgic, and add feline charm to your smilies.' },
    prices: { fairtrade_min: 500, fairtrade_max: 600, store: '333' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 672,
    name: 'Pstyle',
    slug: 'pstyle',
    type: 'function',
    description: { short: 'Profile Styles', long: 'Customize your profile with gradient colors, add image and color effects.' },
    prices: { fairtrade_min: 1200, fairtrade_max: 1300, store: '1500' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 670,
    name: 'Supergirl',
    slug: 'supergirl',
    type: 'smiley',
    description: { short: 'Super girl power', long: 'Supergirl smilies. (sgback) back smiley included.' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '340' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 669,
    name: 'Springeaster',
    slug: 'springeaster',
    type: 'smiley',
    description: { short: 'Enjoy your easter', long: 'More spring and easter smilies.' },
    prices: { fairtrade_min: 320, fairtrade_max: 350, store: '299' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 668,
    name: 'Superfarm',
    slug: 'superfarm',
    type: 'supers',
    description: { short: 'Farm Collection', long: 'Farm themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 667,
    name: 'Lights',
    slug: 'lights',
    type: 'smiley',
    description: { short: 'April Fools\' Day 2023', long: 'Turn on the lights with renewable energy. This power marks the April Fools Event of 2023, when chats ran out of energy for a day. #Memories.' },
    prices: { fairtrade_min: 100, fairtrade_max: 150, store: '42' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 666,
    name: '666',
    slug: '666',
    type: 'smiley',
    description: { short: 'Three six smilies', long: 'Beware. Smilies are becoming the devil. SUPER' },
    prices: { fairtrade_min: 13000, fairtrade_max: 14000, store: '666' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 665,
    name: 'Earthhour',
    slug: 'earthhour',
    type: 'smiley',
    description: { short: 'Earth hour smilies', long: 'Turn off the lights and raise awareness with these smilies.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 664,
    name: 'Women',
    slug: 'women',
    type: 'smiley',
    description: { short: 'Women\'s day power', long: 'Every day is women\'s day.' },
    prices: { fairtrade_min: 250, fairtrade_max: 350, store: '199' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 663,
    name: 'Superzoo',
    slug: 'superzoo',
    type: 'supers',
    description: { short: 'Zoo Collection', long: 'Zoo themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 662,
    name: 'Extralove',
    slug: 'extralove',
    type: 'smiley',
    description: { short: 'Extra love', long: 'We can only learn to love by loving.' },
    prices: { fairtrade_min: 250, fairtrade_max: 300, store: '199' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 661,
    name: 'Blackmon',
    slug: 'blackmon',
    type: 'smiley',
    description: { short: 'Blackmon smilies', long: 'The monsters are out there. Bonus back smilies bmshock and bmback.' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 660,
    name: 'Superbf',
    slug: 'superbf',
    type: 'supers',
    description: { short: 'Bf Collection', long: 'Bf themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 41,
  },
  {
    id: 659,
    name: 'Yearnew',
    slug: 'yearnew',
    type: 'smiley',
    description: { short: 'Celebrate the new', long: 'These smilies want to join the new year parties. Can you take them?.' },
    prices: { fairtrade_min: 250, fairtrade_max: 280, store: '223' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 658,
    name: 'Christmasneon',
    slug: 'christmasneon',
    type: 'smiley',
    description: { short: 'Christmas neon', long: 'Love the giver more than the gift. Celebrate christmas with these neon smilies.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '299' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 657,
    name: 'Worldcup2',
    slug: 'worldcup2',
    type: 'smiley',
    description: { short: 'Gooooaaal!', long: 'Turn your smilies into playmakers as these smilies have potential to win. Good dribbling. (wogoldback) back smiley included.' },
    prices: { fairtrade_min: 1300, fairtrade_max: 1700, store: '211' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 656,
    name: 'Fridaysales',
    slug: 'fridaysales',
    type: 'superbf',
    description: { short: 'Friday shopping', long: 'It\'s time to buy. Or is it time to sell? More black friday smilies.' },
    prices: { fairtrade_min: 600, fairtrade_max: 800, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 655,
    name: 'Thanksgiving2',
    slug: 'thanksgiving2',
    type: 'smiley',
    description: { short: 'Thanksgiving2 smilies', long: 'There\'s always something to be thankful for.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '235' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 654,
    name: 'Ktoast',
    slug: 'ktoast',
    type: 'superkao',
    description: { short: 'Kaoani toast', long: 'Delicious smiling slices of bread ready to be combined to other powers.' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '367' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 653,
    name: 'Cemetery',
    slug: 'cemetery',
    type: 'superhalloween',
    description: { short: 'RIP smilies', long: 'Will these thrilling smilies rest in peace?' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '239' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 652,
    name: 'Gkgirl',
    slug: 'gkgirl',
    type: 'superkao',
    description: { short: 'GIRL POWER!', long: 'These cute kaoani smilies are proof that girls rule the world. GROUP POWER!' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 651,
    name: 'Sands',
    slug: 'sands',
    type: 'smiley',
    description: { short: 'Smilies in the sand', long: 'Beach time for these smilies. Go and relax. You deserve it.' },
    prices: { fairtrade_min: 900, fairtrade_max: 1200, store: '211' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 650,
    name: 'Floats',
    slug: 'floats',
    type: 'smiley',
    description: { short: 'Rubber ring smilies', long: 'Float your smilies with these rubber rings.' },
    prices: { fairtrade_min: 250, fairtrade_max: 300, store: '220' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 649,
    name: 'Glasses2',
    slug: 'glasses2',
    type: 'smiley',
    description: { short: 'Glasses series II', long: 'More exclusive glasses to your combinations.' },
    prices: { fairtrade_min: 2000, fairtrade_max: 2500, store: '234' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 648,
    name: 'Silentg',
    slug: 'silentg',
    type: 'function',
    description: { short: 'Silent guest', long: 'Make a guest/un-gag without the message popping up in the chat. Owners only unless changed by gcontrol.' },
    prices: { fairtrade_min: 240, fairtrade_max: 260, store: '20' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 647,
    name: 'Superbunny',
    slug: 'superbunny',
    type: 'supers',
    description: { short: 'Bunny Collection', long: 'Bunny themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 646,
    name: 'Loveearth',
    slug: 'loveearth',
    type: 'smiley',
    description: { short: 'Earth needs love', long: 'Seven Earth smiles full of love for nature.' },
    prices: { fairtrade_min: 400, fairtrade_max: 450, store: '350' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 645,
    name: 'Littlebunny',
    slug: 'littlebunny',
    type: 'superbunny',
    description: { short: 'Little bunnies', long: 'It\'s Easter time. Combine the little bunnies with your smilies.' },
    prices: { fairtrade_min: 3500, fairtrade_max: 3700, store: '333' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 42,
  },
  {
    id: 644,
    name: 'Smokefx',
    slug: 'smokefx',
    type: 'superfx',
    description: { short: 'Smoke effects', long: 'Add smoke effects to your smilies.' },
    prices: { fairtrade_min: 600, fairtrade_max: 800, store: '311' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 643,
    name: 'Springsmile',
    slug: 'springsmile',
    type: 'smiley',
    description: { short: 'Springy smiles', long: 'Springy smilies for spring time.' },
    prices: { fairtrade_min: 150, fairtrade_max: 190, store: '249' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 642,
    name: 'Superfx',
    slug: 'superfx',
    type: 'supers',
    description: { short: 'Fx Collection', long: 'Fx themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 641,
    name: 'Showlove',
    slug: 'showlove',
    type: 'smiley',
    description: { short: 'Show love smilies', long: 'Show your love.' },
    prices: { fairtrade_min: 270, fairtrade_max: 290, store: '243' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 638,
    name: 'Newyearfx',
    slug: 'newyearfx',
    type: 'fx',
    description: { short: 'New year FX', long: 'New year smiley effects.' },
    prices: { fairtrade_min: 600, fairtrade_max: 700, store: '199' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 637,
    name: 'Christmastide',
    slug: 'christmastide',
    type: 'smiley',
    description: { short: 'Christmas smilies', long: 'Great smilies for Christmas.' },
    prices: { fairtrade_min: 200, fairtrade_max: 230, store: '245' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 636,
    name: 'Gstyle',
    slug: 'gstyle',
    type: 'function',
    description: { short: 'Group style', long: 'Add cool styles to your group. Multiple powers may be needed.' },
    prices: { fairtrade_min: 50, fairtrade_max: 60, store: '65' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 635,
    name: 'Fright',
    slug: 'fright',
    type: 'superhalloween',
    description: { short: 'Frightening smilies', long: 'Frighten your friends with these frightening smilies.' },
    prices: { fairtrade_min: 250, fairtrade_max: 300, store: '199' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 634,
    name: 'Whitelist',
    slug: 'whitelist',
    type: 'function',
    description: { short: 'Guest whitelist', long: 'Allow guests to post links you allow.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '199' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 633,
    name: 'Viki',
    slug: 'viki',
    type: 'superscary',
    description: { short: 'Vampire stickers', long: 'Customizable Viki the Vampire stickers.' },
    prices: { fairtrade_min: 200, fairtrade_max: 270, store: '299' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 632,
    name: 'Graffiti',
    slug: 'graffiti',
    type: 'smiley',
    description: { short: 'Graffiti smilies', long: 'Graffiti smilies and effects.' },
    prices: { fairtrade_min: 230, fairtrade_max: 250, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 631,
    name: 'Circlefx',
    slug: 'circlefx',
    type: 'superfx',
    description: { short: 'Circular Effects', long: 'Add circular effects to smilies.' },
    prices: { fairtrade_min: 250, fairtrade_max: 300, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 630,
    name: 'Bantimes',
    slug: 'bantimes',
    type: 'function',
    description: { short: 'Ban time rules', long: 'Set predefined times and rules for bans for your moderators.' },
    prices: { fairtrade_min: 350, fairtrade_max: 380, store: '400' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 43,
  },
  {
    id: 629,
    name: 'Claude',
    slug: 'claude',
    type: 'stickers',
    description: { short: 'Crab stickers', long: 'Customizable Claude the crab stickers.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1000, store: '249' },
    status: 'limited',
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 628,
    name: 'Flipban',
    slug: 'flipban',
    type: 'function',
    description: { short: 'Flip ban', long: 'Flip text of messages upside down when banned.' },
    prices: { fairtrade_min: 200, fairtrade_max: 230, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 627,
    name: 'Worms',
    slug: 'worms',
    type: 'smiley',
    description: { short: 'Worms smilies', long: 'Can of worms and more.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '212' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 626,
    name: 'Jellybean',
    slug: 'jellybean',
    type: 'smiley',
    description: { short: 'Jellybean smilies', long: 'All sorts of jellbean smilies.' },
    prices: { fairtrade_min: 180, fairtrade_max: 230, store: '202' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 625,
    name: 'Lovespring',
    slug: 'lovespring',
    type: 'smiley',
    description: { short: 'Spring love smilies', long: 'Love these spring smilies.' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '232' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 624,
    name: 'Chicky',
    slug: 'chicky',
    type: 'stickers',
    description: { short: 'Chick stickers', long: 'Customizable chick stickers.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 623,
    name: 'Statusfx',
    slug: 'statusfx',
    type: 'epic',
    description: { short: 'Status Effects', long: 'Add cool effects to your status. Multiple powers may be needed.' },
    prices: { fairtrade_min: 4500, fairtrade_max: 4700, store: '5000' },
    status: false,
    released: '2023-10-26',
    category: 'epic',
        categories: ['is_epic'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 622,
    name: 'Bd',
    slug: 'bd',
    type: 'stickers',
    description: { short: 'Birthday stickers', long: 'Happy birthday to you, Happy birthday to you.' },
    prices: { fairtrade_min: 250, fairtrade_max: 350, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 621,
    name: 'Lavafx',
    slug: 'lavafx',
    type: 'superfx',
    description: { short: 'Lava effects', long: 'Lava smilies erupting here.' },
    prices: { fairtrade_min: 180, fairtrade_max: 280, store: '219' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 620,
    name: 'Forever',
    slug: 'forever',
    type: 'smiley',
    description: { short: 'Forever smilies', long: 'Forever in your heart.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '229' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 619,
    name: 'Different',
    slug: 'different',
    type: 'smiley',
    description: { short: 'Different smilies', long: 'A set of unusual back smilies.' },
    prices: { fairtrade_min: 500, fairtrade_max: 700, store: '199' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 618,
    name: 'Acid',
    slug: 'acid',
    type: 'smiley',
    description: { short: 'Acid smilies', long: 'CAUTION! This power may be radioactive. Acid back included.' },
    prices: { fairtrade_min: 250, fairtrade_max: 300, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 617,
    name: 'Subzero',
    slug: 'subzero',
    type: 'smiley',
    description: { short: 'Chilly smilies', long: 'Really really cold smilies.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '99' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 616,
    name: 'Magnus',
    slug: 'magnus',
    type: 'stickers',
    description: { short: 'Viking stickers', long: 'Customizable viking stickers.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '202' },
    status: false,
    released: '2023-10-26',
    category: 'stickers',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 615,
    name: 'Natal',
    slug: 'natal',
    type: 'smiley',
    description: { short: 'Santa is here', long: 'Santa claus smilies and bonuses. It is gifts time!' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '212' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 44,
  },
  {
    id: 614,
    name: 'Knack',
    slug: 'knack',
    type: 'stickers',
    description: { short: 'Reindeer stickers', long: 'Customizable reindeer stickers. Knack is the reindeer that got cut from the team when you know who took his place.' },
    prices: { fairtrade_min: 100, fairtrade_max: 120, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 613,
    name: 'Trader',
    slug: 'trader',
    type: 'superbf',
    description: { short: 'Trader smilies', long: 'This power is for the real traders.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '100' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 612,
    name: 'Darkfriday',
    slug: 'darkfriday',
    type: 'superbf',
    description: { short: 'Sale smilies', long: 'Pick up a bargain with these Saley Smilies.' },
    prices: { fairtrade_min: 400, fairtrade_max: 600, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 611,
    name: 'Scaryxmas',
    slug: 'scaryxmas',
    type: 'superscary',
    description: { short: 'Scary Christmas', long: 'The nightmare before Christmas.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '211' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 610,
    name: 'Miedo',
    slug: 'miedo',
    type: 'superhalloween',
    description: { short: 'Miedo smilies', long: 'Dont be afraid! Halloween themed smilies. SUPER' },
    prices: { fairtrade_min: 3000, fairtrade_max: 4000, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 609,
    name: 'Haunt',
    slug: 'haunt',
    type: 'superhalloween',
    description: { short: 'Haunting smilies', long: 'Haunting Halloween smiles.' },
    prices: { fairtrade_min: 100, fairtrade_max: 150, store: '212' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 608,
    name: 'Care',
    slug: 'care',
    type: 'smiley',
    description: { short: 'Caring smilies', long: 'Smilies to care for you and your loved ones.' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 606,
    name: 'Kavocado',
    slug: 'kavocado',
    type: 'superkao',
    description: { short: 'Kaoani avocados', long: 'Try not to eat these cute avocado smilies! Back and yellow included.' },
    prices: { fairtrade_min: 120, fairtrade_max: 140, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 605,
    name: 'Rabb',
    slug: 'rabb',
    type: 'superbunny',
    description: { short: 'Rabbit stickers', long: 'Rabbit on with these stickers.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '210' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 604,
    name: 'Seals',
    slug: 'seals',
    type: 'smiley',
    description: { short: 'Seal smilie', long: 'Hurry! These smilies might slip away!. LIMITED' },
    prices: { fairtrade_min: 2000, fairtrade_max: 3000, store: '220' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 603,
    name: 'Tropical',
    slug: 'tropical',
    type: 'smiley',
    description: { short: 'Tropical smilies', long: 'Relax with these tropical themed smilies' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 602,
    name: 'Gamer',
    slug: 'gamer',
    type: 'smiley',
    description: { short: 'Gamer smilies', long: '3, 2, 1, Game On!' },
    prices: { fairtrade_min: 600, fairtrade_max: 700, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 601,
    name: 'Plasticwaste',
    slug: 'plasticwaste',
    type: 'smiley',
    description: { short: 'Plastic waste', long: 'Plastic waste smiles.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1100, store: '212' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 600,
    name: 'Superkao',
    slug: 'superkao',
    type: 'supers',
    description: { short: 'Kao Collection', long: 'Kao themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 45,
  },
  {
    id: 599,
    name: 'Quackers',
    slug: 'quackers',
    type: 'stickers',
    description: { short: 'Duck stickers', long: 'These duck stickers are quackers.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 598,
    name: 'Main',
    slug: 'main',
    type: 'function',
    description: { short: 'Main powers', long: 'Choose how your main owners manage your group. Group power.' },
    prices: { fairtrade_min: 140, fairtrade_max: 170, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 597,
    name: 'Polar',
    slug: 'polar',
    type: 'smiley',
    description: { short: 'Polar smilies', long: 'Go pole to pole with these smiles.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '219' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 596,
    name: 'Rainbows',
    slug: 'rainbows',
    type: 'smiley',
    description: { short: 'Rainbow smilies', long: 'Brighten up your day with these rainbow smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 120, store: '199' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 595,
    name: 'Jaws',
    slug: 'jaws',
    type: 'smiley',
    description: { short: 'Shark smilies', long: 'Shark smilies. Stay out of the water!' },
    prices: { fairtrade_min: 400, fairtrade_max: 600, store: '239' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 594,
    name: 'Teddi',
    slug: 'teddi',
    type: 'stickers',
    description: { short: 'Bear stickers', long: 'Teddy bear stickers, awww.' },
    prices: { fairtrade_min: 280, fairtrade_max: 330, store: '249' },
    status: 'limited',
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 593,
    name: 'Matryoshka',
    slug: 'matryoshka',
    type: 'smiley',
    description: { short: 'Matryoshka smilies', long: 'Russian doll smilies.' },
    prices: { fairtrade_min: 90, fairtrade_max: 110, store: '249' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 592,
    name: 'Medical',
    slug: 'medical',
    type: 'smiley',
    description: { short: 'Medical smilies', long: 'Take care with these medical smilies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '199' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 591,
    name: 'Organicwaste',
    slug: 'organicwaste',
    type: 'smiley',
    description: { short: 'Organic waste', long: 'Organic waste and bonus yellow.' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '219' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 590,
    name: 'Linefx',
    slug: 'linefx',
    type: 'fx',
    description: { short: 'Line effects', long: 'Various line based FX smilies. LIMITED' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '239' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 588,
    name: 'Superegg',
    slug: 'superegg',
    type: 'supers',
    description: { short: 'Egg Collection', long: 'Egg themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 587,
    name: 'Yellegg',
    slug: 'yellegg',
    type: 'superegg',
    description: { short: 'Eggs and yellows', long: 'It\'s some new yellows and some eggs.' },
    prices: { fairtrade_min: 180, fairtrade_max: 200, store: '229' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 585,
    name: 'Newpatrick',
    slug: 'newpatrick',
    type: 'smiley',
    description: { short: 'Patrick Smilies', long: 'St Patrick Smilies.' },
    prices: { fairtrade_min: 500, fairtrade_max: 600, store: '350' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 46,
  },
  {
    id: 584,
    name: 'Superlove',
    slug: 'superlove',
    type: 'supers',
    description: { short: 'Love Collection', long: 'Love themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 583,
    name: 'Lovepotion',
    slug: 'lovepotion',
    type: 'superlove',
    description: { short: 'Love potions', long: 'Love potion smilies.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 582,
    name: 'Ali',
    slug: 'ali',
    type: 'stickers',
    description: { short: 'Alien stickers', long: 'Customizable Alien stickers.' },
    prices: { fairtrade_min: 550, fairtrade_max: 700, store: '242' },
    status: false,
    released: '2023-10-26',
    category: 'stickers',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 581,
    name: 'Luvcat',
    slug: 'luvcat',
    type: 'stickers',
    description: { short: 'Cat love stickers', long: 'Customizable lovely cat stickers.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1000, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 580,
    name: 'Scamp',
    slug: 'scamp',
    type: 'superzoo',
    description: { short: 'Monkey smilies', long: 'Don’t monkey around, get it before it’s gone.' },
    prices: { fairtrade_min: 1200, fairtrade_max: 1300, store: '199' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 579,
    name: 'Puzzle',
    slug: 'puzzle',
    type: 'smiley',
    description: { short: 'Puzzle smilies', long: 'Smilies to puzzle over.' },
    prices: { fairtrade_min: 500, fairtrade_max: 600, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 578,
    name: 'Narwhal',
    slug: 'narwhal',
    type: 'stickers',
    description: { short: 'Stickers for narwhals', long: 'Customizable narwhal stickers.' },
    prices: { fairtrade_min: 350, fairtrade_max: 450, store: '232' },
    status: 'limited',
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 577,
    name: 'Xmastime',
    slug: 'xmastime',
    type: 'superxmas',
    description: { short: 'Christmas smilies', long: 'Christmas time smilies and fun. LIMITED .' },
    prices: { fairtrade_min: 180, fairtrade_max: 220, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 576,
    name: 'Revel',
    slug: 'revel',
    type: 'stickers',
    description: { short: 'Stickers for celebrations', long: 'Customizable celebration stickers.' },
    prices: { fairtrade_min: 250, fairtrade_max: 350, store: '232' },
    status: 'limited',
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 574,
    name: 'Supersanta',
    slug: 'supersanta',
    type: 'supers',
    description: { short: 'Santa Collection', long: 'Santa themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 573,
    name: 'Neva',
    slug: 'neva',
    type: 'supersanta',
    description: { short: 'Snowman stickers', long: 'Customizable snowman stickers. LIMITED .' },
    prices: { fairtrade_min: 600, fairtrade_max: 700, store: '249' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 572,
    name: 'Blacksale',
    slug: 'blacksale',
    type: 'superbf',
    description: { short: 'blacksale smilies', long: 'Black Friday sale smiles. LIMITED .' },
    prices: { fairtrade_min: 400, fairtrade_max: 600, store: '100' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 571,
    name: 'Kris',
    slug: 'kris',
    type: 'supersanta',
    description: { short: 'Kristmas stickers', long: 'Customizable Kristmas santa stickers. LIMITED' },
    prices: { fairtrade_min: 600, fairtrade_max: 700, store: '210' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 570,
    name: 'King',
    slug: 'king',
    type: 'superzoo',
    description: { short: 'Lion stickers', long: 'Customizable lion stickers. LIMITED' },
    prices: { fairtrade_min: 600, fairtrade_max: 800, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 47,
  },
  {
    id: 569,
    name: 'Energyfx',
    slug: 'energyfx',
    type: 'superfx',
    description: { short: 'Energy effects', long: 'Energy effects to add some spark to your name.' },
    prices: { fairtrade_min: 180, fairtrade_max: 200, store: '212' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 568,
    name: 'Woodland',
    slug: 'woodland',
    type: 'smiley',
    description: { short: 'Woodland smilies', long: 'Go down to the woods today.' },
    prices: { fairtrade_min: 100, fairtrade_max: 200, store: '202' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 567,
    name: 'Spookies',
    slug: 'spookies',
    type: 'superscary',
    description: { short: 'Spooky smilies', long: 'Spooktacular ghost smilies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '232' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 566,
    name: 'Superscary',
    slug: 'superscary',
    type: 'supers',
    description: { short: 'Scary Collection', long: 'Scary themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 565,
    name: 'Fear',
    slug: 'fear',
    type: 'superscary',
    description: { short: 'Fearful smilies', long: 'Are you afraid of the dark? These smilies are not.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '232' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 564,
    name: 'Nin',
    slug: 'nin',
    type: 'stickers',
    description: { short: 'Ninja stickers', long: 'Customizable ninja stickers.' },
    prices: { fairtrade_min: 550, fairtrade_max: 650, store: '232' },
    status: 'limited',
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 563,
    name: 'Workout',
    slug: 'workout',
    type: 'smiley',
    description: { short: 'Working out smilies', long: 'Workout your similes.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 562,
    name: 'Hairb',
    slug: 'hairb',
    type: 'yellow',
    description: { short: 'More hair', long: 'More hair styles for your smilies.' },
    prices: { fairtrade_min: 600, fairtrade_max: 800, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 561,
    name: 'Mythy',
    slug: 'mythy',
    type: 'stickers',
    description: { short: 'Unicorn stickers', long: 'This sticker is unicorns and rainbows.' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '249' },
    status: 'limited',
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 560,
    name: 'Umbrella',
    slug: 'umbrella',
    type: 'yellow',
    description: { short: 'Umbrella smilies', long: '"sometimes you just need an umbrella.' },
    prices: { fairtrade_min: 200, fairtrade_max: 230, store: '239' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 559,
    name: 'Dinosaur',
    slug: 'dinosaur',
    type: 'superhistory',
    description: { short: 'Dinosaur smilies', long: '"Fierce dinosaur smilies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '229' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 558,
    name: 'Couple',
    slug: 'couple',
    type: 'stickers',
    description: { short: 'Love stickers', long: 'Express your love with stickers.' },
    prices: { fairtrade_min: 190, fairtrade_max: 210, store: '235' },
    status: false,
    released: '2023-10-26',
    category: 'stickers',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 557,
    name: 'Acting',
    slug: 'acting',
    type: 'superhobby',
    description: { short: 'Acting smilies', long: 'Acting smilies and part of hobbies collection.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1000, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 556,
    name: 'Max',
    slug: 'max',
    type: 'stickers',
    description: { short: 'Fat cat stickers', long: 'Customizable fat cat stickers.' },
    prices: { fairtrade_min: 750, fairtrade_max: 950, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 555,
    name: 'Creature',
    slug: 'creature',
    type: 'smiley',
    description: { short: 'Cute or creepy', long: 'Cute or creepy creature.' },
    prices: { fairtrade_min: 180, fairtrade_max: 220, store: '229' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 48,
  },
  {
    id: 554,
    name: 'Baby',
    slug: 'baby',
    type: 'smiley',
    description: { short: 'Baby smilies', long: 'Awww baby smilies.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '229' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 553,
    name: 'Nat',
    slug: 'nat',
    type: 'stickers',
    description: { short: 'Anime girl stickers', long: 'Customisable anime girl stickers.' },
    prices: { fairtrade_min: 180, fairtrade_max: 220, store: '239' },
    status: false,
    released: '2023-10-26',
    category: 'stickers',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 552,
    name: 'Heat',
    slug: 'heat',
    type: 'smiley',
    description: { short: 'Hot smiles', long: 'Smilies to get hot and bothered over.' },
    prices: { fairtrade_min: 180, fairtrade_max: 220, store: '219' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 551,
    name: 'Ahair',
    slug: 'ahair',
    type: 'yellow',
    description: { short: 'Anime hairstyles', long: 'More anime hairstyles.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '209' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 550,
    name: 'Swimming',
    slug: 'swimming',
    type: 'superhobby',
    description: { short: 'Swimming smilies', long: 'Swimming-themed smilies.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '219' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 549,
    name: 'Balloony',
    slug: 'balloony',
    type: 'smiley',
    description: { short: 'Balloony smilies', long: 'Don\'t be loony, get balloony.' },
    prices: { fairtrade_min: 200, fairtrade_max: 230, store: '229' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 548,
    name: 'Fido',
    slug: 'fido',
    type: 'stickers',
    description: { short: 'Fido dog stickers', long: 'Customizable dog stickers.' },
    prices: { fairtrade_min: 180, fairtrade_max: 220, store: '249' },
    status: false,
    released: '2023-10-26',
    category: 'stickers',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 547,
    name: 'Tshirt',
    slug: 'tshirt',
    type: 'superhobby',
    description: { short: 'Football T-shirts', long: 'Design football T-shirts.' },
    prices: { fairtrade_min: 170, fairtrade_max: 200, store: '229' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 546,
    name: 'Catnmouse',
    slug: 'catnmouse',
    type: 'animal',
    description: { short: 'Cat and Mouse smilies', long: 'Cat and mouse: why can\'t they just get along!' },
    prices: { fairtrade_min: 300, fairtrade_max: 500, store: '239' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 545,
    name: 'Category',
    slug: 'category',
    type: 'function',
    description: { short: 'Friend categories', long: 'Divide your friends list into categories.' },
    prices: { fairtrade_min: 90, fairtrade_max: 120, store: '150' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 544,
    name: 'Easterfx',
    slug: 'easterfx',
    type: 'superegg',
    description: { short: 'Easter effects', long: 'Effects smilies for Easter.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '229' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 542,
    name: 'Kbacks',
    slug: 'kbacks',
    type: 'superkao',
    description: { short: 'Kao backs', long: 'Textured kao backs.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '229' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 541,
    name: 'Superhobby',
    slug: 'superhobby',
    type: 'supers',
    description: { short: 'Hobby Collection', long: 'Hobby themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 540,
    name: 'Mime',
    slug: 'mime',
    type: 'superhobby',
    description: { short: 'Miming smilies', long: 'These smilies are silent! Mime artist smilies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '239' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 49,
  },
  {
    id: 539,
    name: 'Meerkat',
    slug: 'meerkat',
    type: 'superzoo',
    description: { short: 'Meerkat smilies', long: 'Meerly carnivores.' },
    prices: { fairtrade_min: 550, fairtrade_max: 650, store: '219' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 538,
    name: 'Time',
    slug: 'time',
    type: 'smiley',
    description: { short: 'Time smilies', long: 'Time dependent smilies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '229' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 537,
    name: 'Skunk',
    slug: 'skunk',
    type: 'superzoo',
    description: { short: 'Skunk smilies', long: 'This power stinks.' },
    prices: { fairtrade_min: 600, fairtrade_max: 800, store: '219' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 536,
    name: 'Fashion',
    slug: 'fashion',
    type: 'superhobby',
    description: { short: 'Fashionable smilies', long: 'Fashion themed smilies and the first of the Hobbies collection.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '225' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 535,
    name: 'Morph',
    slug: 'morph',
    type: 'smiley',
    description: { short: 'Morphing backs', long: 'Smiley back shapes that change from one to another, with options for different effects.' },
    prices: { fairtrade_min: 180, fairtrade_max: 220, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 534,
    name: 'Trains',
    slug: 'trains',
    type: 'smiley',
    description: { short: 'Train smilies', long: 'All aboard with the new trains power.' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 533,
    name: 'Superheart',
    slug: 'superheart',
    type: 'supers',
    description: { short: 'Heart Collection', long: 'Heart themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 532,
    name: 'Valentinefx',
    slug: 'valentinefx',
    type: 'superheart',
    description: { short: 'Valentine effects', long: 'These effects might be love at first sight!' },
    prices: { fairtrade_min: 180, fairtrade_max: 210, store: '222' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 531,
    name: 'Tech',
    slug: 'tech',
    type: 'superhistory',
    description: { short: 'Technology smilies', long: 'Go high tech with these technology themed smilies.' },
    prices: { fairtrade_min: 700, fairtrade_max: 1000, store: '239' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 530,
    name: 'Marshie',
    slug: 'marshie',
    type: 'smiley',
    description: { short: 'Marshmallow smilies', long: 'These smilies are mellow!' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 529,
    name: 'Khair',
    slug: 'khair',
    type: 'superkao',
    description: { short: 'Kaoani hair', long: 'Girl kaoani hairstyles.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '229' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 528,
    name: 'Superboy',
    slug: 'superboy',
    type: 'smiley',
    description: { short: 'Boy power!', long: 'Superboy smilies and a back.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '239' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 527,
    name: 'Kactus',
    slug: 'kactus',
    type: 'superkao',
    description: { short: 'Kactus smilies', long: 'Can\'t touch these kaoani style kacti!' },
    prices: { fairtrade_min: 200, fairtrade_max: 300, store: '219' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 526,
    name: 'Superxmas',
    slug: 'superxmas',
    type: 'supers',
    description: { short: 'Xmas Collection', long: 'Xmas themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 525,
    name: 'Snowflake',
    slug: 'snowflake',
    type: 'santa',
    description: { short: 'Snowflake smilies', long: 'All sorts of snowflakes for the season.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '239' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 50,
  },
  {
    id: 524,
    name: 'Wigs',
    slug: 'wigs',
    type: 'yellow',
    description: { short: 'Wigs for smilies', long: 'Get stylish with these fancy wigs!' },
    prices: { fairtrade_min: 500, fairtrade_max: 600, store: '239' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 523,
    name: 'Swordfx',
    slug: 'swordfx',
    type: 'yellow',
    description: { short: 'Sword effects and smilies', long: 'Sword themed smilies and FX for the ultimate battles!' },
    prices: { fairtrade_min: 750, fairtrade_max: 950, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 522,
    name: 'Appbot',
    slug: 'appbot',
    type: 'function',
    description: { short: 'Make xat apps', long: 'Make and run games and applications for the chat.' },
    prices: { fairtrade_min: 700, fairtrade_max: 800, store: '1000' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 521,
    name: 'Piggy',
    slug: 'piggy',
    type: 'superfarm',
    description: { short: 'Pig smilies', long: 'Get muddy with these piggy smilies.' },
    prices: { fairtrade_min: 190, fairtrade_max: 240, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 520,
    name: 'Kcar',
    slug: 'kcar',
    type: 'superkao',
    description: { short: 'Car smilies', long: 'Vroom vroom. Kaoani style cars.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1000, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 519,
    name: 'Superhalloween',
    slug: 'superhalloween',
    type: 'supers',
    description: { short: 'Halloween Collection', long: 'Halloween themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 518,
    name: 'Muertos',
    slug: 'muertos',
    type: 'superhalloween',
    description: { short: 'El dia de los muertos', long: 'Smilies for the day of the dead.' },
    prices: { fairtrade_min: 170, fairtrade_max: 210, store: '222' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 517,
    name: 'Glasses',
    slug: 'glasses',
    type: 'yellow',
    description: { short: 'Smilies with glasses', long: 'Smilies with all sorts of fun glasses.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '212' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 516,
    name: 'Gorilla',
    slug: 'gorilla',
    type: 'superzoo',
    description: { short: 'Gorilla smilies', long: 'Gorillas not to be missed.' },
    prices: { fairtrade_min: 3000, fairtrade_max: 3500, store: '999' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 515,
    name: 'Gesture',
    slug: 'gesture',
    type: 'smiley',
    description: { short: 'Gestures', long: 'Emphasize an idea, sentiment, or attitude with these gestures. Use #wb to unlock a yellow smilie with the same gesture.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 514,
    name: 'Faceoff',
    slug: 'faceoff',
    type: 'function',
    description: { short: 'Face replace', long: 'Replace faces on many smilies to make many new combinations.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 513,
    name: 'Eyeeye',
    slug: 'eyeeye',
    type: 'smiley',
    description: { short: 'Eye smilies', long: 'Eyes only smiley parts.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '222' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 512,
    name: 'Suspense',
    slug: 'suspense',
    type: 'yellow',
    description: { short: 'Suspense smilies', long: 'Create suspense by making them wait to see your true smiley.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '249' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 510,
    name: 'Opacity',
    slug: 'opacity',
    type: 'smiley',
    description: { short: 'Transparent smilies', long: 'Change the opacity of your smilies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 51,
  },
  {
    id: 509,
    name: 'Money',
    slug: 'money',
    type: 'superbf',
    description: { short: 'Money smilies', long: 'Money smilies are priceless!' },
    prices: { fairtrade_min: 900, fairtrade_max: 1200, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 508,
    name: 'Slimefx',
    slug: 'slimefx',
    type: 'superfx',
    description: { short: 'Slimey FX smilies', long: 'Be sure these effects don\'t slime away!' },
    prices: { fairtrade_min: 2000, fairtrade_max: 2500, store: '999' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 507,
    name: 'Ktree',
    slug: 'ktree',
    type: 'superkao',
    description: { short: 'Kaoani tree smilies', long: 'Kaoani trees, all backs.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 506,
    name: 'Pcplus',
    slug: 'pcplus',
    type: 'function',
    description: { short: 'Block toon PCs', long: 'Allow registered users and subscribers to override nopc.' },
    prices: { fairtrade_min: 70, fairtrade_max: 90, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 505,
    name: 'Supersummer',
    slug: 'supersummer',
    type: 'supers',
    description: { short: 'Summer Collection', long: 'Summer themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 504,
    name: 'Raccoons',
    slug: 'raccoons',
    type: 'superzoo',
    description: { short: 'Raccoon smilies', long: 'Endearing, cute or pests: raccoons.' },
    prices: { fairtrade_min: 1300, fairtrade_max: 1500, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 503,
    name: 'Glitch',
    slug: 'glitch',
    type: 'yellow',
    description: { short: 'Glitching smilies', long: 'Glitch, experience a glitch in the matrix.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '229' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 502,
    name: 'Esmile',
    slug: 'esmile',
    type: 'smiley',
    description: { short: 'Random smile', long: 'Post a random smiling smiley from your powers.' },
    prices: { fairtrade_min: 140, fairtrade_max: 180, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 501,
    name: 'Portalfx',
    slug: 'portalfx',
    type: 'superfx',
    description: { short: 'Portal effects', long: 'Portal into another dimension.' },
    prices: { fairtrade_min: 850, fairtrade_max: 1000, store: '249' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 500,
    name: 'Superanime',
    slug: 'superanime',
    type: 'supers',
    description: { short: 'Anime Collection', long: 'Anime themed collection.' },
    prices: { fairtrade_min: 0, fairtrade_max: 0, store: 'unknown' },
    status: false,
    released: '2023-11-19',
    category: 'collection',
        categories: ['is_collection'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 499,
    name: 'Koffee',
    slug: 'koffee',
    type: 'superkao',
    description: { short: 'Kaoani coffee smilies', long: 'Start your day with koffee, kaoani style coffee smilies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 498,
    name: 'Pizzas',
    slug: 'pizzas',
    type: 'yellow',
    description: { short: 'Pizza smilies', long: 'Who doesn\'t love pizza? 6 backs, some yellows and a hug.' },
    prices: { fairtrade_min: 400, fairtrade_max: 600, store: '260' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 497,
    name: 'Hats',
    slug: 'hats',
    type: 'function',
    description: { short: 'More hats', long: 'More hats for smilies. Requires HAT power.' },
    prices: { fairtrade_min: 450, fairtrade_max: 480, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 496,
    name: 'Baghead',
    slug: 'baghead',
    type: 'yellow',
    description: { short: 'Smilies in bags', long: 'Smilies doing fun stuff in paper bags.' },
    prices: { fairtrade_min: 80, fairtrade_max: 120, store: '233' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 495,
    name: 'Guineapig',
    slug: 'guineapig',
    type: 'superzoo',
    description: { short: 'Guinea pig smilies', long: 'Are you guinea buy these cute things?' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '233' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 52,
  },
  {
    id: 494,
    name: 'Botstat',
    slug: 'botstat',
    type: 'function',
    description: { short: 'Bot status writer', long: 'Allow approved bots to update status, avatar and home page.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 493,
    name: 'Kitcup',
    slug: 'kitcup',
    type: 'kao',
    description: { short: 'Kittens in cups', long: 'Mischievous kittens in cups.' },
    prices: { fairtrade_min: 300, fairtrade_max: 500, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 492,
    name: 'Seasons',
    slug: 'seasons',
    type: 'event',
    description: { short: 'Seasons smilies', long: 'Season smilies for Earth day.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '222' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 491,
    name: 'Birdie',
    slug: 'birdie',
    type: 'superfarm',
    description: { short: 'Cute birdie smilies', long: 'Fly away with these smilies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '249' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 490,
    name: 'Bestfriend',
    slug: 'bestfriend',
    type: 'function',
    description: { short: 'Bestfriend smilies', long: 'Bestfriend smilies and extra kisses for BFF.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 489,
    name: 'Hippos',
    slug: 'hippos',
    type: 'superzoo',
    description: { short: 'Hippo smilies', long: 'Fun and foolish Hippos.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1000, store: '269' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 488,
    name: 'Rankscroll',
    slug: 'rankscroll',
    type: 'function',
    description: { short: 'Rank dependent scroll', long: 'Add different scrolls for different ranks.' },
    prices: { fairtrade_min: 140, fairtrade_max: 180, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 487,
    name: 'Patrickfx',
    slug: 'patrickfx',
    type: 'fx',
    description: { short: 'St Patrick\'s day FX', long: 'Effects smilies for St Patrick\'s day.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 486,
    name: 'Cups',
    slug: 'cups',
    type: 'smiley',
    description: { short: 'Cup smilies', long: 'Cups for all occasions and HUG.' },
    prices: { fairtrade_min: 130, fairtrade_max: 170, store: '222' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 485,
    name: 'Accessory',
    slug: 'accessory',
    type: 'yellow',
    description: { short: 'Accessory smilies', long: 'Accessorize with this power.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '239' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 484,
    name: 'Illusionfx',
    slug: 'illusionfx',
    type: 'superfx',
    description: { short: 'Illusion effects', long: 'Illuminate your chat with these FX.' },
    prices: { fairtrade_min: 400, fairtrade_max: 450, store: '249' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 483,
    name: 'Nuts',
    slug: 'nuts',
    type: 'smiley',
    description: { short: 'Nutty smilies', long: 'Go nuts about the googly eyed smilies.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '229' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 482,
    name: 'Heartbreak',
    slug: 'heartbreak',
    type: 'superlove',
    description: { short: 'Heartbroken smilies', long: 'Heartbreak themed smilies and a nolove hug.' },
    prices: { fairtrade_min: 350, fairtrade_max: 450, store: '279' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 481,
    name: 'Samba',
    slug: 'samba',
    type: 'yellow',
    description: { short: 'Samba smilies', long: 'Get in the samba rhythm.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '222' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 480,
    name: 'Dragons',
    slug: 'dragons',
    type: 'animal',
    description: { short: 'Dragon smilies', long: 'A fire-spewing mythical power, dragons!' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '270' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 53,
  },
  {
    id: 478,
    name: 'Superblastkick',
    slug: 'superblastkick',
    type: 'blasts',
    description: { short: 'Super blast kicks', long: 'More blast kick animations. Requires blastkick.' },
    prices: { fairtrade_min: 400, fairtrade_max: 450, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 477,
    name: 'Sqanimal',
    slug: 'sqanimal',
    type: 'superfarm',
    description: { short: 'Cute square animals', long: 'Square shaped assorted animals.' },
    prices: { fairtrade_min: 250, fairtrade_max: 350, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 476,
    name: 'Flaghug',
    slug: 'flaghug',
    type: 'hugs',
    description: { short: 'Hug with flag', long: 'Send a message with your national flag.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 475,
    name: 'Xmasfactory',
    slug: 'xmasfactory',
    type: 'supersanta',
    description: { short: 'Santa\'s factory', long: 'All kinds of goodies for Christmas.' },
    prices: { fairtrade_min: 180, fairtrade_max: 190, store: '222' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 473,
    name: 'Goodfriend',
    slug: 'goodfriend',
    type: 'function',
    description: { short: 'Add good friend', long: 'Add a good friend to the chat. Good friends will appear higher on your user list as well as your friends list.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 472,
    name: 'Superblastban',
    slug: 'superblastban',
    type: 'blasts',
    description: { short: 'Super blast bans', long: 'More blast ban animations. Requires blastban.' },
    prices: { fairtrade_min: 400, fairtrade_max: 450, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 471,
    name: 'Xmasscroll',
    slug: 'xmasscroll',
    type: 'superxmas',
    description: { short: 'Christmas hug scroller', long: 'Send a Christmas hug to friends.' },
    prices: { fairtrade_min: 130, fairtrade_max: 170, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 470,
    name: 'Kxmas',
    slug: 'kxmas',
    type: 'superxmas',
    description: { short: 'Kaoani Christmas smilies', long: 'Over 15 cute Christmas smilies for a kaoani xmas.' },
    prices: { fairtrade_min: 800, fairtrade_max: 900, store: '299' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 469,
    name: 'Fitness',
    slug: 'fitness',
    type: 'yellow',
    description: { short: 'Fit smilies', long: 'Keep your smilies in top shape.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '260' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 468,
    name: 'Pcbr',
    slug: 'pcbr',
    type: 'function',
    description: { short: 'Random pcback', long: 'Set a random private chat background.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 467,
    name: 'Yoga',
    slug: 'yoga',
    type: 'yellow',
    description: { short: 'Calming smilies', long: 'Relax and rejuvenate with these yoga smilies.' },
    prices: { fairtrade_min: 200, fairtrade_max: 300, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 466,
    name: 'Plants',
    slug: 'plants',
    type: 'smiley',
    description: { short: 'Plant smilies', long: 'Plant based smilies, they will grow on you (includes 7 backs).' },
    prices: { fairtrade_min: 250, fairtrade_max: 300, store: '270' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 465,
    name: 'Halloscroll',
    slug: 'halloscroll',
    type: 'superhalloween',
    description: { short: 'Halloween scroller', long: 'New style halloween themed scroller hug, bonus smilies and a glowing back.' },
    prices: { fairtrade_min: 130, fairtrade_max: 160, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 54,
  },
  {
    id: 464,
    name: 'Skull',
    slug: 'skull',
    type: 'superscary',
    description: { short: 'Skull smilies', long: 'Celebrate the day of the dead (Halloween) with these skull smilies.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '260' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 463,
    name: 'Octo',
    slug: 'octo',
    type: 'animal',
    description: { short: 'Octopus smilies', long: 'These smilies will octopi your fun.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1000, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 462,
    name: 'Mark',
    slug: 'mark',
    type: 'function',
    description: { short: 'Mark keywords', long: 'Highlight keywords you specify in the chat.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 461,
    name: 'Owlie',
    slug: 'owlie',
    type: 'superfarm',
    description: { short: 'Owl smilies', long: 'Hoot! Hoooray! Cute owl smilies.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 460,
    name: 'Flgpwn',
    slug: 'flgpwn',
    type: 'function',
    description: { short: 'Flag on pawn', long: 'Add a flag to your pawn.' },
    prices: { fairtrade_min: 80, fairtrade_max: 110, store: '150' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 459,
    name: 'Firefx',
    slug: 'firefx',
    type: 'superfx',
    description: { short: 'Fire smilies', long: 'You might need a fire extinguisher. Fire FX smilies.' },
    prices: { fairtrade_min: 3000, fairtrade_max: 3500, store: '442' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 458,
    name: 'Puppy',
    slug: 'puppy',
    type: 'animal',
    description: { short: 'Puppy smilies', long: 'Need a hand? These puppies will give you a paw.' },
    prices: { fairtrade_min: 180, fairtrade_max: 230, store: '299' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 457,
    name: 'Travel',
    slug: 'travel',
    type: 'yellow',
    description: { short: 'Traveling smilies', long: 'Go places with these smilies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '212' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 456,
    name: 'Kveggie',
    slug: 'kveggie',
    type: 'superkao',
    description: { short: 'Kaoani vegetables', long: 'Kaoani style vegetable backs and a hug.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 455,
    name: 'Silentban',
    slug: 'silentban',
    type: 'function',
    description: { short: 'Silent ban', long: 'Ban/mute a user with no message displayed. Owners only unless changed with gcontrol.' },
    prices: { fairtrade_min: 330, fairtrade_max: 380, store: '30' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 454,
    name: 'Nightmare',
    slug: 'nightmare',
    type: 'superscary',
    description: { short: 'Nightmarish smilies', long: 'Smilies that go bump in the night.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1500, store: '230' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 453,
    name: 'Cuticorn',
    slug: 'cuticorn',
    type: 'smiley',
    description: { short: 'Cute unicorn smilies', long: 'Cute unicorns, one with a rainbow.' },
    prices: { fairtrade_min: 250, fairtrade_max: 300, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 452,
    name: 'Sline',
    slug: 'sline',
    type: 'function',
    description: { short: 'Custom smiley line', long: 'Set a personal custom smiley line.' },
    prices: { fairtrade_min: 120, fairtrade_max: 170, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 451,
    name: 'Kloud',
    slug: 'kloud',
    type: 'superkao',
    description: { short: 'Kaoani clouds', long: 'Klouds for all moods.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '230' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 450,
    name: 'Fiesta',
    slug: 'fiesta',
    type: 'event',
    description: { short: 'Fun fiesta', long: 'Enjoy these fiesta smilies.' },
    prices: { fairtrade_min: 140, fairtrade_max: 160, store: '220' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 55,
  },
  {
    id: 449,
    name: 'Minimon',
    slug: 'minimon',
    type: 'kao',
    description: { short: 'Small furry monsters', long: 'They are small monsters and they are furry too.' },
    prices: { fairtrade_min: 2300, fairtrade_max: 2600, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 448,
    name: 'Broadcaster',
    slug: 'broadcaster',
    type: 'yellow',
    description: { short: 'Broad casting smilies', long: 'Smilies to help you broadcast.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 446,
    name: 'Ghostmon',
    slug: 'ghostmon',
    type: 'superscary',
    description: { short: 'Ghost Monster', long: 'Is it a ghost? Is it a monster? It\'s Ghostmon!' },
    prices: { fairtrade_min: 3000, fairtrade_max: 3500, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 445,
    name: 'Splashfx',
    slug: 'splashfx',
    type: 'supersummer',
    description: { short: 'Splashing smilies', long: 'Cool down this summer with these splash FX.' },
    prices: { fairtrade_min: 1100, fairtrade_max: 1300, store: '220' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 444,
    name: 'Fishbowl',
    slug: 'fishbowl',
    type: 'animal',
    description: { short: 'Smilies in a fishbowl', long: 'Cute pet fish in a fishbowl.' },
    prices: { fairtrade_min: 120, fairtrade_max: 140, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 443,
    name: 'Drawn',
    slug: 'drawn',
    type: 'smiley',
    description: { short: 'Hand drawn smilies', long: 'Hand drawn style animated smilies and back.' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 442,
    name: 'Neon',
    slug: 'neon',
    type: 'other',
    description: { short: 'Neon light smilies', long: 'Light up your name with neon.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 441,
    name: 'Rapidreason',
    slug: 'rapidreason',
    type: 'function',
    description: { short: 'Rapid reason', long: 'Add a reason to rapid bans. Do rapid kicks. Requires rapid power.' },
    prices: { fairtrade_min: 130, fairtrade_max: 160, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 440,
    name: 'Eggie',
    slug: 'eggie',
    type: 'superegg',
    description: { short: 'Eggy-chick smilies', long: 'Eggsquisite eggy-chick smilies.' },
    prices: { fairtrade_min: 650, fairtrade_max: 950, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 439,
    name: 'Kawaii',
    slug: 'kawaii',
    type: 'kao',
    description: { short: 'Cute kawaii backs', long: 'Cute japanese style smilie backs and hug.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1300, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 438,
    name: 'Hedgehog',
    slug: 'hedgehog',
    type: 'superzoo',
    description: { short: 'Hedgehog smilies', long: 'Zany hedgehog smilies.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '210' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 437,
    name: 'Earth',
    slug: 'earth',
    type: 'event',
    description: { short: 'Earth day hug and smilies', long: 'Love the earth to orbits!' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '230' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 436,
    name: 'Ribunny',
    slug: 'ribunny',
    type: 'superbunny',
    description: { short: 'Bunny smilies', long: 'Everybunny is hopping for this power.' },
    prices: { fairtrade_min: 1500, fairtrade_max: 1700, store: '399' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 435,
    name: 'Anichick',
    slug: 'anichick',
    type: 'animal',
    description: { short: 'Anime Chicks', long: 'Cute anime style chicks for Easter.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1500, store: '999' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 56,
  },
  {
    id: 434,
    name: 'Foolsday',
    slug: 'foolsday',
    type: 'yellow',
    description: { short: 'More foolishness', long: 'Fool about with these smilies.' },
    prices: { fairtrade_min: 400, fairtrade_max: 450, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 433,
    name: 'Toad',
    slug: 'toad',
    type: 'superfarm',
    description: { short: 'Toad smilies', long: 'Toads with attitude. One breathes fire!' },
    prices: { fairtrade_min: 2800, fairtrade_max: 3200, store: '1000' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 432,
    name: 'Spacefx',
    slug: 'spacefx',
    type: 'superfx',
    description: { short: 'Space effects smilies', long: 'Space-cial smiley effects.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 431,
    name: 'Wish',
    slug: 'wish',
    type: 'hugs',
    description: { short: 'Wish genie and smilies', long: 'See if your wish will be granted by the xat genie! NOTE: Wishes may not actually be granted even if the genie says they are!' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '290' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 430,
    name: 'Ruby',
    slug: 'ruby',
    type: 'epic',
    description: { short: 'Ruby pawn', long: 'Sparkling Ruby pawn and Ruby name.' },
    prices: { fairtrade_min: 98000, fairtrade_max: 99000, store: '100000' },
    status: false,
    released: '2023-10-26',
    category: 'epic',
        categories: ['is_epic'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 429,
    name: 'Mousie',
    slug: 'mousie',
    type: 'animal',
    description: { short: 'Mousie smilies', long: 'Did someone say squeak?' },
    prices: { fairtrade_min: 550, fairtrade_max: 650, store: '260' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 428,
    name: 'Kgiraffe',
    slug: 'kgiraffe',
    type: 'superzoo',
    description: { short: 'Kaoani giraffe smilies', long: 'Giraffes sleep standing up!' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 427,
    name: 'Lovetest',
    slug: 'lovetest',
    type: 'superlove',
    description: { short: 'Love Tester', long: 'Calculate your compatibility with the xat love tester!' },
    prices: { fairtrade_min: 500, fairtrade_max: 600, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 426,
    name: 'Lovefx',
    slug: 'lovefx',
    type: 'superlove',
    description: { short: 'Love effects', long: 'Some lovely effects smilies.' },
    prices: { fairtrade_min: 170, fairtrade_max: 190, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 425,
    name: 'Lovemix2',
    slug: 'lovemix2',
    type: 'superlove',
    description: { short: 'Love mixture 2', long: 'Back by popular demand, a further eclectic mix of love themed xat smilies.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 424,
    name: 'Mountain',
    slug: 'mountain',
    type: 'other',
    description: { short: 'Mountains and ice', long: 'Mountains, avalanches and Ice.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 423,
    name: 'X2d',
    slug: 'x2d',
    type: 'function',
    description: { short: 'xats to days', long: 'Convert some of your xats to days. This power will disappear after usage. Convert now!' },
    prices: { fairtrade_min: 50, fairtrade_max: 80, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 422,
    name: 'Kaoears',
    slug: 'kaoears',
    type: 'superkao',
    description: { short: 'Kaoani ears', long: 'Cute kaoani style smilies with flapping ears.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1100, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 421,
    name: 'Fireworksfx',
    slug: 'fireworksfx',
    type: 'fx',
    description: { short: 'Fireworks FX and hug', long: 'Get ready for new year with these fireworks smilies and hug.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 420,
    name: 'Battle',
    slug: 'battle',
    type: 'superanime',
    description: { short: 'Battle hug and smilies', long: 'Let the BATTLE commence with the battle hug plus 14 smilies including an FX.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 57,
  },
  {
    id: 419,
    name: 'Sparklefx',
    slug: 'sparklefx',
    type: 'superxmas',
    description: { short: 'Sparkle effects', long: 'Add some sparkle to your Christmas.' },
    prices: { fairtrade_min: 700, fairtrade_max: 800, store: '390' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 418,
    name: 'Christmix',
    slug: 'christmix',
    type: 'superxmas',
    description: { short: 'Christmas mix', long: 'An eclectic mix of Christmas themed xat smilies and a hug based \'pairs\' GAME.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '375' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 417,
    name: 'Tropicalxmas',
    slug: 'tropicalxmas',
    type: 'superxmas',
    description: { short: 'Summer Christmas', long: 'Hot Christmas smilies and shark hug.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '230' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 416,
    name: 'Gobble',
    slug: 'gobble',
    type: 'holiday',
    description: { short: 'Thanksgiving smilies', long: 'Nom nom nom, gobble gobble smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 150, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 414,
    name: 'Cadet',
    slug: 'cadet',
    type: 'smiley',
    description: { short: 'Space cadet', long: '10 9 8 7 6 5 4 3 2 1 blast off with cadet power!' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 413,
    name: 'Funfair',
    slug: 'funfair',
    type: 'event',
    description: { short: 'Funfair smilies', long: 'All the fun of the fair and hug.' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '230' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 412,
    name: 'Graveyard',
    slug: 'graveyard',
    type: 'superscary',
    description: { short: 'Graveyard game hug', long: 'Graveyard hug GAME for halloween and bonus smilies.' },
    prices: { fairtrade_min: 900, fairtrade_max: 1200, store: '280' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 411,
    name: 'Boo',
    slug: 'boo',
    type: 'superscary',
    description: { short: 'Scary boo smilies', long: 'Boo smilies and hug for halloween.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1000, store: '240' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 410,
    name: 'Classic',
    slug: 'classic',
    type: 'yellow',
    description: { short: 'Classic smilies', long: 'Roll back time with classic versions of the yellow smilies.' },
    prices: { fairtrade_min: 700, fairtrade_max: 730, store: '750' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 409,
    name: 'Fall',
    slug: 'fall',
    type: 'other',
    description: { short: 'Autumn smilies', long: 'Smilies for the autumn season.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '230' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 408,
    name: 'Meow',
    slug: 'meow',
    type: 'smiley',
    description: { short: 'Cat smilies', long: 'We are pawsitive this is the purrfect pawer fur you.' },
    prices: { fairtrade_min: 100, fairtrade_max: 140, store: '270' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 407,
    name: 'Spacejinx',
    slug: 'spacejinx',
    type: 'jinx',
    description: { short: 'Space jinx', long: 'Remove and randomize spaces in friends messages.' },
    prices: { fairtrade_min: 80, fairtrade_max: 110, store: '270' },
    status: 'limited',
    released: '2023-10-26',
    category: 'jinx',
        categories: ['is_jinx', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 406,
    name: 'Ten',
    slug: 'ten',
    type: 'superhistory',
    description: { short: '10th anniversary', long: 'xat\'s 10th anniversary smilies and hugs.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_jinx', 'is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 405,
    name: 'Elephant',
    slug: 'elephant',
    type: 'superzoo',
    description: { short: 'Elephant smilies', long: 'This power is BIG!' },
    prices: { fairtrade_min: 350, fairtrade_max: 450, store: '220' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 58,
  },
  {
    id: 404,
    name: 'Popcorns',
    slug: 'popcorns',
    type: 'kao',
    description: { short: 'Popcorn smilies', long: 'Popcorn at the movies. Hug also included.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 403,
    name: 'Comics',
    slug: 'comics',
    type: 'other',
    description: { short: 'Comic smilies', long: 'BOOM! ZAP! WOW! Comic smilies/effects.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '240' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 402,
    name: 'Kwolf',
    slug: 'kwolf',
    type: 'superfarm',
    description: { short: 'Kaoani wolves', long: 'Hungry like the wolf.' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 401,
    name: 'Summerhug',
    slug: 'summerhug',
    type: 'supersummer',
    description: { short: 'Summer hugs', long: 'Summer hugs. Rubber RING, volleyBALL and HAMmock.' },
    prices: { fairtrade_min: 1200, fairtrade_max: 1500, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_jinx'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 400,
    name: 'Eggjinx',
    slug: 'eggjinx',
    type: 'jinx',
    description: { short: 'Egg language jinx', long: 'Most people can\'t understand the eggy language, can you?' },
    prices: { fairtrade_min: 600, fairtrade_max: 800, store: '400' },
    status: 'limited',
    released: '2023-10-26',
    category: 'jinx',
        categories: ['is_jinx', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 399,
    name: 'Aprincess',
    slug: 'aprincess',
    type: 'superanime',
    description: { short: 'Anime princess smilies', long: 'Anime princess smilies and hug.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1000, store: '499' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 398,
    name: 'Summerland',
    slug: 'summerland',
    type: 'supersummer',
    description: { short: 'Summer chill', long: 'Fun in the sun smilies.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 397,
    name: 'Nameflag',
    slug: 'nameflag',
    type: 'epic',
    description: { short: 'Flag name animations', long: 'Animated flag effects on your name (requires glow, namecolor).' },
    prices: { fairtrade_min: 9500, fairtrade_max: 10000, store: '12000' },
    status: false,
    released: '2023-10-26',
    category: 'epic',
        categories: ['is_epic'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 396,
    name: 'Worm',
    slug: 'worm',
    type: 'animal',
    description: { short: 'Worm smilies', long: 'Squirmy wormy smilies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '240' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 395,
    name: 'Tooth',
    slug: 'tooth',
    type: 'smiley',
    description: { short: 'Teeth smilies', long: 'Smilies have fillings too!' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1200, store: '230' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 394,
    name: 'Hangjinx',
    slug: 'hangjinx',
    type: 'jinx',
    description: { short: 'Hangman Jinx', long: 'Jinx your friends in a hangman style.' },
    prices: { fairtrade_min: 120, fairtrade_max: 170, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'jinx',
        categories: ['is_jinx'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 393,
    name: 'Diva',
    slug: 'diva',
    type: 'smiley',
    description: { short: 'Diva smilies', long: 'Diva smilies plus car hug.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 392,
    name: 'Animegirl',
    slug: 'animegirl',
    type: 'superanime',
    description: { short: 'Anime girl smilies', long: 'Anime girl power. Two characters each with an FX.' },
    prices: { fairtrade_min: 450, fairtrade_max: 550, store: '299' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 391,
    name: 'Chores',
    slug: 'chores',
    type: 'yellow',
    description: { short: 'Sweep up smilies', long: 'Clean up with these smilies.' },
    prices: { fairtrade_min: 160, fairtrade_max: 200, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 390,
    name: 'Namewave',
    slug: 'namewave',
    type: 'epic',
    description: { short: 'Animated Grad Name', long: 'Animated gradient effects on your name (requires glow, namecolor, namegrad).' },
    prices: { fairtrade_min: 22500, fairtrade_max: 23000, store: '25000' },
    status: false,
    released: '2023-10-26',
    category: 'epic',
        categories: ['is_epic'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 59,
  },
  {
    id: 389,
    name: 'Koala',
    slug: 'koala',
    type: 'superzoo',
    description: { short: 'Koala smilies', long: 'Koalas spend most of their time sleeping or eating.' },
    prices: { fairtrade_min: 500, fairtrade_max: 700, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 388,
    name: 'Mom',
    slug: 'mom',
    type: 'yellow',
    description: { short: 'Mother\'s Day smilies', long: 'MOMumental smilies for Mother\'s Day.' },
    prices: { fairtrade_min: 500, fairtrade_max: 700, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 387,
    name: 'Jumblejinx',
    slug: 'jumblejinx',
    type: 'jinx',
    description: { short: 'Jumbled messages', long: 'Jumble jinx your friends messages.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'jinx',
        categories: ['is_jinx'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 386,
    name: 'Earthday',
    slug: 'earthday',
    type: 'event',
    description: { short: 'Earth Day', long: 'Celebrate earth day with these earth themed smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 385,
    name: 'Onion',
    slug: 'onion',
    type: 'superkao',
    description: { short: 'Onion', long: 'Kaoani style onion smilies, including head bang and fall.' },
    prices: { fairtrade_min: 700, fairtrade_max: 800, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 384,
    name: 'Songkran',
    slug: 'songkran',
    type: 'event',
    description: { short: 'Songkran festival', long: 'Thai new year Songkran smilies and water hug.' },
    prices: { fairtrade_min: 90, fairtrade_max: 110, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 382,
    name: 'Fools',
    slug: 'fools',
    type: 'yellow',
    description: { short: 'April fools smilies', long: 'Don\'t let these smilies fool you!' },
    prices: { fairtrade_min: 600, fairtrade_max: 800, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 381,
    name: 'Easterland',
    slug: 'easterland',
    type: 'superegg',
    description: { short: 'Easter fun', long: 'Easter smilies FX and hug.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 380,
    name: 'Ebunny',
    slug: 'ebunny',
    type: 'superbunny',
    description: { short: 'Easter bunny smilies', long: 'Easter bunny with Easter egg. Includes an Easter hug.' },
    prices: { fairtrade_min: 250, fairtrade_max: 450, store: '450' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 379,
    name: 'Patrick',
    slug: 'patrick',
    type: 'yellow',
    description: { short: 'St Patrick\'s day smilies', long: 'These smilies will shamrock your world! Hug also included.' },
    prices: { fairtrade_min: 800, fairtrade_max: 900, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 378,
    name: 'Namegrad',
    slug: 'namegrad',
    type: 'epic',
    description: { short: 'Rainbow name', long: 'Rainbow and custom gradient effects on your name (requires glow and namecolor).' },
    prices: { fairtrade_min: 9300, fairtrade_max: 9800, store: '10000' },
    status: false,
    released: '2023-10-26',
    category: 'epic',
        categories: ['is_epic'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 377,
    name: 'Caterpillar',
    slug: 'caterpillar',
    type: 'superfarm',
    description: { short: 'Cool caterpillars', long: 'Caterpillar themed smilies with animated bodies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '299' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 376,
    name: 'Mining',
    slug: 'mining',
    type: 'other',
    description: { short: 'Mining smilies', long: 'Go underground and get mining!' },
    prices: { fairtrade_min: 110, fairtrade_max: 150, store: '222' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 375,
    name: 'Lovehug',
    slug: 'lovehug',
    type: 'hugs',
    description: { short: 'Send love hugs', long: 'Send hugs to your loved one.' },
    prices: { fairtrade_min: 250, fairtrade_max: 300, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_jinx'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 60,
  },
  {
    id: 374,
    name: 'Lovemix',
    slug: 'lovemix',
    type: 'superlove',
    description: { short: 'A mixture of love smilies', long: 'An eclectic mix of love themed xat smilies.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 373,
    name: 'Paints',
    slug: 'paints',
    type: 'yellow',
    description: { short: 'Painted smilies', long: 'Make your smilies do art. This power includes 8 smilies and one FX with 13 custom items.' },
    prices: { fairtrade_min: 500, fairtrade_max: 600, store: '240' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 372,
    name: 'Ricebowl',
    slug: 'ricebowl',
    type: 'superkao',
    description: { short: 'Ricebowl smilies', long: 'A kaoani rice bowl may be enough to convince you that rice is good, so rice and shine with this cute power.' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '230' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 371,
    name: 'Electricity',
    slug: 'electricity',
    type: 'other',
    description: { short: 'Electric smilies', long: 'Electrify your smilies.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '220' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 370,
    name: 'Masks',
    slug: 'masks',
    type: 'yellow',
    description: { short: 'Masks for smilies', long: 'Add masks to smilies.' },
    prices: { fairtrade_min: 800, fairtrade_max: 900, store: '240' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 369,
    name: 'Fireworkshug',
    slug: 'fireworkshug',
    type: 'hugs',
    description: { short: 'New year fireworks hug', long: 'Send new year fireworks hugs.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 368,
    name: 'Ornaments',
    slug: 'ornaments',
    type: 'superxmas',
    description: { short: 'Christmas ornaments', long: 'Christmas ornaments to decorate your chat.' },
    prices: { fairtrade_min: 600, fairtrade_max: 1000, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 367,
    name: 'Sleighhug',
    slug: 'sleighhug',
    type: 'supersanta',
    description: { short: 'Sleigh animations', long: 'Send 5 types of sleigh hug animations in main and private chats.' },
    prices: { fairtrade_min: 5000, fairtrade_max: 6000, store: '500' },
    status: 'limited',
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 366,
    name: 'Choirhug',
    slug: 'choirhug',
    type: 'superxmas',
    description: { short: 'Christmas hug animations', long: 'Christmas choir hug animations plus bonus smilies.' },
    prices: { fairtrade_min: 700, fairtrade_max: 750, store: '900' },
    status: false,
    released: '2023-10-26',
    category: 'hug',
        categories: ['is_hug'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 365,
    name: 'Beautifly',
    slug: 'beautifly',
    type: 'superfarm',
    description: { short: 'Butterfly smilies', long: 'Flutter away with these smilies!' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1500, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 364,
    name: 'Tigers',
    slug: 'tigers',
    type: 'superzoo',
    description: { short: 'Cute tiger smilies', long: 'These smilies are GRRRReat!' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '42' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 363,
    name: 'Witch',
    slug: 'witch',
    type: 'superhalloween',
    description: { short: 'A witch\'s brew smilies', long: 'Brew up some smilies.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 362,
    name: 'Allhallows',
    slug: 'allhallows',
    type: 'superhalloween',
    description: { short: 'All Hallows\' Eve smilies', long: 'All Hallows\' Eve smilies and FX.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '400' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 361,
    name: 'Kstar',
    slug: 'kstar',
    type: 'superkao',
    description: { short: 'Kaoani star smilies', long: 'All star powers.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1200, store: '232' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 360,
    name: 'Ranklock',
    slug: 'ranklock',
    type: 'function',
    description: { short: 'Lock a user to a rank', long: 'Allows owners and above to lock a user\'s rank, and prevent it from being changed by others.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 61,
  },
  {
    id: 359,
    name: 'Roosters',
    slug: 'roosters',
    type: 'superfarm',
    description: { short: 'Roosting smilies', long: 'Happy flappy rooster smilies.' },
    prices: { fairtrade_min: 400, fairtrade_max: 600, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 358,
    name: 'Kandle',
    slug: 'kandle',
    type: 'superkao',
    description: { short: 'Kandle smilies', long: 'The kandle that burns twice as bright, burns half as long.' },
    prices: { fairtrade_min: 100, fairtrade_max: 150, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 357,
    name: 'Glob',
    slug: 'glob',
    type: 'smiley',
    description: { short: 'Glob smilies', long: 'Fifteen globules form because the liquid exhibits surface tension.' },
    prices: { fairtrade_min: 250, fairtrade_max: 300, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 356,
    name: 'Precious',
    slug: 'precious',
    type: 'other',
    description: { short: 'Precious smilies', long: 'Time to get precious.' },
    prices: { fairtrade_min: 130, fairtrade_max: 180, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 355,
    name: 'Big',
    slug: 'big',
    type: 'epic',
    description: { short: 'Big smilies', long: 'Send a big smiley in the chat. VERY' },
    prices: { fairtrade_min: 22000, fairtrade_max: 23000, store: '5000' },
    status: 'limited',
    released: '2023-10-26',
    category: 'epic',
        categories: ['is_epic', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 354,
    name: 'Reaper',
    slug: 'reaper',
    type: 'superscary',
    description: { short: 'Reaper smilies', long: 'Don\'t fear the reaper.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 353,
    name: 'Planets',
    slug: 'planets',
    type: 'other',
    description: { short: 'Planet smilies', long: 'Out of this world.' },
    prices: { fairtrade_min: 120, fairtrade_max: 140, store: '275' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 352,
    name: 'Lions',
    slug: 'lions',
    type: 'superzoo',
    description: { short: 'Cute lion smilies', long: 'These 11 cute lion smilies will keep you safe, the power also includes one lion back.' },
    prices: { fairtrade_min: 300, fairtrade_max: 500, store: '275' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 350,
    name: 'Transport',
    slug: 'transport',
    type: 'yellow',
    description: { short: 'Transport smilies', long: 'Get your smilies going places.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 349,
    name: 'Me',
    slug: 'me',
    type: 'function',
    description: { short: 'Advanced profile CSS', long: 'Make an advanced xat.me page with minimal xat branding.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 348,
    name: 'Poke',
    slug: 'poke',
    type: 'yellow',
    description: { short: 'Poke fun smilies', long: 'Remixable poke, punch, crush smilies and more.' },
    prices: { fairtrade_min: 200, fairtrade_max: 230, store: '234' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 347,
    name: 'Balloonfx',
    slug: 'balloonfx',
    type: 'fx',
    description: { short: 'Balloon FX', long: 'Fun balloon inspired effects.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 346,
    name: 'Microbe',
    slug: 'microbe',
    type: 'smiley',
    description: { short: 'Microbe smilies', long: 'Fun microbe smilies. They are all backs!' },
    prices: { fairtrade_min: 80, fairtrade_max: 120, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 345,
    name: 'Shells',
    slug: 'shells',
    type: 'supersummer',
    description: { short: 'Sea shell smilies', long: 'Sea shells from the sea shore.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 62,
  },
  {
    id: 344,
    name: 'Nick',
    slug: 'nick',
    type: 'function',
    description: { short: 'Set friend nickname', long: 'Change your friend\'s name.' },
    prices: { fairtrade_min: 110, fairtrade_max: 140, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 343,
    name: 'Vacation',
    slug: 'vacation',
    type: 'supersummer',
    description: { short: 'Vacation smilies', long: 'Have a nice vacation!' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 342,
    name: 'Aquatic',
    slug: 'aquatic',
    type: 'other',
    description: { short: 'Aquatic smilies', long: 'Is it safe to get back in the water?' },
    prices: { fairtrade_min: 110, fairtrade_max: 130, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 341,
    name: 'Ceebear',
    slug: 'ceebear',
    type: 'superzoo',
    description: { short: 'Anime style bears', long: '20 Anime style bear smilies, male and female versions.' },
    prices: { fairtrade_min: 170, fairtrade_max: 230, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 339,
    name: 'Redcard',
    slug: 'redcard',
    type: 'epic',
    description: { short: 'Red card', long: 'Red card a user. They can watch but if they try to talk they ban themselves.' },
    prices: { fairtrade_min: 4200, fairtrade_max: 4500, store: '5000' },
    status: false,
    released: '2023-10-26',
    category: 'epic',
        categories: ['is_epic'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 337,
    name: 'Aliblue',
    slug: 'aliblue',
    type: 'smiley',
    description: { short: 'Cute Blue Alien smilies', long: 'Far out cute blue aliens.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1200, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 336,
    name: 'Shinobi',
    slug: 'shinobi',
    type: 'superhistory',
    description: { short: 'Shinobi smilies', long: 'Try and dodge the new shinobi smilies. Be a ninja, and fight!' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 335,
    name: 'Floral',
    slug: 'floral',
    type: 'superlove',
    description: { short: 'Floral smilies', long: 'Spring into life with these floral smilies.' },
    prices: { fairtrade_min: 600, fairtrade_max: 700, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 334,
    name: 'Eco',
    slug: 'eco',
    type: 'yellow',
    description: { short: 'Ecological smilies', long: 'Save the planet with these green smilies.' },
    prices: { fairtrade_min: 170, fairtrade_max: 250, store: '232' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 333,
    name: 'Mobilebeta',
    slug: 'mobilebeta',
    type: 'function',
    description: { short: 'Mobile smilies', long: 'It was about time for smilies to learn how to use smartphones. Mobile themed smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 332,
    name: 'Easterlove',
    slug: 'easterlove',
    type: 'superegg',
    description: { short: 'Easter love smilies', long: 'Have a lovely Easter.' },
    prices: { fairtrade_min: 170, fairtrade_max: 200, store: '222' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 331,
    name: 'Beastie',
    slug: 'beastie',
    type: 'smiley',
    description: { short: 'Beastie smilies', long: 'Beastie smilies, including 5 backs.' },
    prices: { fairtrade_min: 80, fairtrade_max: 120, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 330,
    name: 'Clouds',
    slug: 'clouds',
    type: 'smiley',
    description: { short: 'Cloud smilies', long: 'Too windy? With these cloud smilies you can define the weather on your chat.' },
    prices: { fairtrade_min: 500, fairtrade_max: 600, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 63,
  },
  {
    id: 329,
    name: 'Tv',
    slug: 'tv',
    type: 'smiley',
    description: { short: 'TV smilies', long: 'Sit back and relax with these cute television smilies.' },
    prices: { fairtrade_min: 2200, fairtrade_max: 2500, store: '275' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 328,
    name: 'Supercycle',
    slug: 'supercycle',
    type: 'colors',
    description: { short: 'Personalized color cycling', long: 'Cycle smiley colors your way. Requires cycle power.' },
    prices: { fairtrade_min: 430, fairtrade_max: 480, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 327,
    name: 'Birthday',
    slug: 'birthday',
    type: 'other',
    description: { short: 'Birthday smilies', long: 'Birthday and celebration themed smilies.' },
    prices: { fairtrade_min: 130, fairtrade_max: 160, store: '220' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 326,
    name: 'Oids',
    slug: 'oids',
    type: 'smiley',
    description: { short: 'Planetoid smilies', long: 'Turquoise xat planet themed smilies.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 325,
    name: 'Valfx',
    slug: 'valfx',
    type: 'superheart',
    description: { short: 'Valentine effects', long: 'Valentine themed FX with many options.' },
    prices: { fairtrade_min: 230, fairtrade_max: 280, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 324,
    name: 'Amore',
    slug: 'amore',
    type: 'superheart',
    description: { short: 'Amore smilies', long: 'Amore smilies for your valentine.' },
    prices: { fairtrade_min: 2500, fairtrade_max: 2800, store: '230' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 323,
    name: 'Offset',
    slug: 'offset',
    type: 'function',
    description: { short: 'Move smiley XY', long: 'Move a smiley up, down, left and right.' },
    prices: { fairtrade_min: 110, fairtrade_max: 140, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 322,
    name: 'Jewelry',
    slug: 'jewelry',
    type: 'other',
    description: { short: 'Jewelry', long: 'Add sparkle to your xat with jewelry.' },
    prices: { fairtrade_min: 200, fairtrade_max: 300, store: '260' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 321,
    name: 'Retro',
    slug: 'retro',
    type: 'superhistory',
    description: { short: 'Retro smilies', long: 'Relive the 8-bit era with these retro smilies.' },
    prices: { fairtrade_min: 600, fairtrade_max: 800, store: '100' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 320,
    name: 'Egyptian',
    slug: 'egyptian',
    type: 'superhistory',
    description: { short: 'Ancient Egypt', long: 'Chat like an Egyptian.' },
    prices: { fairtrade_min: 500, fairtrade_max: 700, store: '270' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 318,
    name: 'Backup',
    slug: 'backup',
    type: 'function',
    description: { short: 'Daily group backup', long: 'Have regular backups of your group\'s staff, guests and banned users.' },
    prices: { fairtrade_min: 170, fairtrade_max: 200, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 317,
    name: 'Lunar',
    slug: 'lunar',
    type: 'event',
    description: { short: 'Chinese new year', long: 'Chinese new year smilies.' },
    prices: { fairtrade_min: 130, fairtrade_max: 160, store: '222' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 316,
    name: 'Size',
    slug: 'size',
    type: 'function',
    description: { short: 'Resize smilies', long: 'Change the size of your smilies.' },
    prices: { fairtrade_min: 220, fairtrade_max: 240, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 315,
    name: 'Holidays',
    slug: 'holidays',
    type: 'supersanta',
    description: { short: 'Holiday smilies', long: 'Themed smilies for the holidays.' },
    prices: { fairtrade_min: 180, fairtrade_max: 230, store: '275' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 64,
  },
  {
    id: 314,
    name: 'Reveal',
    slug: 'reveal',
    type: 'supersanta',
    description: { short: 'Reveal smilies', long: 'Revealing holiday smilies.' },
    prices: { fairtrade_min: 400, fairtrade_max: 450, store: '450' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 313,
    name: 'Gamefx2',
    slug: 'gamefx2',
    type: 'superfx',
    description: { short: 'Game effects 2', long: 'The game goes on. More game style FX smilies.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 312,
    name: 'Icebucket',
    slug: 'icebucket',
    type: 'other',
    description: { short: 'Icebucket Smilies', long: 'Challenge your friends to chill out.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '210' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 311,
    name: 'Blueoni',
    slug: 'blueoni',
    type: 'superanime',
    description: { short: 'Blue Oni Smilies', long: 'Blue Oni characters with two FX.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '220' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 310,
    name: 'Manage',
    slug: 'manage',
    type: 'function',
    description: { short: 'Manage group users', long: 'Backup and tweak your user ranks.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '15' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 309,
    name: 'Creepy',
    slug: 'creepy',
    type: 'superhalloween',
    description: { short: 'Creepy smilies', long: 'Look creepy for Halloween.' },
    prices: { fairtrade_min: 500, fairtrade_max: 700, store: '230' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 308,
    name: 'Trickortreat',
    slug: 'trickortreat',
    type: 'superhalloween',
    description: { short: 'Trick or treat', long: 'Trick or treat smilies for Halloween.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '230' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 307,
    name: 'Jump',
    slug: 'jump',
    type: 'yellow',
    description: { short: 'Jumping smilies', long: 'Get jumping! This power includes 11 different jumping smilies.' },
    prices: { fairtrade_min: 130, fairtrade_max: 170, store: '220' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 306,
    name: 'Ksun',
    slug: 'ksun',
    type: 'superkao',
    description: { short: 'Kaoani sun', long: 'Enjoy some time in the sun.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1100, store: '240' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 305,
    name: 'Kmoon',
    slug: 'kmoon',
    type: 'superkao',
    description: { short: 'Kaoani moon smilies', long: 'Take a trip to the moon!' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '240' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 304,
    name: 'Chocolate',
    slug: 'chocolate',
    type: 'other',
    description: { short: 'Chocolate smilies', long: 'These smilies sure are chocolatey.' },
    prices: { fairtrade_min: 1300, fairtrade_max: 1500, store: '220' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 303,
    name: 'Bird',
    slug: 'bird',
    type: 'animal',
    description: { short: 'Bird smilies and FX', long: 'You know that saying \'one rock two birds\'? We present one power eleven birds.' },
    prices: { fairtrade_min: 130, fairtrade_max: 180, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 302,
    name: 'Pcback',
    slug: 'pcback',
    type: 'function',
    description: { short: 'Private chat background', long: 'Set a custom background that users will see when they private chat you.' },
    prices: { fairtrade_min: 660, fairtrade_max: 690, store: '700' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 301,
    name: 'Instruments',
    slug: 'instruments',
    type: 'other',
    description: { short: 'Musical instruments', long: 'Play your instruments!' },
    prices: { fairtrade_min: 800, fairtrade_max: 1000, store: '288' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 300,
    name: 'Blubunni',
    slug: 'blubunni',
    type: 'superbunny',
    description: { short: 'Blue bunnies', long: 'Zany blue bunny smilies.' },
    prices: { fairtrade_min: 900, fairtrade_max: 1200, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 65,
  },
  {
    id: 298,
    name: 'Coolz',
    slug: 'coolz',
    type: 'superanime',
    description: { short: 'Cool guy', long: 'Anime style guy smilies.' },
    prices: { fairtrade_min: 600, fairtrade_max: 700, store: '350' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 297,
    name: 'Statuscolor',
    slug: 'statuscolor',
    type: 'epic',
    description: { short: 'Set your status color', long: 'Color your status on the chat box. Requires status, statusglow and color powers.' },
    prices: { fairtrade_min: 4400, fairtrade_max: 4600, store: '5000' },
    status: false,
    released: '2023-10-26',
    category: 'epic',
        categories: ['is_epic'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 296,
    name: 'Summerflix',
    slug: 'summerflix',
    type: 'supersummer',
    description: { short: 'Summer flix and smilies', long: 'Summer animations and smilies for your chat.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 295,
    name: 'Cutie',
    slug: 'cutie',
    type: 'superanime',
    description: { short: 'Cute girl smilies', long: 'These girls are cute and beaut!' },
    prices: { fairtrade_min: 1200, fairtrade_max: 1500, store: '350' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 294,
    name: 'Winner',
    slug: 'winner',
    type: 'event',
    description: { short: 'Winning smilies', long: 'You\'ll need to be a winner to buy this power.' },
    prices: { fairtrade_min: 170, fairtrade_max: 200, store: '220' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 293,
    name: 'Ballfx',
    slug: 'ballfx',
    type: 'superfx',
    description: { short: 'Bouncing balls', long: 'Bouncing ball effects.' },
    prices: { fairtrade_min: 600, fairtrade_max: 1000, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 292,
    name: 'Yellowcard',
    slug: 'yellowcard',
    type: 'function',
    description: { short: 'Yellow card', long: 'Give a user a yellow card on their pawn.' },
    prices: { fairtrade_min: 750, fairtrade_max: 900, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 291,
    name: 'Rocks',
    slug: 'rocks',
    type: 'smiley',
    description: { short: 'Rocky smilies', long: 'Let\'s rock and roll! Rock smilies including rollingFX.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '230' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 290,
    name: 'Worldcup',
    slug: 'worldcup',
    type: 'event',
    description: { short: 'World cup smilies', long: 'Show your World Cup support with these soccer smilies.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 289,
    name: 'Gamefx',
    slug: 'gamefx',
    type: 'superfx',
    description: { short: 'Game effects', long: 'Game style FX smilies.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 288,
    name: 'Dreams',
    slug: 'dreams',
    type: 'other',
    description: { short: 'Dream smilies', long: 'Time to count the sheep. Sweet dreams with dreams power.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '267' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 286,
    name: 'Hamster',
    slug: 'hamster',
    type: 'kao',
    description: { short: 'Hamster smilies', long: 'You won\'t let this cute hamster go in a cage, will you?' },
    prices: { fairtrade_min: 600, fairtrade_max: 700, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 285,
    name: 'Coffee',
    slug: 'coffee',
    type: 'other',
    description: { short: 'Coffee smilies', long: 'Relax with these coffee themed smilies.' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '210' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 66,
  },
  {
    id: 284,
    name: 'Naughtystep',
    slug: 'naughtystep',
    type: 'function',
    description: { short: 'Naughty step', long: 'Put a user on the naughty step. Limit the number of messages a user can send to one per 30 seconds.' },
    prices: { fairtrade_min: 140, fairtrade_max: 170, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 283,
    name: 'Springy',
    slug: 'springy',
    type: 'smiley',
    description: { short: 'Springy smilies', long: 'Get a spring in your smiley.' },
    prices: { fairtrade_min: 600, fairtrade_max: 1000, store: '230' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 282,
    name: 'Butterflies',
    slug: 'butterflies',
    type: 'superfarm',
    description: { short: 'Butterflies smilies', long: 'Spring is here and the butterflies are emerging.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 281,
    name: 'Easteregg',
    slug: 'easteregg',
    type: 'superegg',
    description: { short: 'Easter smilies', long: 'Hip hop hooray! More easter-themed smilies.' },
    prices: { fairtrade_min: 170, fairtrade_max: 220, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 280,
    name: 'Eventstats',
    slug: 'eventstats',
    type: 'function',
    description: { short: 'Events statistics', long: 'Get events on chats summarized graphically.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 279,
    name: 'Snail',
    slug: 'snail',
    type: 'superfarm',
    description: { short: 'Snail smilies', long: 'No need to rush, patience is a virtue. Become sluggish with snail smilies.' },
    prices: { fairtrade_min: 80, fairtrade_max: 120, store: '222' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 278,
    name: 'Springflix',
    slug: 'springflix',
    type: 'other',
    description: { short: 'Spring and flix smilies', long: 'Spring is here. Use these flix animations to decorate your chat.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 277,
    name: 'Tongues',
    slug: 'tongues',
    type: 'yellow',
    description: { short: 'Tongue smilies', long: 'A fun selection of tongue smilies.' },
    prices: { fairtrade_min: 950, fairtrade_max: 1150, store: '290' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 276,
    name: 'Luck',
    slug: 'luck',
    type: 'holiday',
    description: { short: 'Lucky Irish smilies', long: 'The luck of the Irish.' },
    prices: { fairtrade_min: 400, fairtrade_max: 600, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 275,
    name: 'Bitefx',
    slug: 'bitefx',
    type: 'superfx',
    description: { short: 'Bite mark effects', long: 'Make a smiley look like it\'s being eaten.' },
    prices: { fairtrade_min: 180, fairtrade_max: 220, store: '230' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 274,
    name: 'Cupcake',
    slug: 'cupcake',
    type: 'smiley',
    description: { short: 'Cupcake smilies', long: 'Happiness is only a cupcake away.' },
    prices: { fairtrade_min: 100, fairtrade_max: 150, store: '240' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 273,
    name: 'Ladybug',
    slug: 'ladybug',
    type: 'superfarm',
    description: { short: 'Ladybug smilies', long: 'Little red bug, oh so cute!' },
    prices: { fairtrade_min: 80, fairtrade_max: 130, store: '275' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 272,
    name: 'Random',
    slug: 'random',
    type: 'function',
    description: { short: 'Randomize your smilies', long: 'This power displays a random smiley from all of the existing regular xat smilies and smilies from the powers that you have.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 271,
    name: 'Sweetheart',
    slug: 'sweetheart',
    type: 'superheart',
    description: { short: 'Sweetheart smilies', long: 'These smilies are a sweet deal!' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 270,
    name: 'Arachnid',
    slug: 'arachnid',
    type: 'superzoo',
    description: { short: 'Spidery smilies', long: 'Creepy arachnid smilies.' },
    prices: { fairtrade_min: 250, fairtrade_max: 350, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 67,
  },
  {
    id: 269,
    name: 'Divorce',
    slug: 'divorce',
    type: 'yellow',
    description: { short: 'Divorce kisses and smilies', long: 'Divorce in style. Smilies and 4 new kisses to get divorced with.' },
    prices: { fairtrade_min: 130, fairtrade_max: 170, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 268,
    name: 'Farm',
    slug: 'farm',
    type: 'superfarm',
    description: { short: 'Farm smilies', long: 'Get back to the farm.' },
    prices: { fairtrade_min: 130, fairtrade_max: 170, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 267,
    name: 'Cooking',
    slug: 'cooking',
    type: 'yellow',
    description: { short: 'Cooking smilies', long: 'Get cooking with these culinary smilies.' },
    prices: { fairtrade_min: 180, fairtrade_max: 230, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 266,
    name: 'Hogmanay',
    slug: 'hogmanay',
    type: 'other',
    description: { short: 'New year smilies', long: 'Bring in the new year with these festive smilies.' },
    prices: { fairtrade_min: 30, fairtrade_max: 60, store: '255' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 265,
    name: 'Celebrate',
    slug: 'celebrate',
    type: 'yellow',
    description: { short: 'Celebrate smilies', long: 'Find a reason to celebrate with this power.' },
    prices: { fairtrade_min: 250, fairtrade_max: 300, store: '275' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 264,
    name: 'Badge',
    slug: 'badge',
    type: 'function',
    description: { short: 'Award a badge', long: 'Award a badge pawn to anyone on the chat.' },
    prices: { fairtrade_min: 160, fairtrade_max: 180, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 263,
    name: 'Toys',
    slug: 'toys',
    type: 'supersanta',
    description: { short: 'Toy smilies', long: 'It\'s time for Christmas. 10 different toy smilies for you to gift your friends.' },
    prices: { fairtrade_min: 170, fairtrade_max: 200, store: '220' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 262,
    name: 'Noel',
    slug: 'noel',
    type: 'supersanta',
    description: { short: 'Seasonal Characters', long: 'Seasonal characters in male and female forms.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '150' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 261,
    name: 'Winterland',
    slug: 'winterland',
    type: 'superxmas',
    description: { short: 'Winter wonderland', long: 'Get ready for Christmas with the winter wonderland.' },
    prices: { fairtrade_min: 80, fairtrade_max: 130, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 260,
    name: 'Blackfriday',
    slug: 'blackfriday',
    type: 'superbf',
    description: { short: 'Black Friday power', long: 'Shopping and bargain hunting smilies.' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 259,
    name: 'Froggy',
    slug: 'froggy',
    type: 'superfarm',
    description: { short: 'Froggy smilies', long: 'Ribbit! Leap to these froggy style smilies.' },
    prices: { fairtrade_min: 60, fairtrade_max: 80, store: '341' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 258,
    name: 'Piracy',
    slug: 'piracy',
    type: 'superhistory',
    description: { short: 'Pirate smilies', long: 'Piracy on the high seas.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '258' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 257,
    name: 'Halloween2',
    slug: 'halloween2',
    type: 'superhalloween',
    description: { short: 'Halloween costumes', long: 'These smilies have the best Halloween costumes.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '252' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 256,
    name: 'Zwhack',
    slug: 'zwhack',
    type: 'superscary',
    description: { short: 'Whack a zombie game', long: 'Use the xat ban hammer to smite those pesky zombies.' },
    prices: { fairtrade_min: 500, fairtrade_max: 550, store: '600' },
    status: false,
    released: '2023-10-26',
    category: 'games',
        categories: ['is_group', 'is_game'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 68,
  },
  {
    id: 254,
    name: 'Scary',
    slug: 'scary',
    type: 'superscary',
    description: { short: 'Scary smilies', long: 'Don\'t be scared for Halloween!' },
    prices: { fairtrade_min: 350, fairtrade_max: 450, store: '552' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 253,
    name: 'Ani1',
    slug: 'ani1',
    type: 'superanime',
    description: { short: 'Anime smilies and FX', long: 'Anime smilies and a star FX.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '307' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 252,
    name: 'Redirect',
    slug: 'redirect',
    type: 'function',
    description: { short: 'Redirect a chat', long: 'Use this power to redirect a chat to another chat.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 251,
    name: 'Eggy',
    slug: 'eggy',
    type: 'superegg',
    description: { short: 'Cute egg smilies', long: 'These eggs are special!' },
    prices: { fairtrade_min: 30, fairtrade_max: 40, store: '110' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 250,
    name: 'Autumn',
    slug: 'autumn',
    type: 'yellow',
    description: { short: 'Smilies for fall', long: 'Celebrate the fall colors.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '275' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 249,
    name: 'Kdemon',
    slug: 'kdemon',
    type: 'superkao',
    description: { short: 'Kaoani demons', long: 'Can demons be cute?' },
    prices: { fairtrade_min: 1500, fairtrade_max: 2000, store: '1000' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 248,
    name: 'Kangel',
    slug: 'kangel',
    type: 'superkao',
    description: { short: 'Kaoani angels', long: 'Kaoani styled angel smilies.' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 247,
    name: 'Weather',
    slug: 'weather',
    type: 'other',
    description: { short: 'Weather smilies', long: 'Smilies for all weathers.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 246,
    name: 'Darts',
    slug: 'darts',
    type: 'games',
    description: { short: 'Dart zing game', long: 'Allows you to play alone or compete against other players by racking up points by throwing darts at a dartboard.' },
    prices: { fairtrade_min: 550, fairtrade_max: 600, store: '900' },
    status: false,
    released: '2023-10-26',
    category: 'games',
        categories: ['is_group', 'is_game'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 245,
    name: 'Fruities',
    slug: 'fruities',
    type: 'smiley',
    description: { short: 'Fruities smilies', long: 'These smilies will have you feeling fruity!' },
    prices: { fairtrade_min: 100, fairtrade_max: 150, store: '253' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 244,
    name: 'Kickall',
    slug: 'kickall',
    type: 'function',
    description: { short: 'Kick all guests', long: 'Kick all guests in your chat. This power should not be miss used.' },
    prices: { fairtrade_min: 200, fairtrade_max: 230, store: '30' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 243,
    name: 'Sticky',
    slug: 'sticky',
    type: 'other',
    description: { short: 'Sticky note smilies', long: 'Smilies inspired by sticky notes.' },
    prices: { fairtrade_min: 140, fairtrade_max: 170, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 242,
    name: 'Romance',
    slug: 'romance',
    type: 'superlove',
    description: { short: 'Romantic smilies', long: 'Show your affection with these romantic smilies.' },
    prices: { fairtrade_min: 1800, fairtrade_max: 2200, store: '623' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 241,
    name: 'Marriage',
    slug: 'marriage',
    type: 'superlove',
    description: { short: 'Marriage smilies', long: '4 marriage kisses and 10 smilies to help you celebrate your marriage.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 240,
    name: 'Phasefx',
    slug: 'phasefx',
    type: 'superfx',
    description: { short: 'Phase effects', long: 'Add a cool phase effect to your smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 69,
  },
  {
    id: 239,
    name: 'Cuboid',
    slug: 'cuboid',
    type: 'smiley',
    description: { short: 'Cube shaped smilies', long: 'These smilies have an edge!' },
    prices: { fairtrade_min: 50, fairtrade_max: 70, store: '239' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 238,
    name: 'Switch',
    slug: 'switch',
    type: 'games',
    description: { short: 'Switch card game', long: 'Be game master of a switch card game.' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'games',
        categories: ['is_group', 'is_game'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 237,
    name: 'Fourth',
    slug: 'fourth',
    type: 'yellow',
    description: { short: 'Fourth July smilies', long: 'Celebrate the 4th July with these festive smilies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '260' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 236,
    name: 'Slotban',
    slug: 'slotban',
    type: 'gamebans',
    description: { short: 'Slot ban', long: 'A slotbanned user has the chance to get out of the ban by playing a slot machine game.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'games',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 235,
    name: 'Cactus',
    slug: 'cactus',
    type: 'smiley',
    description: { short: 'Cactus smilies', long: 'These smilies are lookin\' sharp!' },
    prices: { fairtrade_min: 50, fairtrade_max: 70, store: '238' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 234,
    name: 'Germ',
    slug: 'germ',
    type: 'smiley',
    description: { short: 'Germ smilies', long: 'Ewwww germs themed smilies.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '325' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 233,
    name: 'Wedding',
    slug: 'wedding',
    type: 'superlove',
    description: { short: 'Wedding smilies', long: 'Celebrate a wedding with these wedding smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '253' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 232,
    name: 'Super',
    slug: 'super',
    type: 'smiley',
    description: { short: 'Super smilies', long: 'Lookin\' super shiny!' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 231,
    name: 'Statusglow',
    slug: 'statusglow',
    type: 'function',
    description: { short: 'Neon glow status', long: 'Add a glow to your status (status power required).' },
    prices: { fairtrade_min: 200, fairtrade_max: 220, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 230,
    name: 'Hair2f',
    slug: 'hair2f',
    type: 'yellow',
    description: { short: 'Hair smilies', long: 'More female smiley hair styles.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '306' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 229,
    name: 'Seaside',
    slug: 'seaside',
    type: 'supersummer',
    description: { short: 'Seaside smilies', long: 'Get ready for summer with the seaside power.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '297' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 228,
    name: 'Led',
    slug: 'led',
    type: 'other',
    description: { short: 'LED effect', long: 'Write a message with LED lighted effects.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 227,
    name: 'Sketch',
    slug: 'sketch',
    type: 'smiley',
    description: { short: 'Sketch smilies', long: 'Animated sketch style smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 150, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 226,
    name: 'Kcow',
    slug: 'kcow',
    type: 'superfarm',
    description: { short: 'Kaoani cow smilies', long: 'These cute cows will put you in a good MOOd.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '133' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 225,
    name: 'Kfox',
    slug: 'kfox',
    type: 'superfarm',
    description: { short: 'Kaoani fox smilies', long: 'Don\'t get outfoxed!' },
    prices: { fairtrade_min: 300, fairtrade_max: 400, store: '469' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 70,
  },
  {
    id: 222,
    name: 'Eggs',
    slug: 'eggs',
    type: 'superegg',
    description: { short: 'Egg and Easter smilies', long: 'These smilies will make you feel egg-cited for Easter!' },
    prices: { fairtrade_min: 180, fairtrade_max: 230, store: '231' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 221,
    name: 'Hands2',
    slug: 'hands2',
    type: 'yellow',
    description: { short: 'More hands smilies', long: 'More hands smilies including high five.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 220,
    name: 'Vote',
    slug: 'vote',
    type: 'function',
    description: { short: 'Add a poll to your chat', long: 'Survey your users with a mini poll plus bonus smilies.' },
    prices: { fairtrade_min: 250, fairtrade_max: 300, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 219,
    name: 'Spring',
    slug: 'spring',
    type: 'yellow',
    description: { short: 'Spring season smilies', long: 'Spring is here. Celebrate with these beautiful smilies.' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '585' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 218,
    name: 'Stylist',
    slug: 'stylist',
    type: 'yellow',
    description: { short: 'Smilies with style', long: 'Add style to your smilies. This power includes 4 smilies and 7 hairstyles.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 217,
    name: 'Nuclear',
    slug: 'nuclear',
    type: 'function',
    description: { short: 'Nuclear effect', long: 'Add a cool nuclear effect to your smilies.' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 216,
    name: 'Kmonkey',
    slug: 'kmonkey',
    type: 'superzoo',
    description: { short: 'Kaoani monkey smilies', long: 'Monkey around with these smilies.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 215,
    name: 'Kheart',
    slug: 'kheart',
    type: 'superheart',
    description: { short: 'Kaoani heart smilies', long: 'Heart shaped smilies in kaoani style.' },
    prices: { fairtrade_min: 500, fairtrade_max: 600, store: '458' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 214,
    name: 'Makeup',
    slug: 'makeup',
    type: 'yellow',
    description: { short: 'Makeup smilies', long: 'You\'ll be made up with these smilies.' },
    prices: { fairtrade_min: 80, fairtrade_max: 120, store: '168' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 212,
    name: 'Foe',
    slug: 'foe',
    type: 'function',
    description: { short: 'Foe or friend', long: 'Mark a user as a foe.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 211,
    name: 'Eighties',
    slug: 'eighties',
    type: 'superhistory',
    description: { short: 'Eighties smilies', long: 'Throw it back to the eighties with these smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '185' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 210,
    name: 'Kmouse',
    slug: 'kmouse',
    type: 'kao',
    description: { short: 'Kaoani mouse smilies', long: 'Be as quiet as a mouse!' },
    prices: { fairtrade_min: 800, fairtrade_max: 1200, store: '1740' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 71,
  },
  {
    id: 208,
    name: 'Glitterfx',
    slug: 'glitterfx',
    type: 'superfx',
    description: { short: 'Glitter effects', long: 'Glitter effects for smilies.' },
    prices: { fairtrade_min: 180, fairtrade_max: 220, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 207,
    name: 'Quest2',
    slug: 'quest2',
    type: 'yellow',
    description: { short: 'More quest smilies', long: 'Continue the quest.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '112' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 206,
    name: 'Lang',
    slug: 'lang',
    type: 'function',
    description: { short: 'Custom language', long: 'Create a custom language for your chat. There are over 250 features you can customize such as the titles of ranks, bans, kicks etc.' },
    prices: { fairtrade_min: 1500, fairtrade_max: 1600, store: '2000' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 205,
    name: 'Quest',
    slug: 'quest',
    type: 'yellow',
    description: { short: 'Quest smilies', long: 'Smilies to help you on your journey.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 204,
    name: 'Claus',
    slug: 'claus',
    type: 'supersanta',
    description: { short: 'Santa Claus smilies', long: 'Comical santa smilies for Christmas.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '272' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 203,
    name: 'Treefx',
    slug: 'treefx',
    type: 'superxmas',
    description: { short: 'Christmas tree effects', long: 'Christmas tree FX for smilies.' },
    prices: { fairtrade_min: 3000, fairtrade_max: 3500, store: '600' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 202,
    name: 'Vampyre',
    slug: 'vampyre',
    type: 'superscary',
    description: { short: 'Vampyre smilies', long: 'Frighten your friends with these vampyre smilies.' },
    prices: { fairtrade_min: 230, fairtrade_max: 280, store: '297' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 201,
    name: 'Speech',
    slug: 'speech',
    type: 'kao',
    description: { short: 'Speech smilies', long: 'Never stop chatting with these speech bubble smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 200,
    name: 'Spacewar',
    slug: 'spacewar',
    type: 'games',
    description: { short: 'Space war ', long: 'Compete with others by destroying the other player\'s planets.' },
    prices: { fairtrade_min: 8000, fairtrade_max: 9000, store: '1160' },
    status: 'limited',
    released: '2023-10-26',
    category: 'games',
        categories: ['is_group', 'is_game', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 199,
    name: 'Drop',
    slug: 'drop',
    type: 'kao',
    description: { short: 'Drop smilies', long: 'Drop-themed smilies.' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '700' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 198,
    name: 'Clockfx',
    slug: 'clockfx',
    type: 'superfx',
    description: { short: 'Clock effects', long: 'Add rotating (clock) effects to your smilies!' },
    prices: { fairtrade_min: 900, fairtrade_max: 1200, store: '339' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 197,
    name: 'Pony',
    slug: 'pony',
    type: 'superzoo',
    description: { short: 'Pony smilies', long: 'Pony up!' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '164' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 196,
    name: 'Poker',
    slug: 'poker',
    type: 'yellow',
    description: { short: 'Poker smilies', long: 'Keep a straight face with these poker themed smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '382' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 195,
    name: 'Kpig',
    slug: 'kpig',
    type: 'superfarm',
    description: { short: 'Kaoani pig smilies', long: 'This little piggy had to be cute.' },
    prices: { fairtrade_min: 200, fairtrade_max: 300, store: '234' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 72,
  },
  {
    id: 194,
    name: 'Snakerace',
    slug: 'snakerace',
    type: 'games',
    description: { short: 'Snake race', long: 'Play and control a snake game. Compete with others by eating the designated number of apples before the other players.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'games',
        categories: ['is_group', 'is_game', 'is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 193,
    name: 'Burningheart',
    slug: 'burningheart',
    type: 'superheart',
    description: { short: 'Burning heart effect', long: 'Add a decorative flame to a smiley of your choice.' },
    prices: { fairtrade_min: 3500, fairtrade_max: 3700, store: '1099' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 190,
    name: 'Aliens',
    slug: 'aliens',
    type: 'smiley',
    description: { short: 'Alien smilies', long: 'Aliens are taking over!' },
    prices: { fairtrade_min: 2000, fairtrade_max: 2200, store: '619' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 189,
    name: 'Olympic',
    slug: 'olympic',
    type: 'smiley',
    description: { short: 'Olympic smilies', long: 'Smilies to celebrate the Olympic games.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '334' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 188,
    name: 'Doodlerace',
    slug: 'doodlerace',
    type: 'games',
    description: { short: 'DoodleRace game', long: 'Start and control the doodlerace game. Compete with others by creating the best doodle.' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '400' },
    status: false,
    released: '2023-10-26',
    category: 'games',
        categories: ['is_group', 'is_game'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 187,
    name: 'Whirlfx',
    slug: 'whirlfx',
    type: 'superfx',
    description: { short: 'Whirling effect', long: 'Add a whirl effect to your smilies.' },
    prices: { fairtrade_min: 1400, fairtrade_max: 1600, store: '906' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 186,
    name: 'Moustache',
    slug: 'moustache',
    type: 'yellow',
    description: { short: 'Moustaches', long: 'Add moustaches to your smilies.' },
    prices: { fairtrade_min: 40, fairtrade_max: 60, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 185,
    name: 'Drip',
    slug: 'drip',
    type: 'smiley',
    description: { short: 'Drip smilies', long: 'Tears or raindrops, these smilies are wet!' },
    prices: { fairtrade_min: 200, fairtrade_max: 250, store: '306' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 184,
    name: 'Zip',
    slug: 'zip',
    type: 'function',
    description: { short: 'Zip ban', long: 'Force a user to only send smilies. All other messages will appear blank.' },
    prices: { fairtrade_min: 1200, fairtrade_max: 1500, store: '455' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 183,
    name: 'Jail',
    slug: 'jail',
    type: 'yellow',
    description: { short: 'Jail smilies', long: 'Lock up your smilies.' },
    prices: { fairtrade_min: 80, fairtrade_max: 100, store: '190' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 182,
    name: 'Vortexfx',
    slug: 'vortexfx',
    type: 'superfx',
    description: { short: 'Vortex effects', long: 'Add vortex effects to smilies.' },
    prices: { fairtrade_min: 1200, fairtrade_max: 1400, store: '764' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 181,
    name: 'Kbee',
    slug: 'kbee',
    type: 'superfarm',
    description: { short: 'Kaoani bee smilies', long: 'Buzz around with these smilies.' },
    prices: { fairtrade_min: 350, fairtrade_max: 450, store: '409' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 180,
    name: 'Gsound',
    slug: 'gsound',
    type: 'function',
    description: { short: 'Group sounds', long: 'Change the sounds on your group.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '20' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 73,
  },
  {
    id: 179,
    name: 'Nursing',
    slug: 'nursing',
    type: 'yellow',
    description: { short: 'Nursing smilies', long: 'Nursing smilies for nursing week.' },
    prices: { fairtrade_min: 60, fairtrade_max: 80, store: '115' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 178,
    name: 'Spiralfx',
    slug: 'spiralfx',
    type: 'superfx',
    description: { short: 'Spiral effects', long: 'Add spiral effects to smilies.' },
    prices: { fairtrade_min: 180, fairtrade_max: 200, store: '222' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 177,
    name: 'Fuzzy',
    slug: 'fuzzy',
    type: 'smiley',
    description: { short: 'Fuzzy smilies', long: 'Feel fuzzier with these fuzzy monster smilies.' },
    prices: { fairtrade_min: 550, fairtrade_max: 650, store: '324' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 176,
    name: 'Reverse',
    slug: 'reverse',
    type: 'function',
    description: { short: 'Reverse ban', long: 'Cause a users words to be reversed.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 175,
    name: 'Blobby',
    slug: 'blobby',
    type: 'smiley',
    description: { short: 'Blobby smilies', long: 'Feel blobby with these blob-themed smilies.' },
    prices: { fairtrade_min: 700, fairtrade_max: 800, store: '138' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 174,
    name: 'Pulsefx',
    slug: 'pulsefx',
    type: 'superfx',
    description: { short: 'Pulse effect', long: 'Add pulse-styled glowing effects to smilies' },
    prices: { fairtrade_min: 1500, fairtrade_max: 1800, store: '500' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 173,
    name: 'Ksheep',
    slug: 'ksheep',
    type: 'superfarm',
    description: { short: 'Sheep smilies', long: 'Baa! Kaoani sheep smilies.' },
    prices: { fairtrade_min: 150, fairtrade_max: 200, store: '175' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 172,
    name: 'Typing',
    slug: 'typing',
    type: 'function',
    description: { short: 'Show typing', long: 'Notify users when you\'re typing a message.' },
    prices: { fairtrade_min: 60, fairtrade_max: 70, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 171,
    name: 'Kat',
    slug: 'kat',
    type: 'kao',
    description: { short: 'Kat smilies', long: 'The kat is out of the bag' },
    prices: { fairtrade_min: 500, fairtrade_max: 600, store: '645' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 170,
    name: 'Monster',
    slug: 'monster',
    type: 'smiley',
    description: { short: 'Monster smilies', long: 'This power is monstrous!' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 169,
    name: 'Movie',
    slug: 'movie',
    type: 'yellow',
    description: { short: 'Movie-themed smilies', long: 'Lights, camera, action! Welcome to the film industry.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1200, store: '141' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 168,
    name: 'Topspin',
    slug: 'topspin',
    type: 'other',
    description: { short: 'Topspin effect', long: 'Don\'t get dizzy! Make certain smiley objects spin.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 167,
    name: 'Carnival',
    slug: 'carnival',
    type: 'yellow',
    description: { short: 'Carnival-themed smilies', long: 'Get in the carnival spirit!' },
    prices: { fairtrade_min: 140, fairtrade_max: 180, store: '275' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 166,
    name: 'Heartfx',
    slug: 'heartfx',
    type: 'superheart',
    description: { short: 'Heart FXs', long: 'Add heart-styled glowing effects to smilies.' },
    prices: { fairtrade_min: 140, fairtrade_max: 180, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 165,
    name: 'Kduck',
    slug: 'kduck',
    type: 'superfarm',
    description: { short: 'Duck smilies', long: 'Quack quack! 13 duck smilies and one back.' },
    prices: { fairtrade_min: 700, fairtrade_max: 900, store: '789' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 74,
  },
  {
    id: 164,
    name: 'Spy',
    slug: 'spy',
    type: 'yellow',
    description: { short: 'Spy smilies', long: 'Turn on the radar, look through the keyhole. With the right espionage methods, you can make the best smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '193' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 163,
    name: 'Magicfx',
    slug: 'magicfx',
    type: 'superfx',
    description: { short: 'Magical effect', long: 'Add glowing effects to any smiley with custom colors!' },
    prices: { fairtrade_min: 100, fairtrade_max: 150, store: '111' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 162,
    name: 'Codeban',
    slug: 'codeban',
    type: 'gamebans',
    description: { short: 'Code Ban', long: 'Ban a user so that they are required to complete a code themed mini-game to be unbanned.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'games',
        categories: ['is_group', 'is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 161,
    name: 'Can',
    slug: 'can',
    type: 'smiley',
    description: { short: 'Can smilies', long: 'Can-themed smilies. Companion power to Punch power.' },
    prices: { fairtrade_min: 100, fairtrade_max: 120, store: '125' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 160,
    name: 'Newyear',
    slug: 'newyear',
    type: 'yellow',
    description: { short: 'New year smilies', long: 'Smilies to help bring in the new year.' },
    prices: { fairtrade_min: 400, fairtrade_max: 500, store: '552' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 158,
    name: 'Dunce',
    slug: 'dunce',
    type: 'function',
    description: { short: 'Dunce power', long: 'Dunce your friends. Add a dunce cap to their pawn.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 157,
    name: 'Sparta',
    slug: 'sparta',
    type: 'superhistory',
    description: { short: 'Spartan smilies', long: 'This is Sparta!' },
    prices: { fairtrade_min: 100, fairtrade_max: 150, store: '125' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 156,
    name: 'Santa',
    slug: 'santa',
    type: 'supersanta',
    description: { short: 'Santa flix and smilies', long: 'Ho ho ho! Santa flix (animated backgrounds) and smilies.' },
    prices: { fairtrade_min: 230, fairtrade_max: 250, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 155,
    name: 'Reindeer',
    slug: 'reindeer',
    type: 'supersanta',
    description: { short: 'Reindeer smilies', long: 'Don\'t forget the magic reindeer food!' },
    prices: { fairtrade_min: 160, fairtrade_max: 180, store: '110' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 154,
    name: 'Snowman',
    slug: 'snowman',
    type: 'supersanta',
    description: { short: 'Snowman smilies', long: 'Build your snowman!' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '148' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 152,
    name: 'Mazeban',
    slug: 'mazeban',
    type: 'gamebans',
    description: { short: 'Maze ban', long: 'Ban a user so that they are required to complete a maze to be unbanned.' },
    prices: { fairtrade_min: 230, fairtrade_max: 250, store: '400' },
    status: false,
    released: '2023-10-26',
    category: 'games',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 151,
    name: 'Manga',
    slug: 'manga',
    type: 'superanime',
    description: { short: 'Manga smilies', long: 'Over 15 manga-themed smilies.' },
    prices: { fairtrade_min: 2100, fairtrade_max: 2500, store: '1870' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 150,
    name: 'Bot',
    slug: 'bot',
    type: 'function',
    description: { short: 'Add a bot to your chat', long: 'Allow a bot to access your group plus bonus smilies.' },
    prices: { fairtrade_min: 700, fairtrade_max: 800, store: '1000' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 75,
  },
  {
    id: 149,
    name: 'Kdog',
    slug: 'kdog',
    type: 'kao',
    description: { short: 'Kaoani dog smilies', long: 'Woof woof! Cute kaoani dog smilies.' },
    prices: { fairtrade_min: 400, fairtrade_max: 450, store: '223' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 148,
    name: 'Spooky',
    slug: 'spooky',
    type: 'superscary',
    description: { short: 'Spooky power', long: 'Scare your friends with spooky smilies and flix animations.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '334' },
    status: 'limited',
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 147,
    name: 'Carve',
    slug: 'carve',
    type: 'superhalloween',
    description: { short: 'Carved pumpkin smilies', long: 'Show off your carved pumpkins!' },
    prices: { fairtrade_min: 330, fairtrade_max: 380, store: '268' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 146,
    name: 'Kchick',
    slug: 'kchick',
    type: 'superfarm',
    description: { short: 'Chick smilies', long: 'These smilies are tres chick!' },
    prices: { fairtrade_min: 200, fairtrade_max: 300, store: '506' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 145,
    name: 'Peace',
    slug: 'peace',
    type: 'yellow',
    description: { short: 'Peace-themed smilies', long: 'Get your hippie on!' },
    prices: { fairtrade_min: 120, fairtrade_max: 140, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 144,
    name: 'Away',
    slug: 'away',
    type: 'function',
    description: { short: 'Away from chat', long: 'Let other users know that you are away from the chatroom with an automatic away icon on your pawn.' },
    prices: { fairtrade_min: 60, fairtrade_max: 70, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 143,
    name: 'Punch',
    slug: 'punch',
    type: 'smiley',
    description: { short: 'Punching bag smilies', long: 'Block all the punches.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 142,
    name: 'Silentm',
    slug: 'silentm',
    type: 'function',
    description: { short: 'Silent member', long: 'Make a member without the message popping up in the chat. Owners only unless changed by gcontrol.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '20' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 141,
    name: 'School',
    slug: 'school',
    type: 'yellow',
    description: { short: 'School smilies', long: 'Smilies for the classroom.' },
    prices: { fairtrade_min: 70, fairtrade_max: 90, store: '132' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 140,
    name: 'Matchban',
    slug: 'matchban',
    type: 'gamebans',
    description: { short: 'Match ban', long: 'Ban a user so that they are required to complete a matching themed mini-game to be unbanned.' },
    prices: { fairtrade_min: 400, fairtrade_max: 450, store: '1270' },
    status: 'limited',
    released: '2023-10-26',
    category: 'games',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 139,
    name: 'Nerd',
    slug: 'nerd',
    type: 'yellow',
    description: { short: 'Nerd smilies', long: 'Nerd smilies for all you geeks.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '218' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 138,
    name: 'Kpeng',
    slug: 'kpeng',
    type: 'kao',
    description: { short: 'Penguin smilies', long: 'Waddle around the chat with these kaoani penguin smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 120, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 136,
    name: 'Spaceban',
    slug: 'spaceban',
    type: 'gamebans',
    description: { short: 'Space ban', long: 'Ban a user so that they are required to complete a space themed mini-game to be unbanned.' },
    prices: { fairtrade_min: 4800, fairtrade_max: 5300, store: '1130' },
    status: 'limited',
    released: '2023-10-26',
    category: 'games',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 135,
    name: 'Stoneage',
    slug: 'stoneage',
    type: 'superhistory',
    description: { short: 'Stoneage smilies', long: 'Prehistoric and caveman smilies. A Stoneage kiss is included.' },
    prices: { fairtrade_min: 100, fairtrade_max: 150, store: '77' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 76,
  },
  {
    id: 134,
    name: 'Snakeban',
    slug: 'snakeban',
    type: 'gamebans',
    description: { short: 'Snake ban', long: 'Ban a user so that they are required to complete a snake themed mini-game to be unbanned.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '600' },
    status: false,
    released: '2023-10-26',
    category: 'games',
        categories: ['is_group', 'is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 132,
    name: 'Flower',
    slug: 'flower',
    type: 'smiley',
    description: { short: 'Flower-themed smilies', long: 'Get your flowers while they\'re fresh!' },
    prices: { fairtrade_min: 1800, fairtrade_max: 2200, store: '1210' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 131,
    name: 'Zodiac',
    slug: 'zodiac',
    type: 'kao',
    description: { short: 'Zodiac smilies', long: 'Which zodiac sign are you?' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 130,
    name: 'Gback',
    slug: 'gback',
    type: 'epic',
    description: { short: 'Group smiley backs', long: 'Set a default smiley back on your chat.' },
    prices: { fairtrade_min: 11500, fairtrade_max: 12500, store: '15000' },
    status: false,
    released: '2023-10-26',
    category: 'epic',
        categories: ['is_group', 'is_epic'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 129,
    name: 'Candy',
    slug: 'candy',
    type: 'yellow',
    description: { short: 'Sweet smilies', long: 'Candy-themed smilies including two backs and two hands.' },
    prices: { fairtrade_min: 1000, fairtrade_max: 1200, store: '454' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 128,
    name: 'Beach',
    slug: 'beach',
    type: 'supersummer',
    description: { short: 'Fun on the beach', long: 'Fun beach smilies in time for the summer.' },
    prices: { fairtrade_min: 100, fairtrade_max: 120, store: '250' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 126,
    name: 'Banpool',
    slug: 'banpool',
    type: 'function',
    description: { short: 'Pool for banned users', long: 'Create a brown colored pool for banned users. Rankpool is also required to use this power.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '15' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 125,
    name: 'Work',
    slug: 'work',
    type: 'yellow',
    description: { short: 'Worker smilies', long: 'Celebrate work day with this power. Work hard, smile harder.' },
    prices: { fairtrade_min: 100, fairtrade_max: 150, store: '236' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 124,
    name: 'Wildwest',
    slug: 'wildwest',
    type: 'superhistory',
    description: { short: 'Western smilies', long: 'Wild west smilies and two western kisses.' },
    prices: { fairtrade_min: 700, fairtrade_max: 1000, store: '731' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 123,
    name: 'Outfit',
    slug: 'outfit',
    type: 'yellow',
    description: { short: 'Smiley outfits', long: 'Time to get dressed up!' },
    prices: { fairtrade_min: 800, fairtrade_max: 850, store: '841' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 122,
    name: 'Sins',
    slug: 'sins',
    type: 'smiley',
    description: { short: 'Deadly sins smilies', long: 'Which of the seven deadly sins smilies are you?' },
    prices: { fairtrade_min: 1100, fairtrade_max: 1300, store: '1520' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 121,
    name: 'Zap',
    slug: 'zap',
    type: 'function',
    description: { short: 'Kick with a shake', long: 'Kick a user and make their chat shake with a sound of your choice.' },
    prices: { fairtrade_min: 70, fairtrade_max: 90, store: '10' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 120,
    name: 'Events',
    slug: 'events',
    type: 'function',
    description: { short: 'View chat events', long: 'See the log of recent actions such as kicking and banning on a chat.' },
    prices: { fairtrade_min: 180, fairtrade_max: 200, store: '20' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 77,
  },
  {
    id: 119,
    name: 'Unwell',
    slug: 'unwell',
    type: 'yellow',
    description: { short: 'Unwell-themed smilies', long: 'Don\'t get other users sick with this power.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '300' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 118,
    name: 'Gkpanda',
    slug: 'gkpanda',
    type: 'kao',
    description: { short: 'Kaoani panda smilies', long: 'Panda smilies for your chat.' },
    prices: { fairtrade_min: 2000, fairtrade_max: 2200, store: '798' },
    status: 'limited',
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 116,
    name: 'Animal',
    slug: 'animal',
    type: 'yellow',
    description: { short: 'Animal smilies', long: 'Animal-themed smilies and a \'Dancing dog\' kiss if assigned to your chat.' },
    prices: { fairtrade_min: 100, fairtrade_max: 125, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 115,
    name: 'Spin',
    slug: 'spin',
    type: 'function',
    description: { short: 'Spin your smilies', long: 'Make your smilies spin 360 degrees, in a clockwise or counter-clockwise direction.' },
    prices: { fairtrade_min: 160, fairtrade_max: 180, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 114,
    name: 'Rankpool',
    slug: 'rankpool',
    type: 'function',
    description: { short: 'Private pool for your chat', long: 'Create a private pool for the rank of your choice. You are able to edit the names of the first two pools as well.' },
    prices: { fairtrade_min: 450, fairtrade_max: 500, store: '800' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 112,
    name: 'Announce',
    slug: 'announce',
    type: 'function',
    description: { short: 'Custom message', long: 'Create a custom welcome message that will be sent to all users that enter your chat.' },
    prices: { fairtrade_min: 1450, fairtrade_max: 1650, store: '2000' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 110,
    name: 'Gkkitty',
    slug: 'gkkitty',
    type: 'kao',
    description: { short: 'Kaoani kitty smilies', long: 'Kitty-themed smilies for your chat.' },
    prices: { fairtrade_min: 300, fairtrade_max: 500, store: '416' },
    status: 'limited',
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 109,
    name: 'Barge',
    slug: 'barge',
    type: 'function',
    description: { short: 'Barge into pool', long: 'Allows owners and mods to move into full pools by barging another user out.' },
    prices: { fairtrade_min: 160, fairtrade_max: 180, store: '20' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 108,
    name: 'Love',
    slug: 'love',
    type: 'yellow',
    description: { short: 'Love smilies and flix', long: 'Love smilies and flix (animated backgrounds) for groups.' },
    prices: { fairtrade_min: 150, fairtrade_max: 180, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 107,
    name: 'Ugly',
    slug: 'ugly',
    type: 'smiley',
    description: { short: 'Ugly smilies', long: 'Be different with these ugly style smilies.' },
    prices: { fairtrade_min: 600, fairtrade_max: 700, store: '387' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 106,
    name: 'Gscol',
    slug: 'gscol',
    type: 'function',
    description: { short: 'Default smiley color', long: 'Change the default color of all of the smilies posted in your chat.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 105,
    name: 'Angry',
    slug: 'angry',
    type: 'yellow',
    description: { short: 'Angry smilies', long: 'Don\'t get upset, get the angry power and vent your frustration with these smilies.' },
    prices: { fairtrade_min: 5500, fairtrade_max: 6000, store: '3999' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 78,
  },
  {
    id: 104,
    name: 'Gkbear',
    slug: 'gkbear',
    type: 'kao',
    description: { short: 'Kaoani bear smilies', long: 'Cute bear smilies for your chat.' },
    prices: { fairtrade_min: 900, fairtrade_max: 1100, store: '660' },
    status: 'limited',
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 102,
    name: 'Fairy',
    slug: 'fairy',
    type: 'yellow',
    description: { short: 'Fairy flix and smilies', long: 'Fairy flix (animated backgrounds) and smilies.' },
    prices: { fairtrade_min: 3900, fairtrade_max: 4200, store: '3860' },
    status: 'limited',
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 101,
    name: 'Shocker',
    slug: 'shocker',
    type: 'yellow',
    description: { short: 'Shocker smilies', long: 'We\'re all shocked after seeing these smilies.' },
    prices: { fairtrade_min: 530, fairtrade_max: 560, store: '1040' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 100,
    name: 'Link',
    slug: 'link',
    type: 'function',
    description: { short: 'Custom link words', long: 'Create custom links to your chat.' },
    prices: { fairtrade_min: 230, fairtrade_max: 250, store: '400' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 99,
    name: 'Single',
    slug: 'single',
    type: 'function',
    description: { short: 'Stay single', long: 'Prevent other users from marrying or BFFing you. Includes broken heart hat: (hat#z).' },
    prices: { fairtrade_min: 90, fairtrade_max: 110, store: '150' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 98,
    name: 'Feast',
    slug: 'feast',
    type: 'yellow',
    description: { short: 'Feast smilies and flix', long: 'A feast of over 25 food smilies and up to 4 flix (animated backgrounds).' },
    prices: { fairtrade_min: 550, fairtrade_max: 600, store: '575' },
    status: 'limited',
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 97,
    name: 'Adventure',
    slug: 'adventure',
    type: 'yellow',
    description: { short: 'Adventure-themed smilies', long: 'Explore xat with these adventure-themed smilies.' },
    prices: { fairtrade_min: 900, fairtrade_max: 1100, store: '1060' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 96,
    name: 'Winter',
    slug: 'winter',
    type: 'superxmas',
    description: { short: 'Winter smilies and flix', long: 'Allows you to add a flix (animated background) to your chat, and also comes with some bonus smilies.' },
    prices: { fairtrade_min: 300, fairtrade_max: 350, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 94,
    name: 'Blastkick',
    slug: 'blastkick',
    type: 'blasts',
    description: { short: 'Kick animations', long: 'Shows animations when someone on the chat gets kicked. The more Blastkick powers assigned, the more animations unlocked.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 93,
    name: 'Mint',
    slug: 'mint',
    type: 'function',
    description: { short: 'Minimum transfer', long: 'Set a minimum transfer amount of xats and days that can be sent to you. Bonus smilies included.' },
    prices: { fairtrade_min: 50, fairtrade_max: 60, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'function',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 92,
    name: 'Horror',
    slug: 'horror',
    type: 'superhalloween',
    description: { short: 'Horror flix', long: 'Allows you to add flix (animated backgrounds) to your chat. Bonus horror smilies included.' },
    prices: { fairtrade_min: 500, fairtrade_max: 600, store: '340' },
    status: 'limited',
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 91,
    name: 'Rapid',
    slug: 'rapid',
    type: 'function',
    description: { short: 'Rapid actions', long: 'Allows you to carry out various actions such as kicking and banning with one mouse click.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '20' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 90,
    name: 'Bad',
    slug: 'bad',
    type: 'function',
    description: { short: 'Custom bad words filter', long: 'Allows you to add your own custom bad words to the filter for your chat. Bonus smilies included.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 79,
  },
  {
    id: 89,
    name: 'Summer',
    slug: 'summer',
    type: 'supersummer',
    description: { short: 'Summer-themed smilies', long: 'Have fun in the sun with these summer smilies.' },
    prices: { fairtrade_min: 80, fairtrade_max: 120, store: '165' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 88,
    name: 'Blastde',
    slug: 'blastde',
    type: 'blasts',
    description: { short: 'Demotion animations', long: 'Shows animations when someone on the chat gets demoted in rank. The more Blastde powers assigned, the more animations unlocked.' },
    prices: { fairtrade_min: 4000, fairtrade_max: 5000, store: '1500' },
    status: 'limited',
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 87,
    name: 'Independence',
    slug: 'independence',
    type: 'yellow',
    description: { short: 'independence day smilies', long: 'Celebrate USA / Independence Day with these smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 120, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 85,
    name: 'Flag',
    slug: 'flag',
    type: 'function',
    description: { short: 'Animated Flags', long: 'Over 190 flag animations. Support your team or country.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 84,
    name: 'Blastpro',
    slug: 'blastpro',
    type: 'blasts',
    description: { short: 'Promotion animations', long: 'Shows animations when someone on the chat gets promoted in rank. The more Blastpro powers assigned, the more animations unlocked.' },
    prices: { fairtrade_min: 280, fairtrade_max: 330, store: '400' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 83,
    name: 'Silly',
    slug: 'silly',
    type: 'yellow',
    description: { short: 'Silly smilies', long: 'Who said you can\'t have a little fun while chatting? Joke around with these silly smilies!' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 82,
    name: 'Sea',
    slug: 'sea',
    type: 'animal',
    description: { short: 'Sea-themed smilies', long: 'Have fun underwater with these sea-themed smilies.' },
    prices: { fairtrade_min: 160, fairtrade_max: 180, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 80,
    name: 'Gcontrol',
    slug: 'gcontrol',
    type: 'function',
    description: { short: 'Configure your chatroom', long: 'Set what rank can promote your chat, can set the scroller, how long mods can ban for and much more.' },
    prices: { fairtrade_min: 300, fairtrade_max: 330, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 79,
    name: 'Tempown',
    slug: 'tempown',
    type: 'function',
    description: { short: 'Make a temporary owner', long: 'Promote a user to owner for any duration of time, up to 24 hours.' },
    prices: { fairtrade_min: 180, fairtrade_max: 200, store: '25' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 78,
    name: 'Supporter',
    slug: 'supporter',
    type: 'function',
    description: { short: 'Support a group', long: 'Lets you show your support for your favourite chat group. Groups with the most supporters appear on the xat homepage.' },
    prices: { fairtrade_min: 70, fairtrade_max: 80, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 77,
    name: 'Scifi',
    slug: 'scifi',
    type: 'yellow',
    description: { short: 'Science fiction smilies', long: 'Time to live in the xat fantasy world with these science fiction themed smilies.' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '733' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 76,
    name: 'Gkaliens',
    slug: 'gkaliens',
    type: 'superkao',
    description: { short: 'Alien Kaoani smilies', long: 'Use kaoani alien themed smilies on your chat.' },
    prices: { fairtrade_min: 2500, fairtrade_max: 2800, store: '1999' },
    status: 'limited',
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group', 'is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 75,
    name: 'Bump',
    slug: 'bump',
    type: 'function',
    description: { short: 'Bump a user in PC', long: 'Get a user\'s attention in private chat by shaking the chat and playing a loud noise. The private chat lock also turns red.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 80,
  },
  {
    id: 74,
    name: 'Gline',
    slug: 'gline',
    type: 'function',
    description: { short: 'Smiley line', long: 'Lets chat owners change the smiley line to a set of smilies of their own choice.' },
    prices: { fairtrade_min: 580, fairtrade_max: 630, store: '900' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 73,
    name: 'Military',
    slug: 'military',
    type: 'yellow',
    description: { short: 'Military smilies', long: 'Salute! Get your military-themed smilies.' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '594' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 72,
    name: 'Gkaoani',
    slug: 'gkaoani',
    type: 'superkao',
    description: { short: 'Kaoani group smilies ', long: 'Assign over 30 gkaoani-style smilies to your group for all users to use.' },
    prices: { fairtrade_min: 800, fairtrade_max: 900, store: '1000' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 71,
    name: 'Circus',
    slug: 'circus',
    type: 'yellow',
    description: { short: 'Circus smilies', long: 'Roll up, roll up, the circus is in town. Get your ticket and smilies for the Cirque du xat.' },
    prices: { fairtrade_min: 120, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 70,
    name: 'Banish',
    slug: 'banish',
    type: 'function',
    description: { short: 'Banish a user', long: 'Banishes any user that has been banned longer than 1 hour. A banished user will not be able to see messages in the main chat.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'group',
        categories: ['is_group'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 69,
    name: 'Nopm',
    slug: 'nopm',
    type: 'function',
    description: { short: 'Only PM from friends', long: 'Blocks private messages from users who you do not have added. Anyone with a higher rank can always PM you.' },
    prices: { fairtrade_min: 100, fairtrade_max: 120, store: '150' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 68,
    name: 'Easter',
    slug: 'easter',
    type: 'superbunny',
    description: { short: 'Easter-themed smilies', long: 'Hop around xat with some easter-related smilies.' },
    prices: { fairtrade_min: 450, fairtrade_max: 550, store: '270' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 67,
    name: 'Flashrank',
    slug: 'flashrank',
    type: 'function',
    description: { short: 'Flash your pawn', long: 'Makes your pawn fade between the current pawn power you are using and your rank on the chat.' },
    prices: { fairtrade_min: 480, fairtrade_max: 500, store: '50' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 66,
    name: 'Irish',
    slug: 'irish',
    type: 'yellow',
    description: { short: 'Irish-themed smilies', long: 'Celebrate St Patrick\'s day with these smilies.' },
    prices: { fairtrade_min: 700, fairtrade_max: 800, store: '800' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 65,
    name: 'Party',
    slug: 'party',
    type: 'yellow',
    description: { short: 'Party smilies', long: 'Have a party on xat with some party smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 64,
    name: 'Blueman',
    slug: 'blueman',
    type: 'function',
    description: { short: 'Dark blue pawn', long: 'Turn your pawn dark blue. Must be a guest, member or have Flashrank power enabled for it to be visible.' },
    prices: { fairtrade_min: 800, fairtrade_max: 850, store: '1000' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 61,
    name: 'Tempmem',
    slug: 'tempmem',
    type: 'function',
    description: { short: 'Make temporary member', long: 'Promote guests to member for any duration of time, up to 24 hours.' },
    prices: { fairtrade_min: 100, fairtrade_max: 130, store: '15' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 60,
    name: 'Dx',
    slug: 'dx',
    type: 'function',
    description: { short: 'Convert days to xats', long: 'Convert some of your days to xats. This power will disappear after usage. Convert now!' },
    prices: { fairtrade_min: 85, fairtrade_max: 90, store: '7' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 81,
  },
  {
    id: 59,
    name: 'Stick',
    slug: 'stick',
    type: 'function',
    description: { short: 'Stickman Animator', long: 'Create your own custom animated stickmen and use it on chat.' },
    prices: { fairtrade_min: 180, fairtrade_max: 200, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 82,
  },
  {
    id: 58,
    name: 'Count',
    slug: 'count',
    type: 'function',
    description: { short: 'Countdown smilies', long: 'Set a countdown to a specific date. This power includes mechanical and LED styles.' },
    prices: { fairtrade_min: 100, fairtrade_max: 120, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 82,
  },
  {
    id: 56,
    name: 'Snowy',
    slug: 'snowy',
    type: 'superxmas',
    description: { short: 'Snowy power', long: 'Snowy effects for your smilies and avatar. Additionally, 2 snowy kisses.' },
    prices: { fairtrade_min: 1500, fairtrade_max: 2000, store: '404' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 82,
  },
  {
    id: 55,
    name: 'Thanksgiving',
    slug: 'thanksgiving',
    type: 'holiday',
    description: { short: 'Thanksgiving smilies', long: 'Get your turkey for Thanksgiving!' },
    prices: { fairtrade_min: 550, fairtrade_max: 600, store: '200' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 82,
  },
  {
    id: 54,
    name: 'Status',
    slug: 'status',
    type: 'function',
    description: { short: 'Status message', long: 'Show a status message under your name on the user list.' },
    prices: { fairtrade_min: 350, fairtrade_max: 360, store: '400' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 82,
  },
  {
    id: 51,
    name: 'Hush',
    slug: 'hush',
    type: 'function',
    description: { short: 'Hush a chat', long: 'Temporarily stop users on a chat from talking so you can make an announcement, up to a maximum 60 seconds.' },
    prices: { fairtrade_min: 180, fairtrade_max: 230, store: '25' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 82,
  },
  {
    id: 50,
    name: 'Num',
    slug: 'num',
    type: 'function',
    description: { short: 'Annotate smilies', long: 'Add 1-3 letters or numbers to some smilies.' },
    prices: { fairtrade_min: 230, fairtrade_max: 250, store: '400' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 82,
  },
  {
    id: 48,
    name: 'Fruit',
    slug: 'fruit',
    type: 'smiley',
    description: { short: 'Fruit smilies', long: 'Give a fruity background to your smilies.' },
    prices: { fairtrade_min: 800, fairtrade_max: 1000, store: '400' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 82,
  },
  {
    id: 47,
    name: 'Radio',
    slug: 'radio',
    type: 'function',
    description: { short: 'Personal radio', long: 'Take your radio with you to any chat and let other users tune in at any time.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 82,
  },
  {
    id: 46,
    name: 'Mute',
    slug: 'mute',
    type: 'function',
    description: { short: 'Silent ban', long: 'Allows owners and above to ban a user for up to 1 hour without them knowing. Guests in the chat will not be able to see the ban message.' },
    prices: { fairtrade_min: 280, fairtrade_max: 300, store: '40' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 82,
  },
  {
    id: 45,
    name: 'Angel',
    slug: 'angel',
    type: 'yellow',
    description: { short: 'Angel smilies', long: 'Add wings, a halo, a cloud and prayer hands to any smiley.' },
    prices: { fairtrade_min: 11500, fairtrade_max: 12500, store: '5000' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 82,
  },
  {
    id: 44,
    name: 'Dood',
    slug: 'dood',
    type: 'function',
    description: { short: 'Import images to doodle', long: 'Allows you to import images into doodle.' },
    prices: { fairtrade_min: 120, fairtrade_max: 140, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 43,
    name: 'Six',
    slug: 'six',
    type: 'yellow',
    description: { short: 'Devil-themed smilies', long: 'Turn your yellow smilies into devils.' },
    prices: { fairtrade_min: 1700, fairtrade_max: 1900, store: '1666' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 42,
    name: 'Costumes',
    slug: 'costumes',
    type: 'yellow',
    description: { short: 'Costume-themed smilies', long: 'Dress up your smilies with some costumes.' },
    prices: { fairtrade_min: 180, fairtrade_max: 200, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 41,
    name: 'Gag',
    slug: 'gag',
    type: 'function',
    description: { short: 'Gag a user', long: 'Allows you to stop guests and members from being able to talk in main chat for a maximum of 1 hour. Must be moderator.' },
    prices: { fairtrade_min: 1200, fairtrade_max: 1500, store: '600' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 40,
    name: 'Fade',
    slug: 'fade',
    type: 'function',
    description: { short: 'Fade effect', long: 'Give a ghost-like fade effect to any yellow smiley by adding #f to the end of a smiley code.' },
    prices: { fairtrade_min: 1400, fairtrade_max: 1600, store: '999' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 39,
    name: 'Hairf',
    slug: 'hairf',
    type: 'yellow',
    description: { short: 'Female hair', long: 'Female hair styles for your smilies.' },
    prices: { fairtrade_min: 100, fairtrade_max: 120, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 38,
    name: 'Hairm',
    slug: 'hairm',
    type: 'yellow',
    description: { short: 'Male hair', long: 'Male hair styles for your smilies.' },
    prices: { fairtrade_min: 80, fairtrade_max: 100, store: '150' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 37,
    name: 'Hands',
    slug: 'hands',
    type: 'yellow',
    description: { short: 'Add hands to smilies', long: 'Give your smilies some hands. Set different actions and mix/match for interesting combinations.' },
    prices: { fairtrade_min: 180, fairtrade_max: 200, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 36,
    name: 'Ttth',
    slug: 'ttth',
    type: 'function',
    description: { short: 'Talk to the hand', long: 'Send a giant smiley and ignore someone you do not want to talk to all at once. Type \'/t\' in their private chat.' },
    prices: { fairtrade_min: 180, fairtrade_max: 200, store: '400' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 34,
    name: 'Diamond',
    slug: 'diamond',
    type: 'back',
    description: { short: 'Diamond shaped smilies', long: 'Give a purple diamond shape to any yellow smiley by adding #d to the end of a smiley code.' },
    prices: { fairtrade_min: 500, fairtrade_max: 600, store: '820' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 33,
    name: 'Sinbin',
    slug: 'sinbin',
    type: 'function',
    description: { short: 'Sinbin a moderator', long: 'Remove moderator abilities to kick and ban for seconds or up to 24 hours. Must be owner+.' },
    prices: { fairtrade_min: 150, fairtrade_max: 170, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 32,
    name: 'Guestself',
    slug: 'guestself',
    type: 'function',
    description: { short: 'Make yourself a guest', long: 'Turn yourself into a guest at any chat where you are currently ranked by typing /g in the chat.' },
    prices: { fairtrade_min: 130, fairtrade_max: 150, store: '250' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 30,
    name: 'Pink',
    slug: 'pink',
    type: 'function',
    description: { short: 'Pink pawn', long: 'Turn your guest or member pawn pink (will need flashrank power for other ranks).' },
    prices: { fairtrade_min: 350, fairtrade_max: 400, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 83,
  },
  {
    id: 29,
    name: 'Invisible',
    slug: 'invisible',
    type: 'function',
    description: { short: 'Invisible owner', long: 'Become invisible as owner, hiding yourself from lower ranks.' },
    prices: { fairtrade_min: 450, fairtrade_max: 500, store: '40' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 28,
    name: 'Superkick',
    slug: 'superkick',
    type: 'function',
    description: { short: 'Kick lower ranks', long: 'Allows you to kick lower ranks that you normally would not be able to kick.' },
    prices: { fairtrade_min: 110, fairtrade_max: 130, store: '15' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 27,
    name: 'Show',
    slug: 'show',
    type: 'function',
    description: { short: 'Show your xats and days', long: 'Allows other users to see the amount of your xats and days.' },
    prices: { fairtrade_min: 25, fairtrade_max: 35, store: '50' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 26,
    name: 'Octogram',
    slug: 'octogram',
    type: 'back',
    description: { short: 'Octogram shaped smilies', long: 'Give a light green octogram shape to any yellow smiley by adding #o to the smiley code.' },
    prices: { fairtrade_min: 90, fairtrade_max: 100, store: '150' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 24,
    name: 'Clear',
    slug: 'clear',
    type: 'function',
    description: { short: 'Clear your smilies', long: 'Give a clear background to any yellow smiley.' },
    prices: { fairtrade_min: 7000, fairtrade_max: 7500, store: '3000' },
    status: 'limited',
    released: '2023-10-26',
    category: 'function',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 23,
    name: 'Hexagon',
    slug: 'hexagon',
    type: 'back',
    description: { short: 'Hexagon shaped smilies', long: 'Give a pink hexagon shape to any yellow smiley.' },
    prices: { fairtrade_min: 60, fairtrade_max: 75, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 22,
    name: 'Cycle',
    slug: 'cycle',
    type: 'colors',
    description: { short: 'Color cycle smilies', long: 'Give a cycle of rainbow colors to any yellow smiley.' },
    prices: { fairtrade_min: 330, fairtrade_max: 350, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 20,
    name: 'Square',
    slug: 'square',
    type: 'back',
    description: { short: 'Square shaped smilies', long: 'Give a cyan square shape to any yellow smiley by adding #s to the smiley code.' },
    prices: { fairtrade_min: 60, fairtrade_max: 75, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 19,
    name: 'Animate',
    slug: 'animate',
    type: 'function',
    description: { short: 'Animated avatar', long: 'Set your avatar as a strip of square pictures and an animation will be shown as your avatar.' },
    prices: { fairtrade_min: 360, fairtrade_max: 380, store: '400' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 18,
    name: 'Shuffle',
    slug: 'shuffle',
    type: 'function',
    description: { short: 'Random avatar', long: 'Allows you to create a strip of pictures to display as your avatar and one picture is shown randomly with each message.' },
    prices: { fairtrade_min: 10, fairtrade_max: 15, store: '25' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 17,
    name: 'Heart',
    slug: 'heart',
    type: 'superheart',
    description: { short: 'Heart shaped smilies', long: 'Change the back of any yellow smiley to the shape of a heart by adding #h at the end of a smiley.' },
    prices: { fairtrade_min: 750, fairtrade_max: 850, store: '664' },
    status: 'limited',
    released: '2023-10-26',
    category: 'smiley',
        categories: ['is_limited'],
    limited: true,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 16,
    name: 'Light',
    slug: 'light',
    type: 'colors',
    description: { short: 'Light/Dark smilies', long: 'Make any yellow smiley white, gray or black. Add #--- after the smiley code to make a smiley lighter or #+++ to make it darker.' },
    prices: { fairtrade_min: 120, fairtrade_max: 130, store: '150' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 15,
    name: 'Blue',
    slug: 'blue',
    type: 'colors',
    description: { short: 'Blue color smilies', long: 'Color any yellow smiley blue by adding #b after the smiley code.' },
    prices: { fairtrade_min: 120, fairtrade_max: 130, store: '150' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 84,
  },
  {
    id: 14,
    name: 'Green',
    slug: 'green',
    type: 'colors',
    description: { short: 'Green color smilies', long: 'Color any yellow smiley green by adding #g after the smiley code.' },
    prices: { fairtrade_min: 120, fairtrade_max: 130, store: '150' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },
  {
    id: 13,
    name: 'Red',
    slug: 'red',
    type: 'colors',
    description: { short: 'Red color smilies', long: 'Color any yellow smiley red by adding #r after the smiley code.' },
    prices: { fairtrade_min: 120, fairtrade_max: 130, store: '150' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },
  {
    id: 11,
    name: 'Tempmod',
    slug: 'tempmod',
    type: 'function',
    description: { short: 'Temporary moderator', long: 'Promote guests and members to moderator for any duration of time, up to 24 hours.' },
    prices: { fairtrade_min: 380, fairtrade_max: 420, store: '50' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },
  {
    id: 10,
    name: 'Nopc',
    slug: 'nopc',
    type: 'function',
    description: { short: 'Only have friends PC you', long: 'Prevents members and guests not added as a friend from starting a private chat with you. Anyone with a higher rank can always PC you.' },
    prices: { fairtrade_min: 120, fairtrade_max: 140, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },
  {
    id: 9,
    name: 'Reghide',
    slug: 'reghide',
    type: 'function',
    description: { short: 'Remove subscriber shape', long: 'Removes the star, heart or cross on your pawn completely. All other subscriber features still work.' },
    prices: { fairtrade_min: 70, fairtrade_max: 80, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },
  {
    id: 8,
    name: 'Noaudies',
    slug: 'noaudies',
    type: 'function',
    description: { short: 'Turn off audie sounds', long: 'Prevents audies sent by users from being automatically played. Click on the audie if you still want to hear it.' },
    prices: { fairtrade_min: 5, fairtrade_max: 8, store: '10' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },
  {
    id: 7,
    name: 'Mirror',
    slug: 'mirror',
    type: 'function',
    description: { short: 'Mirror flip your smilies', long: 'Mirror flip almost any smiley from left to right by placing #m after a smilie code.' },
    prices: { fairtrade_min: 55, fairtrade_max: 66, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },
  {
    id: 6,
    name: 'Invert',
    slug: 'invert',
    type: 'function',
    description: { short: 'Upside-down smilies', long: 'Make almost any smiley upside down by adding #i after the smilie code.' },
    prices: { fairtrade_min: 110, fairtrade_max: 130, store: '200' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },
  {
    id: 5,
    name: 'Nofollow',
    slug: 'nofollow',
    type: 'function',
    description: { short: 'Friends can\'t follow you', long: 'Block friends from seeing what chat group you are currently chatting at.' },
    prices: { fairtrade_min: 60, fairtrade_max: 75, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },
  {
    id: 4,
    name: 'Zoom',
    slug: 'zoom',
    type: 'function',
    description: { short: 'Zoom on avatar', long: 'Enlarges the avatar by hovering over with mouse for both you and other users.' },
    prices: { fairtrade_min: 250, fairtrade_max: 270, store: '300' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },
  {
    id: 3,
    name: 'Mod8',
    slug: 'mod8',
    type: 'function',
    description: { short: 'Increase ban time', long: 'Adds two additional hours to the moderation max ban time.' },
    prices: { fairtrade_min: 300, fairtrade_max: 320, store: '500' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },
  {
    id: 2,
    name: 'Subhide',
    slug: 'subhide',
    type: 'function',
    description: { short: 'Black subscriber shape', long: 'Star, heart or cross on your pawn appears black while keeping subscriber abilities.' },
    prices: { fairtrade_min: 70, fairtrade_max: 80, store: '100' },
    status: false,
    released: '2023-10-26',
    category: 'function',
        categories: ['is_allpowers'],
    limited: false,
    smilies: [],
    pawns: [],
    popularity: 85,
  },

];

export const FEATURED_POWER_SLUGS = ['bowgal', 'astrox', 'dad', 'roshi', 'stella', 'purple', 'gold', 'cstyle', 'nameglow', 'namecolor'];

export function getPowerBySlug(slug: string): PowerInfo | undefined {
  const power = MOCK_POWERS.find((p) => p.slug === slug.toLowerCase());
  return power ? withLocalizedDescription(power) : undefined;
}

/** Resolve um power a partir do código do smiley (slug do power ou smiley interno). */
export function findPowerBySmileyCode(code: string): PowerInfo | undefined {
  const key = code.trim().toLowerCase();
  if (!key) return undefined;

  const bySlug = getPowerBySlug(key);
  if (bySlug) return bySlug;

  const bySmiley = MOCK_POWERS.find((p) =>
    p.smilies.some((s) => s.toLowerCase() === key),
  );
  return bySmiley ? withLocalizedDescription(bySmiley) : undefined;
}

/** Slugs de todos os powers — pool do hero flutuante (apenas powers, sem smilies normais). */
export function getAllPowerSlugs(): string[] {
  return withoutAcePowers(MOCK_POWERS).map((p) => p.slug);
}

export function getFeaturedPowers(): PowerInfo[] {
  return FEATURED_POWER_SLUGS.map((slug) => getPowerBySlug(slug)).filter(
    (p): p is PowerInfo => Boolean(p)
  );
}

/** Pisos verificados da xatBlog — o seed/CMS costuma ter galerias incompletas. */
const XATBLOG_VERIFIED_TOTALS = {
  smilies: 5222,
  powerPawns: 71,
  groupPowers: 62,
} as const;

function isGroupPower(power: PowerInfo): boolean {
  if (power.category === 'group') return true;
  if (power.categories?.includes('is_group')) return true;
  const type = power.type?.toLowerCase() ?? '';
  return type === 'group' || type === 'grouppowers';
}

function getCollectionPawnTotal(): number {
  return getCollectionList().reduce((sum, item) => sum + (item.total?.pawns ?? 0), 0);
}

/**
 * Soma o que o catálogo vivo tem a mais que o MOCK, power a power.
 * Powers novos (só no CMS) entram por completo; galerias enriquecidas
 * contribuem só com o excedente — assim um power novo sobe as métricas
 * sem o seed incompleto esconder o ganho.
 */
function sumGalleryExtrasOverMock(
  catalog: PowerInfo[],
  mockBySlug: Map<string, PowerInfo>,
  pick: (power: PowerInfo) => number
): number {
  let extras = 0;
  for (const power of catalog) {
    const liveCount = pick(power);
    if (liveCount <= 0) continue;
    const mockPower = mockBySlug.get(power.slug);
    if (!mockPower) {
      extras += liveCount;
      continue;
    }
    extras += Math.max(0, liveCount - pick(mockPower));
  }
  return extras;
}

/**
 * Totais da Powerpedia (powers, smilies, peões, group powers).
 *
 * - Powers: tamanho do catálogo público actual (CMS + merge).
 * - Smilies / peões / groups: piso xatBlog (seed incompleto) + extras
 *   de powers novos ou galerias maiores que o MOCK.
 */
export function getPowersDetailedStats(powers?: PowerInfo[]) {
  // `undefined` = ainda não hidratou → MOCK; array (mesmo vazio) = catálogo real.
  const catalog = withoutAcePowers(powers === undefined ? MOCK_POWERS : powers);
  const mockCatalog = withoutAcePowers(MOCK_POWERS);
  const mockBySlug = new Map(mockCatalog.map((p) => [p.slug, p]));

  const mockSmilies = mockCatalog.reduce((acc, p) => acc + (p.smilies?.length ?? 0), 0);
  const mockPowerPawns = mockCatalog.reduce((acc, p) => acc + (p.pawns?.length ?? 0), 0);
  const mockGroups = mockCatalog.filter(isGroupPower).length;

  const smiliesBase = Math.max(mockSmilies, XATBLOG_VERIFIED_TOTALS.smilies);
  const powerPawnsBase = Math.max(mockPowerPawns, XATBLOG_VERIFIED_TOTALS.powerPawns);
  const groupsBase = Math.max(mockGroups, XATBLOG_VERIFIED_TOTALS.groupPowers);

  const smiliesExtras = sumGalleryExtrasOverMock(
    catalog,
    mockBySlug,
    (p) => p.smilies?.length ?? 0
  );
  const pawnsExtras = sumGalleryExtrasOverMock(
    catalog,
    mockBySlug,
    (p) => p.pawns?.length ?? 0
  );
  const groupsExtras = catalog.filter((p) => {
    if (!isGroupPower(p)) return false;
    const mockPower = mockBySlug.get(p.slug);
    // Novo no CMS, ou passou a ser group power face ao MOCK.
    return !mockPower || !isGroupPower(mockPower);
  }).length;

  return {
    totalPowers: catalog.length,
    totalSmilies: smiliesBase + smiliesExtras,
    totalPawns: powerPawnsBase + pawnsExtras + getCollectionPawnTotal(),
    totalGroups: groupsBase + groupsExtras,
  };
}

export function getNewestPowers(count = 6): PowerInfo[] {
  return withoutAcePowers(MOCK_POWERS)
    .sort((a, b) => b.id - a.id)
    .slice(0, count)
    .map(withLocalizedDescription);
}

export function getCategoryCount(category: string): number {
  const catalog = withoutAcePowers(MOCK_POWERS);
  if (category === 'limited') return catalog.filter((p) => p.limited).length;
  if (category === 'unlimited') return catalog.filter((p) => !p.limited).length;
  return catalog.filter((p) => p.category === category).length;
}

/** @deprecated Preferir `powerShowsPstyleSection` de `power-pstyle-themes`. */
export function isPstylePower(power: PowerInfo): boolean {
  return powerShowsPstyleSection(power);
}

export function isPowerCategory(value: string): value is PowerCategory {
  return POWER_CATEGORIES.some((c) => c.id === value);
}

/** Sugestões para explorar mais powers relacionados. */
export function getRelatedPowers(
  current: PowerInfo,
  count = 4,
  catalog?: PowerInfo[]
): PowerInfo[] {
  const source = withoutAcePowers(catalog?.length ? catalog : MOCK_POWERS);
  const manual = resolveRelatedByItems(
    current.relatedExplore?.items,
    source,
    current.slug,
    count
  );
  if (manual) {
    return manual.map(withLocalizedDescription);
  }

  return source
    .filter((p) => p.slug !== current.slug && p.category === current.category)
    .sort((a, b) => (b.popularity ?? 0) - (a.popularity ?? 0) || b.id - a.id)
    .slice(0, count)
    .map(withLocalizedDescription);
}

export function getRelatedExploreTitle(power: PowerInfo): string {
  const custom = power.relatedExplore?.title?.trim();
  if (custom) return custom;
  return `Explore mais powers ${getPowerCategoryLabel(power.category)}`;
}
